export interface VspStandard {
  section: string;
  label: string;
  title: string;
  critical: boolean;
  pointValue: number;
  description: string;
}

export interface VspScoredItem {
  itemNo: string;
  label: string;
  pointValue: number;
  critical: boolean;
  description: string;
  subItems: VspStandard[];
}

export interface VspCategory {
  id: string;
  name: string;
  items: VspScoredItem[];
}

export interface VspData {
  source: string;
  totalPossibleScore: number;
  satisfactoryScoreThreshold: number;
  categories: VspCategory[];
}

export const VSP_DATA: VspData = {
  source:
    'VSP 2025 Environmental Public Health Standards - Annex 13.16 Vessel Sanitation Inspection Report',
  totalPossibleScore: 100,
  satisfactoryScoreThreshold: 86,
  categories: [
    {
      id: 'disease-reporting',
      name: 'Disease Reporting',
      items: [
        {
          itemNo: '01',
          label: '01 - Disease reporting',
          pointValue: 4,
          critical: true,
          description: 'Disease reporting',
          subItems: [
            {
              section: '4.2.1.1',
              label: '4.2.1.1 - 24-Hour Report',
              title: '24-Hour Report',
              critical: true,
              pointValue: 4,
              description:
                'The master, medical staff, or other designated staff of a vessel destined for a U.S. port from an international port must submit at least one standardized AGE report based on the number of REPORTABLE AGE CASES in the AGE surveillance log to VSP no less than 24 hours—but not more than 36 hours—before the vessel’s expected arrival at the U.S. port.',
            },
            {
              section: '4.2.1.2',
              label: '4.2.1.2 - 4-Hour Update Report',
              title: '4-Hour Update Report',
              critical: true,
              pointValue: 4,
              description:
                'If the number of cases changes after submission of the initial report, an updated report must be submitted no less than 4 hours before the vessel’s arrival at the U.S. port. The 4-hour update report must be a cumulative total count of the reported crew and passengers during the entire cruise, including the additional cases.\nIf there is an update to the AGE surveillance log after the 4-hour report is submitted, an additional 4-hour report must be submitted only if the vessel is still more than 4 hours from arrival in the U.S. port.\nIf the vessel is less than 4 hours from arrival in the U.S. port, no additional 4-hour report is needed.',
            },
            {
              section: '4.2.2.1',
              label: '4.2.2.1 - Contents',
              title: 'Contents',
              critical: true,
              pointValue: 4,
              description:
                'The AGE report must contain the following:\n• Name of the vessel.\n• Port of embarkation.\n• Date of embarkation.\n• Port of disembarkation.\n• Date of disembarkation.\n• Total number of REPORTABLE AGE CASES among passengers, including those who have disembarked because of illness—even if the number is 0 (zero reporting).\n• Total number of REPORTABLE AGE CASES among crew members, including those who have disembarked because of illness—even if the number is 0 (zero reporting).\n• Total number of passengers and crew members on the cruise.',
            },
            {
              section: '4.2.3.1',
              label: '4.2.3.1 - 2% and 3% Illness Report',
              title: '2% and 3% Illness Report',
              critical: true,
              pointValue: 4,
              description:
                'The master or designated corporate representative of a vessel with an international itinerary destined for a U.S. port must submit a special report at any time during a cruise, including between two U.S. ports,\n• When the cumulative percentage of REPORTABLE AGE CASES for the entire voyage entered in the AGE surveillance log reaches 2% among passengers or 2% among crew AND\n• The vessel is within 15 days of expected arrival at a U.S. port.\nFor segmented voyages where those segments are 8 days or less, a special AGE report must be made to VSP\n• When a cumulative percentage of REPORTABLE AGE CASES of either 2% or 3% is reached among passengers or crew within one segment or two consecutive segments AND\n• The vessel is within 15 days of expected arrival at a U.S. port.\nThe AGE surveillance log must include all segments from the start of the voyage, unless 67% (two-thirds) or more of the passengers are disembarked in any one segment.\nFor cruises lasting longer than 15 days before entering a U.S. port, the 2% or 3% special AGE report must include all cases for the entire voyage.\nA telephone notification to VSP (1-800-323-2132) must accompany the special 2% report.\nA second special report must be submitted when the cumulative percentage of REPORTABLE AGE CASES entered in the AGE surveillance log reaches 3% among passengers or 3% among crew and the vessel is within 15 days of expected arrival at a U.S. port.',
            },
            {
              section: '4.2.3.2',
              label: '4.2.3.2 - Daily Updates',
              title: 'Daily Updates',
              critical: false,
              pointValue: 4,
              description:
                'Daily updates of illness status must be submitted as requested by VSP after the initial submission of a special report. Daily updates may be submitted by email, or as requested by VSP.',
            },
            {
              section: '4.2.3.3',
              label: '4.2.3.3 - Routine Reporting Continues',
              title: 'Routine Reporting Continues',
              critical: false,
              pointValue: 4,
              description:
                'Routine reports (24-hour and 4-hour) must continue to be submitted by the master or designated corporate representative of a vessel that has submitted a special report.',
            },
          ],
        },
        {
          itemNo: '02',
          label: '02 - Medical logs maintained',
          pointValue: 1,
          critical: false,
          description: 'Medical logs maintained',
          subItems: [
            {
              section: '4.1.1.1',
              label: '4.1.1.1 - Reportable Case Definition',
              title: 'Reportable Case Definition',
              critical: false,
              pointValue: 1,
              description:
                'Passengers and crew with AGE symptoms must be evaluated using the REPORTABLE AGE CASE definition. A REPORTABLE case of AGE is a case reported to the master of the vessel, the medical staff, or other designated staff by a passenger or a crew member and meets the definition below.\nA case is defined as\n• Diarrhea (three or more episodes of loose stools in a 24-hour period or what is above normal for the individual) OR\n• Vomiting and one additional symptom including one or more episodes of loose stools in a 24-hour period, or abdominal cramps, or headache, or muscle aches, or fever (temperature of ≥38°C [100.4°F]).\nNausea, although a common symptom of AGE, is specifically excluded from this definition to avoid misclassifying seasickness (nausea and vomiting) as AGE.\nUse these case definitions for identifying and classifying cases for reporting purposes. These case definitions should not be used as criteria for clinical intervention or public health action. For many conditions of public health importance, action to contain disease should be initiated as soon as a problem is identified; in many circumstances, appropriate public health action should be undertaken even though insufficient information is available to determine whether cases meet the case definition.',
            },
            {
              section: '4.1.1.2',
              label: '4.1.1.2 - Illness Onset Time',
              title: 'Illness Onset Time',
              critical: false,
              pointValue: 1,
              description:
                'REPORTABLE AGE CASES must include crew members with an ILLNESS ONSET time of up to 3 days before boarding the vessel.',
            },
            {
              section: '4.1.2.1',
              label: '4.1.2.1 - Responsibility',
              title: 'Responsibility',
              critical: false,
              pointValue: 1,
              description:
                'A standardized AGE surveillance log for each cruise must be maintained daily by the master of the vessel, the medical staff, or other designated staff.',
            },
            {
              section: '4.1.2.2',
              label: '4.1.2.2 - Required Information',
              title: 'Required Information',
              critical: false,
              pointValue: 1,
              description:
                'The AGE surveillance log must list\n• The name of the vessel, cruise dates, and cruise number.\n• All REPORTABLE AGE CASES.\n• All passengers and crew members who are dispensed antidiarrheal medication from the master of the vessel, medical staff, or other designated staff.',
            },
            {
              section: '4.1.2.3',
              label: '4.1.2.3 - AGE Surveillance Log Details',
              title: 'AGE Surveillance Log Details',
              critical: false,
              pointValue: 1,
              description:
                'The AGE surveillance log template can be found at www.cdc.gov/vessel- sanitation/php/communication-resources/index.html.\nThe AGE surveillance log must include a header containing the following information about the voyage:\n• Vessel name.\n• Voyage number.\n• Date from.\n• Date to.\n• Total number of passengers.*\n• Total numbers of REPORTABLE AGE CASES among passengers.\n• Total number of crew. *\n• Total numbers of REPORTABLE AGE CASES among crew.\n*Total number of passengers and total number of crew must be the totals at the beginning of the voyage (i.e., totals on “date from”).\nThe AGE surveillance log entry for each passenger or crew member must contain the following information in separate columns:\n• Patient I.D.\n• Date of the first medical visit or report to staff of illness.\n• Time of the first medical visit or report to staff of illness.\n• Person’s last name.\n• Person’s first name.\n• Person’s age.\n• Person’s sex.\n• Designation as passenger or crew member.\n• Cabin number.\n• Crew member position or job on the vessel.\n• Date of ILLNESS ONSET.\n• Time of ILLNESS ONSET.\n• Illness symptoms, including the presence or absence of the following selected signs and symptoms, with a separate column for each of the following: o Number of episodes of diarrhea in a 24-hour period. o Bloody stools (yes/no). o Number of episodes of vomiting in a 24-hour period. o Fever (yes/no). o Recorded temperature. o Abdominal cramps (yes/no). o Headaches (yes/no). o Myalgia (yes/no).\n• Date of last symptom.\n• Time of last symptom.\n• Entry (yes/no) for whether a specimen was requested.\n• Entry (yes/no) for whether a specimen was received.\n• Entry (yes/no) for antidiarrheal medications sold or dispensed by designated medical staff.\n• Entry (yes/no) for whether this was a REPORTABLE AGE CASE.\n• Presence of underlying medical conditions that may affect interpretation of AGE; for example, diabetic diarrhea, inflammatory bowel disease, gastrectomy, antibiotic-induced diarrhea, vomiting from chemotherapy, ear infections in children or others. If none, write “none,” “not applicable,” “N/A,” or similar wording.\nThe AGE surveillance log must contain the above information in this exact order. All fields of the AGE surveillance log must be completed, even if entry is zero or nonapplicable. If a field is not completed, document the reason in the notes column (for example, a virtual consultation).\nThe log data must be exported in an analyzable format (such as Microsoft Excel or Microsoft Access) in the exact order noted above. Any additional data fields in the surveillance log must be entered only outside of the form margins when exported.',
            },
            {
              section: '4.1.3',
              label: '4.1.3 - Food and Beverage Questionnaire',
              title: 'Food and Beverage Questionnaire',
              critical: false,
              pointValue: 1,
              description:
                'Questionnaires detailing activities and meal locations for the 72 hours before ILLNESS ONSET must be distributed to all passengers and crew members who are identified as REPORTABLE AGE CASES.\nThe food and beverage questionnaire template can be found at www.cdc.gov/vessel-sanitation/php/communication-resources/index.html.',
            },
            {
              section: '4.1.4',
              label: '4.1.4 - Retention and Review',
              title: 'Retention and Review',
              critical: false,
              pointValue: 1,
              description:
                'The following records must be maintained on the vessel for 12 months and available for review by VSP during inspections and OUTBREAK investigations:\n• Medical log/record.\n• AGE surveillance log.\n• 72-hour self-administered questionnaires.\n• Interviews with cabin mates and IMMEDIATE CONTACTS of crew members with AGE (initial, 24-, and 48-hour).\n• Documentation of follow-up actions for public vomit or diarrhea incidents. Proper follow-up of identified individuals should include determining whether they meet the definition of a REPORTABLE AGE CASE and assessing the need for ISOLATION.\n• Documentation of the 3-day assessment of crew members for symptoms of AGE before joining the vessel.\n• Documentation of the date and time of last symptom and clearance to return to work for FOOD and nonfood EMPLOYEES.\n• Assessment of FOOD prepared or served by the FOOD EMPLOYEE while symptomatic and corrective actions.\n• Documentation of the date and time of verbal interviews with asymptomatic cabin mates and IMMEDIATE CONTACTS of symptomatic crew.\nElectronic records of these documents are acceptable IF the data are complete and can be retrieved during inspections and OUTBREAK investigations.',
            },
            {
              section: '4.2.1.3',
              label: '4.2.1.3 - Report Submission',
              title: 'Report Submission',
              critical: false,
              pointValue: 1,
              description:
                'Submit routine 24-hour and 4-hour update reports electronically. The vessel must maintain proof that the report was successfully received by VSP.\nDo not submit duplicate reports or reports when not required.',
            },
            {
              section: '4.2.4',
              label: '4.2.4 - Report Retention and Review',
              title: 'Report Retention and Review',
              critical: false,
              pointValue: 1,
              description:
                'The 24-hour, 4-hour, and special reports must be maintained on the vessel for 12 months. The reports must be available for review by VSP during inspections and OUTBREAK investigations.',
            },
            {
              section: '4.3.1',
              label: '4.3.1 - Specimen Supplies and Shipping',
              title: 'Specimen Supplies and Shipping',
              critical: false,
              pointValue: 1,
              description:
                'The medical staff will be responsible for maintaining a supply of at least 10 clinical specimen collection containers for both viral and bacterial agents (10 for each), as well as a shipping container that meets the latest shipping requirements of the International Air Transport Association (IATA) and U.S. Department of Transportation for Biological Substances, Category B designated shipments (or higher).\nThe vessel must maintain the appropriate labels and markings required for shipping Biological Substances, Category B shipments. The vessel must maintain appropriate refrigerant materials to keep clinical specimens cool. For guidance, refer to Annex 13.3.5.',
            },
            {
              section: '4.3.2',
              label: '4.3.2 - When to Collect',
              title: 'When to Collect',
              critical: false,
              pointValue: 1,
              description:
                'When a vessel reaches 2% REPORTABLE AGE CASES in either passengers or crew members, the medical staff will begin collecting clinical specimens (stool or vomitus specimens) for bacterial or viral analysis. If the etiologic agent is suspected to be parasitic, the medical staff must consult with VSP epidemiology staff for clinical specimen collection requirements.\nSpecimens should be requested before issuing antibiotic medications, but this patient treatment should not be contingent on specimen collection.\nSpecimens collected after administration of antibiotic medications must not be submitted for CDC analysis unless directed by VSP.',
            },
            {
              section: '4.3.3',
              label: '4.3.3 - Proper Packing',
              title: 'Proper Packing',
              critical: false,
              pointValue: 1,
              description:
                'Before the specimens are packaged and shipped for laboratory testing, VSP will give instructions to vessel/cruise line medical staff about the specific container (i.e., bacterial or viral) for transport to a laboratory.\nAll clinical specimens must be packed and shipped in accordance with the standards outlined in Annex 13.3.5. The specimen packaging must include the proper documentation as required by the receiving laboratory.',
            },
            {
              section: '4.4.1.2',
              label: '4.4.1.2 - Hygiene and Handwashing Facts',
              title: 'Hygiene and Handwashing Facts',
              critical: false,
              pointValue: 1,
              description:
                'Advise symptomatic crew of hygiene and handwashing facts and provide written handwashing and hygiene fact sheets.',
            },
            {
              section: '4.4.1.3',
              label: '4.4.1.3 - Asymptomatic Immediate Contacts of Crew Cases',
              title: 'Asymptomatic Immediate Contacts of Crew Cases',
              critical: false,
              pointValue: 1,
              description:
                'FOOD and nonfood employees:\n• Restrict exposure to symptomatic crew member(s).\n• Undergo a verbal interview with medical or supervisory staff, who will confirm their condition, provide facts and a written fact sheet about hygiene and handwashing, and instruct them to report immediately to medical if they develop symptoms.\n• Complete a verbal interview daily with medical or supervisory staff until 48 hours after the crew members’ ILLNESS ONSET. o The first verbal interview must be conducted within 8 hours from the time the ill crew member initially reported to the medical staff. o If the asymptomatic IMMEDIATE CONTACT or cabin mate is at work, he or she must be contacted by medical or supervisory staff as soon as possible. o The date and time of verbal interviews must be documented.\nIf the symptomatic crew member has no cabin mate or other IMMEDIATE CONTACT, this must be documented.',
            },
            {
              section: '4.4.2.2',
              label: '4.4.2.2 - Hygiene and Handwashing Facts',
              title: 'Hygiene and Handwashing Facts',
              critical: false,
              pointValue: 1,
              description:
                'Advise symptomatic passengers of hygiene and handwashing facts and provide written handwashing and hygiene fact sheets.',
            },
            {
              section: '4.5',
              label: '4.5 - Medications Sold or Dispensed',
              title: 'Medications Sold or Dispensed',
              critical: false,
              pointValue: 1,
              description:
                'Antidiarrheal medications must not be sold or dispensed to passengers or crew except by designated medical staff.',
            },
            {
              section: '7.1.2.1.5',
              label: '7.1.2.1.5 - Record of Restriction and Release',
              title: 'Record of Restriction and Release',
              critical: false,
              pointValue: 1,
              description:
                'A written or electronic record of both the work restriction and release from restriction must be maintained on the vessel for 12 months for inspection review.',
            },
          ],
        },
      ],
    },
    {
      id: 'potable-water',
      name: 'Potable Water',
      items: [
        {
          itemNo: '03',
          label: '03 - Bunker/production source; Halogen residual',
          pointValue: 5,
          critical: true,
          description: 'Bunker/production source; Halogen residual',
          subItems: [
            {
              section: '5.1.1.1',
              label: '5.1.1.1 - Safe Source',
              title: 'Safe Source',
              critical: true,
              pointValue: 5,
              description:
                'DRINKING WATER bunkered from shore supplies must be from a potable source that meets World Health Organization standards for POTABLE WATER.',
            },
            {
              section: '5.1.2.1.1',
              label: '5.1.2.1.1 - Polluted Harbors',
              title: 'Polluted Harbors',
              critical: true,
              pointValue: 5,
              description:
                'A reverse osmosis unit, distillation plant, or other process that supplies water to the vessel’s POTABLE WATER system must only operate while the vessel is MAKING WAY. These processes must not operate in polluted areas, HARBORS, or at anchor.\nA reverse osmosis unit or evaporator with a completely separate plant/process, piping system, and connections from the POTABLE WATER system may be used to produce TECHNICAL WATER while in polluted areas, HARBORS, at anchor, or while not MAKING WAY.',
            },
            {
              section: '5.1.2.1.2',
              label: '5.1.2.1.2 - Onboard Water Sources',
              title: 'Onboard Water Sources',
              critical: true,
              pointValue: 5,
              description:
                'Onboard water sources such as TECHNICAL WATER, air conditioning condensate, or wastewater of any kind (treated or untreated) are not allowed for POTABLE WATER production.',
            },
            {
              section: '5.2.1.1.1',
              label: '5.2.1.1.1 - Halogen and pH Level',
              title: 'Halogen and pH Level',
              critical: true,
              pointValue: 5,
              description:
                'POTABLE WATER must be continuously halogenated to at least',
            },
          ],
        },
        {
          itemNo: '04',
          label: '04 - Distribution system halogen residual',
          pointValue: 5,
          critical: true,
          description: 'Distribution system halogen residual',
          subItems: [
            {
              section: '5.4.1.2.1',
              label: '5.4.1.2.1 - Halogen Residual',
              title: 'Halogen Residual',
              critical: true,
              pointValue: 5,
              description:
                'The halogenation injection EQUIPMENT must provide continuous halogenation of the POTABLE WATER distribution system and must maintain a free HALOGEN residual of greater than or equal to 0.2 MG/L (ppm) and less than or equal to 5.0 MG/L (ppm) throughout the distribution system.',
            },
          ],
        },
        {
          itemNo: '05',
          label: '05 - Distribution system halogen analyzer calibrated',
          pointValue: 2,
          critical: false,
          description: 'Distribution system halogen analyzer calibrated',
          subItems: [
            {
              section: '5.5.1.2.3',
              label: '5.5.1.2.3 - Accuracy',
              title: 'Accuracy',
              critical: false,
              pointValue: 2,
              description:
                'The free HALOGEN residual measured by the HALOGEN analyzer must be ±0.2 MG/L (ppm) of the free HALOGEN residual measured by the manual test.',
            },
          ],
        },
        {
          itemNo: '06',
          label:
            '06 - Halogen analyzer chart recorder maintenance, operation, records; Micro sampling, records',
          pointValue: 2,
          critical: false,
          description:
            'Halogen analyzer chart recorder maintenance, operation, records; Micro sampling, records',
          subItems: [
            {
              section: '5.1.1.2.1',
              label: '5.1.1.2.1 - Water Report',
              title: 'Water Report',
              critical: false,
              pointValue: 2,
              description:
                'Where available, the vessel must have a copy of the most recent microbiologic report from each port before bunkering POTABLE WATER to verify that the water meets potable standards. The date of the analysis report must be 30 days or less from the date of POTABLE WATER bunkering and must include an analysis for Escherichia coli at a minimum.',
            },
            {
              section: '5.1.1.2.2',
              label: '5.1.1.2.2 - Onboard Test',
              title: 'Onboard Test',
              critical: false,
              pointValue: 2,
              description:
                'Water samples collected and analyzed by the vessel for the presence of E. coli may be substituted for the microbiologic report from each port water system. Samples must be analyzed using a method accepted in Standard Methods for the Examination of Water and Wastewater or international Environmental Protection Agency (EPA) APPROVED equivalent. Test kits, incubators, and associated equipment must be operated and maintained in accordance with the manufacturers’ specifications. If a vessel bunkers POTABLE WATER from the same port more than once per month, only one test per month is required.',
            },
            {
              section: '5.1.1.2.3',
              label: '5.1.1.2.3 - Review',
              title: 'Review',
              critical: false,
              pointValue: 2,
              description:
                'These records must be maintained on the vessel for 12 months and must be available for review during inspections.',
            },
            {
              section: '5.2.1.2.4',
              label:
                '5.2.1.2.4 - Calibration of Analyzer-Chart Recorders or Data Loggers',
              title: 'Calibration of Analyzer-Chart Recorders or Data Loggers',
              critical: false,
              pointValue: 2,
              description:
                'HALOGEN and PH analyzer-chart recorders or data loggers used instead of manual tests and logs must be calibrated at the beginning of bunkering or production and each time bunkering or production is restarted. Calibration must be recorded on a chart or in a logbook. Calibration done, checked, verified, or similar language is acceptable.\nThe free HALOGEN residual and PH measured by the HALOGEN/PH analyzer must be accurate to within 0.2 MG/L (ppm) of the free HALOGEN residual and 0.2 of the PH as measured by the manual test.',
            },
            {
              section: '5.2.1.2.5',
              label: '5.2.1.2.5 - Construction',
              title: 'Construction',
              critical: false,
              pointValue: 2,
              description:
                'HALOGEN and PH analyzer-chart recorders used on bunker water systems must be constructed and installed according to the manufacturer’s guidelines.',
            },
            {
              section: '5.2.1.2.6',
              label: '5.2.1.2.6 - Data Logger',
              title: 'Data Logger',
              critical: false,
              pointValue: 2,
              description:
                'Electronic data loggers with CERTIFIED DATA SECURITY FEATURES may be used instead of chart recorders.\nIf electronic data loggers are used, written documentation from the data logger manufacturer, such as a letter or instruction manual, must be provided to verify that the features are secure.',
            },
            {
              section: '5.4.1.1',
              label: '5.4.1.1 - Recommended Engineering Practices',
              title: 'Recommended Engineering Practices',
              critical: false,
              pointValue: 2,
              description:
                'All distribution water system chemical injection EQUIPMENT must be constructed and installed in accordance with recommended engineering practices.',
            },
            {
              section: '5.4.1.2.3',
              label: '5.4.1.2.3 - Halogen Backup Pump',
              title: 'Halogen Backup Pump',
              critical: false,
              pointValue: 2,
              description:
                'At least one backup HALOGEN pump must be installed with an active, automatic switchover feature to maintain the free HALOGEN residual if the primary pump fails, an increase in demand occurs, or the low chlorine alarm sounds.',
            },
            {
              section: '5.4.1.2.4',
              label: '5.4.1.2.4 - Chemical Injection Dosing Point',
              title: 'Chemical Injection Dosing Point',
              critical: false,
              pointValue: 2,
              description:
                'A check valve or nonreturn valve must be installed between the distribution halogen and PH pumps and the injection points. In addition,\n• The potable water distribution halogenation and PH chemical injection dosing points must be located on the delivery line downstream of the potable water pumps OR\n• If the injection dosing point is before the potable water pumps, it must be located above the chemical dosing tanks.',
            },
            {
              section: '5.5.1.1.1',
              label: '5.5.1.1.1 - Distant Point',
              title: 'Distant Point',
              critical: false,
              pointValue: 2,
              description:
                'A HALOGEN analyzer-chart recorder must be installed at a distant point in the POTABLE WATER distribution system where a significant water flow exists and represents the entire distribution system. In cases where multiple distribution loops exist and no pipes connect the loops, there must be an analyzer and chart recorder for each loop.',
            },
            {
              section: '5.5.1.1.2',
              label: '5.5.1.1.2 - Data Logger',
              title: 'Data Logger',
              critical: false,
              pointValue: 2,
              description:
                'Electronic data loggers with CERTIFIED DATA SECURITY FEATURES may be used instead of chart recorders.\nIf used, written documentation from the data logger manufacturer, such as a letter or instruction manual, must be provided to verify that the features are secure.',
            },
            {
              section: '5.5.1.2.1',
              label: '5.5.1.2.1 - Maintenance',
              title: 'Maintenance',
              critical: false,
              pointValue: 2,
              description:
                'The HALOGEN analyzer-chart recorder must be properly maintained and must be operated in accordance with the manufacturer’s instructions.\nA manual comparison test must be conducted daily to verify calibration. Calibration must be made whenever the manual test value is greater than 0.2 MG/L (ppm) higher or lower than the analyzer reading.',
            },
            {
              section: '5.5.1.2.2',
              label: '5.5.1.2.2 - Calibration',
              title: 'Calibration',
              critical: false,
              pointValue: 2,
              description:
                'The daily manual comparison test or calibration must be recorded either on the recorder chart or in a log.',
            },
            {
              section: '5.5.1.2.4',
              label: '5.5.1.2.4 - Test Kit',
              title: 'Test Kit',
              critical: false,
              pointValue: 2,
              description:
                'The HALOGEN test kit used to calibrate the HALOGEN analyzer must be accurate to within 0.2 MG/L (ppm) for HALOGEN and graduated in increments no greater than 0.2 MG/L (ppm) in the range of free HALOGEN residual normally maintained in the POTABLE WATER system.\nEnsure all reagents used with the test kit are not expired.\nWhere available, ensure appropriate secondary standards for electronic test kits are available on the vessel to verify test kit operation.',
            },
            {
              section: '5.5.2.1.1',
              label: '5.5.2.1.1 - Range',
              title: 'Range',
              critical: false,
              pointValue: 2,
              description:
                'HALOGEN analyzer-chart recorder charts must have a range of 0.0 to',
            },
            {
              section: '5.5.2.1.2',
              label: '5.5.2.1.2 - Data Logger',
              title: 'Data Logger',
              critical: false,
              pointValue: 2,
              description:
                'Electronic data loggers with CERTIFIED DATA SECURITY FEATURES used instead of chart recorders must produce records that conform to the principles of operation and data display required of the analog charts, including printing the records.',
            },
            {
              section: '5.5.2.1.3',
              label: '5.5.2.1.3 - Increments',
              title: 'Increments',
              critical: false,
              pointValue: 2,
              description:
                'Electronic data logging must be in increments of less than or equal to 15 minutes.',
            },
            {
              section: '5.5.2.2.1',
              label: '5.5.2.2.1 - Charts',
              title: 'Charts',
              critical: false,
              pointValue: 2,
              description:
                'HALOGEN analyzer-chart recorder charts must be changed, initialed, and dated daily. Charts must contain notations of any unusual events in the POTABLE WATER system.\nIf electronic data loggers are used instead of chart recorders, notations of any unusual events in the POTABLE WATER system must be recorded in a log.',
            },
            {
              section: '5.5.2.2.2',
              label: '5.5.2.2.2 - Retention',
              title: 'Retention',
              critical: false,
              pointValue: 2,
              description:
                'HALOGEN analyzer-chart recorder charts must be retained for at least 12 months and must be available for review during inspections.',
            },
            {
              section: '5.5.2.2.3',
              label: '5.5.2.2.3 - Chart Review',
              title: 'Chart Review',
              critical: false,
              pointValue: 2,
              description:
                'Records from the HALOGEN analyzer-chart recorders or data loggers must verify the free HALOGEN residual of greater than or equal to',
            },
            {
              section: '5.5.3.1.1',
              label: '5.5.3.1.1 - Every 4 hours',
              title: 'Every 4 hours',
              critical: false,
              pointValue: 2,
              description:
                'Free HALOGEN residual must be measured by a manual test kit at the HALOGEN analyzer at least every 4 hours in the event of EQUIPMENT failure.',
            },
            {
              section: '5.5.3.1.2',
              label: '5.5.3.1.2 - Recording',
              title: 'Recording',
              critical: false,
              pointValue: 2,
              description:
                'Manual readings must be recorded on a chart or log, retained for at least 12 months, and available for review during inspections.',
            },
            {
              section: '5.5.3.1.3',
              label: '5.5.3.1.3 - Limit',
              title: 'Limit',
              critical: false,
              pointValue: 2,
              description:
                'Repairs on malfunctioning HALOGEN analyzer-chart recorders must be completed within 10 days of EQUIPMENT failure.',
            },
            {
              section: '5.5.3.1.4',
              label: '5.5.3.1.4 - Alarm',
              title: 'Alarm',
              critical: false,
              pointValue: 2,
              description:
                'Provide an audible alarm in a continuously occupied watch station (for example, the engine control room) to indicate low and high free HALOGEN residual at the distant point analyzer.',
            },
            {
              section: '5.6.1.1.1',
              label: '5.6.1.1.1 - E. coli—POTABLE Water',
              title: 'E. coli—POTABLE Water',
              critical: false,
              pointValue: 2,
              description:
                'A minimum of four POTABLE WATER samples per month must be collected and analyzed for the presence of E. coli. Samples must be collected from the forward, aft, upper, and lower decks of the vessel.\nSample sites for E. coli must be changed monthly to ensure effective monitoring of the entire POTABLE WATER distribution system.\nFollow-up sampling must be conducted for each positive test result.\nMicrobiological samples from ice machines and POTABLE WATER storage tanks do not count toward the monthly routine distribution system monitoring.',
            },
            {
              section: '5.6.1.1.2',
              label: '5.6.1.1.2 - Legionella—POTABLE and Nonpotable Water',
              title: 'Legionella—POTABLE and Nonpotable Water',
              critical: false,
              pointValue: 2,
              description:
                'The POTABLE WATER system must be sampled for Legionella. This microbiological examination must be conducted at least every 6 months. Sample sites for Legionella must be representative of the POTABLE WATER and RWF systems to ensure effective monitoring. The number of samples depends on the size and complexity of the vessel.\nFollow-up actions must be taken when Legionella is detected and should be based on the type of Legionella found, the concentration (for example in CFU/ml), and the extent of colonization.\nActions can include\n• Review of the water management program.\n• Root cause analysis to understand possible reasons for Legionella growth.\n• Updates to the water management program if needed.\nConsider retesting fixtures or devices where Legionella was detected to confirm the effectiveness of the response and to determine if remediation is needed. For additional guidance, see ASHRAE Guideline 12, Appendix C5.\nThe Legionella sampling plan must include a selection of the following locations:\n• POTABLE WATER storage tanks.\n• Water heaters and hot water storage.\n• Hot water return lines.\n• Representative points of use in cabins, galleys, and recreational areas. − Include a mix of fixture types (showers, sink faucets, tubs). − Include potable water samples when temperatures are in the range favorable for Legionella growth (25°–45°C, 77°–113°F).\n• Decorative water features.\n• Misting systems.\n• Salon and pedicure devices.\n• Technical water sources with a risk of exposure via aerosolization to crew or passengers, such as deck washing.',
            },
            {
              section: '5.6.1.1.4',
              label: '5.6.1.1.4 - Analysis',
              title: 'Analysis',
              critical: false,
              pointValue: 2,
              description:
                'Onboard Legionella testing is acceptable. Operate and maintain test kits, incubators, and associated equipment following the manufacturers’ specifications. Cruise lines must use a test method validated to a national or international standard. Additional information on routine testing can be found at www.cdc.gov/control-legionella/php/toolkit/routine-testing- module.html.\nFor cluster investigations, CDC will request additional testing.',
            },
            {
              section: '5.6.1.2',
              label: '5.6.1.2 - Records',
              title: 'Records',
              critical: false,
              pointValue: 2,
              description:
                'Sample results must be maintained on the vessel for at least 12 months and must be available for review during inspections.',
            },
          ],
        },
        {
          itemNo: '07',
          label:
            '07 - System protection cross-connections, backflow; Disinfection',
          pointValue: 3,
          critical: true,
          description:
            'System protection cross-connections, backflow; Disinfection',
          subItems: [
            {
              section: '5.3.2.1.1',
              label: '5.3.2.1.1 - Potable Water Tank Walls',
              title: 'Potable Water Tank Walls',
              critical: true,
              pointValue: 3,
              description:
                'POTABLE WATER TANKS must not share a common wall with the hull of the vessel or with tanks or piping containing nonpotable water or other liquids.',
            },
            {
              section: '5.3.3.1.2',
              label: '5.3.3.1.2 - Protection',
              title: 'Protection',
              critical: true,
              pointValue: 3,
              description:
                'POTABLE WATER piping must not pass under or through tanks holding nonpotable liquids.',
            },
            {
              section: '5.3.5.1',
              label: '5.3.5.1 - Disinfecting',
              title: 'Disinfecting',
              critical: true,
              pointValue: 3,
              description:
                'POTABLE WATER TANKS and all affected parts of the POTABLE WATER distribution system must be cleaned, disinfected, and flushed with POTABLE WATER\n• Before being placed in service.\n• Before returning to operation after repair or replacement.\n• After being subjected to any CONTAMINATION, including entry into a POTABLE WATER tank.\nBackflow prevention devices that are part of the potable water piping must be disinfected before installation.\nDuring dry docks and wet docks, if any work is done to the POTABLE WATER distribution system and piping system that affects a POTABLE WATER TANK(s), the affected POTABLE WATER TANK(s) must be cleaned and disinfected.',
            },
            {
              section: '5.3.5.3',
              label: '5.3.5.3 - Disinfection Residual',
              title: 'Disinfection Residual',
              critical: true,
              pointValue: 3,
              description:
                'DISINFECTION after potential CONTAMINATION must be accomplished by increasing the free HALOGEN residual to at least 50 MG/L (ppm) throughout the affected area and maintaining this concentration for 4 hours or by way of another procedure submitted to and accepted by VSP.\nIn an emergency, this contact time may be shortened to 1 hour by increasing free HALOGEN residual to at least 200 MG/L (ppm) throughout the affected area.\nRefer to Annex 13.4 for DISINFECTION calculations for water and equipment. Refer to Annex 13.5 for examples of DISINFECTION methods.',
            },
            {
              section: '5.7.1.1.1',
              label: '5.7.1.1.1 - Cross-Connections',
              title: 'Cross-Connections',
              critical: true,
              pointValue: 3,
              description:
                'The POTABLE WATER distribution system must be maintained free of CROSS-CONNECTIONS.',
            },
            {
              section: '5.7.1.1.2',
              label: '5.7.1.1.2 - Protection',
              title: 'Protection',
              critical: true,
              pointValue: 3,
              description:
                'The POTABLE WATER system must be protected against BACKFLOW or other CONTAMINATION by BACKFLOW PREVENTION DEVICES or AIR GAPS. The PERMEATE WATER LINES and DISTILLATE WATER LINES directed toward the POTABLE WATER system must also be protected.',
            },
          ],
        },
        {
          itemNo: '08',
          label:
            '08 - Filling hoses, caps, connections, procedures; Sample records, valves; System constructed, maintenance',
          pointValue: 1,
          critical: false,
          description:
            'Filling hoses, caps, connections, procedures; Sample records, valves; System constructed, maintenance',
          subItems: [
            {
              section: '5.2.1.1.2',
              label: '5.2.1.1.2 - Within 30 Minutes',
              title: 'Within 30 Minutes',
              critical: false,
              pointValue: 1,
              description:
                'The free HALOGEN residual level must be adjusted to at least 2.0 MG/L (ppm) and the PH adjusted not to exceed 7.8 within 30 minutes of the start of the bunkering and production processes.',
            },
            {
              section: '5.2.1.2.1',
              label: '5.2.1.2.1 - Bunkering Pretest',
              title: 'Bunkering Pretest',
              critical: false,
              pointValue: 1,
              description:
                'A free HALOGEN residual and PH test must be conducted on the shore- side water supply before starting the POTABLE WATER bunkering process to establish the correct HALOGEN dosage.\nThe results of the pretest must be recorded and available for review during inspections.',
            },
            {
              section: '5.2.1.2.2',
              label: '5.2.1.2.2 - Bunkering/Production Test',
              title: 'Bunkering/Production Test',
              critical: false,
              pointValue: 1,
              description:
                'After the free HALOGEN residual level of at least 2.0 MG/L (ppm) and PH level not exceeding 7.8 have been reached, the free HALOGEN residual and PH monitoring must be performed at least hourly during the bunkering of POTABLE WATER.\nAfter the free HALOGEN residual level of at least 2.0 MG/L (ppm) and PH level not exceeding 7.8 have been reached, the free HALOGEN residual and PH monitoring must be performed at least once every 4 hours during the production of POTABLE WATER.\nA test kit must be available for measuring free HALOGEN residual levels and PH.\nTest kits must be accurate to within 0.2 MG/L (ppm) for HALOGEN and must have a testing range of free HALOGEN residual normally maintained in the POTABLE WATER system. Test kits for PH must be accurate to within 0.2.',
            },
            {
              section: '5.2.1.2.3',
              label: '5.2.1.2.3 - Records',
              title: 'Records',
              critical: false,
              pointValue: 1,
              description:
                'Accurate records of this monitoring must be maintained on the vessel for 12 months and must be available for review during inspections.',
            },
            {
              section: '5.2.1.2.7',
              label: '5.2.1.2.7 - Halogen Injection',
              title: 'Halogen Injection',
              critical: false,
              pointValue: 1,
              description:
                'Water samples for HALOGEN and PH testing must be obtained from a sample cock and/or a HALOGEN analyzer probe located on the bunker or production water line at least 3 meters (10 feet) after the HALOGEN injection point and before the storage tank.\nA static mixer may be used to reduce the distance between the HALOGEN injection point and the sample cock or HALOGEN analyzer sample point. If used, the mixer must be installed per the manufacturer’s recommendations. A copy of all manufacturers’ literature for installation, operation, and maintenance must be maintained.',
            },
            {
              section: '5.3.1',
              label: '5.3.1 - Fixtures That Require Potable Water',
              title: 'Fixtures That Require Potable Water',
              critical: false,
              pointValue: 1,
              description:
                'Supply only POTABLE WATER to the following areas and plumbing connections, regardless of the locations of these fixtures on the vessel:\n• All showers and sinks (not just in cabins).\n• Chemical feed tanks for the POTABLE WATER system or RWF systems.\n• Drinking fountains.\n• Emergency showers.\n• Eye wash stations.\n• FOOD AREAS.\n• Handwashing sinks.\n• HVAC fan rooms. *Applies to vessels built or renovated in accordance with the 2011 VSP Construction Guidelines or later.\n• Medical facilities.\n• Deck and window cleaning facilities. *Applies to vessels built or renovated in accordance with the 2018 VSP Construction Guidelines or later.\nUTILITY SINKS for engine/mechanical spaces are excluded.',
            },
            {
              section: '5.3.2.1.2',
              label: '5.3.2.1.2 - Nonpotable Piping',
              title: 'Nonpotable Piping',
              critical: false,
              pointValue: 1,
              description:
                'Piping systems carrying SEWAGE or other nonpotable liquids must not pass through POTABLE WATER TANKS. Minimize the use of nonpotable lines above POTABLE WATER TANKS. Nonpotable lines above POTABLE WATER TANKS must not have any mechanical couplings. For SCUPPER lines, factory assembled transition fittings for steel to plastic pipes are allowed when manufactured per American Society for Testing and Materials (ASTM) F1973 or equivalent standard.',
            },
            {
              section: '5.3.2.1.3',
              label: '5.3.2.1.3 - Coatings',
              title: 'Coatings',
              critical: false,
              pointValue: 1,
              description:
                'Interior coatings on POTABLE WATER TANKS must be APPROVED for POTABLE WATER contact by a certification organization. Follow all manufacturers’ recommendations for application, drying, and curing.\nThe following must be maintained on the vessel for the tank coatings used:\n• Written documentation of approval from the certification organization (independent of the coating manufacturer).\n• Manufacturers’ recommendations for application, drying, and curing.\n• Written documentation that the manufacturers’ recommendations have been followed for application, drying, and curing.',
            },
            {
              section: '5.3.2.2.1',
              label: '5.3.2.2.1 - Identification',
              title: 'Identification',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER TANKS must be identified with a number and the words “POTABLE WATER” in letters at least 13 millimeters (1/2 inch) high.',
            },
            {
              section: '5.3.2.2.2',
              label: '5.3.2.2.2 - Sample Cocks',
              title: 'Sample Cocks',
              critical: false,
              pointValue: 1,
              description:
                'Sample cocks must be pointed down, identified, and numbered with the appropriate tank number.',
            },
            {
              section: '5.3.2.2.3',
              label: '5.3.2.2.3 - Vent/Overflow',
              title: 'Vent/Overflow',
              critical: false,
              pointValue: 1,
              description:
                'The POTABLE WATER TANKS, vents, and overflows must be protected from CONTAMINATION.',
            },
            {
              section: '5.3.2.2.4',
              label: '5.3.2.2.4 - Level Measurement',
              title: 'Level Measurement',
              critical: false,
              pointValue: 1,
              description:
                'Any device for determining the depth of water in the POTABLE WATER TANKS must be constructed and maintained to prevent contaminated substances or liquids from entering the tanks.',
            },
            {
              section: '5.3.2.2.5',
              label: '5.3.2.2.5 - Manual Sounding',
              title: 'Manual Sounding',
              critical: false,
              pointValue: 1,
              description:
                'Manual sounding of POTABLE WATER TANKS must be performed only in emergencies and must be performed in a sanitary manner.',
            },
            {
              section: '5.3.3.1.1',
              label: '5.3.3.1.1 - Identification',
              title: 'Identification',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER lines must be striped or painted either in accordance with ISO 14726 (blue/green/blue) or blue only. DISTILLATE and PERMEATE WATER LINES directed to the POTABLE WATER system must be striped or painted in accordance with ISO 14726 (blue/gray/blue).\nOther lines must not have the above color designations.\nThese lines must be striped or painted at 5-meter (16.4-foot) intervals and on each side of partitions, decks, and BULKHEADS except where decor would be marred by such markings. This includes POTABLE WATER supply lines in technical lockers.\nPOTABLE WATER lines after REDUCED PRESSURE ASSEMBLIES must not be striped or painted as POTABLE WATER.\nStriping is not required in FOOD AREAS of the vessel because only POTABLE WATER is permitted in these areas.\nAll refrigerant brine lines and nonpotable chilled water lines in all FOOD AREAS, such as on ice machines, ice cream machines, etc., must be identified with either ISO 14726 (blue/white/blue) or by another uniquely identifiable method to prevent CROSS-CONNECTIONS.',
            },
            {
              section: '5.3.3.1.3',
              label: '5.3.3.1.3 - Bunker Connection',
              title: 'Bunker Connection',
              critical: false,
              pointValue: 1,
              description:
                'The POTABLE WATER bunker filling line must begin either horizontally or pointing downward and at a point at least 460 millimeters (18 inches) above the bunker station deck.',
            },
            {
              section: '5.3.3.1.4',
              label: '5.3.3.1.4 - Cap/Keeper Chain',
              title: 'Cap/Keeper Chain',
              critical: false,
              pointValue: 1,
              description:
                'The POTABLE WATER filling line must have a screw cap fastened by a NONCORRODING cable or chain to an adjacent BULKHEAD or surface in such a manner that the cap cannot touch the deck when hanging free.\nThe hose connections must be unique and fit only the POTABLE WATER hoses.',
            },
            {
              section: '5.3.3.1.5',
              label: '5.3.3.1.5 - Identification',
              title: 'Identification',
              critical: false,
              pointValue: 1,
              description:
                'Each bunker station POTABLE WATER filling line must be striped or painted blue or in accordance with the color designation in ISO 14726 (blue/green/blue) and clearly labeled “POTABLE WATER FILLING” in letters at least 13 millimeters (1/2 inch) high, stamped on a noncorrosive label plate or the equivalent, and located at or near the point of the hose connection.',
            },
            {
              section: '5.3.3.1.6',
              label: '5.3.3.1.6 - Technical Water',
              title: 'Technical Water',
              critical: false,
              pointValue: 1,
              description:
                'If used on the vessel, TECHNICAL WATER must be bunkered through separate piping using fittings incompatible for POTABLE WATER bunkering.\nThis does not apply to water bunkered from shoreside municipal water supply.',
            },
            {
              section: '5.3.3.1.7',
              label: '5.3.3.1.7 - Different Piping',
              title: 'Different Piping',
              critical: false,
              pointValue: 1,
              description:
                'TECHNICAL WATER must flow through a completely different piping system.',
            },
            {
              section: '5.3.4.1.1',
              label: '5.3.4.1.1 - Fittings',
              title: 'Fittings',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hoses must have unique fittings from all other hose fittings on the vessel.',
            },
            {
              section: '5.3.4.1.2',
              label: '5.3.4.1.2 - Identification',
              title: 'Identification',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hoses must be labeled for use with the words “POTABLE WATER ONLY” in letters at least 13 millimeters (1/2 inch) high at each connecting end.',
            },
            {
              section: '5.3.4.1.3',
              label: '5.3.4.1.3 - Construction',
              title: 'Construction',
              critical: false,
              pointValue: 1,
              description:
                'All hoses, fittings, and water filters used in the bunkering of POTABLE WATER must be constructed of safe, easy-to-clean materials APPROVED for POTABLE WATER use and must be maintained in good repair.',
            },
            {
              section: '5.3.4.1.4',
              label: '5.3.4.1.4 - Other Equipment',
              title: 'Other Equipment',
              critical: false,
              pointValue: 1,
              description:
                'Other equipment and tools used in the bunkering of POTABLE WATER must be constructed of safe, EASILY CLEANABLE materials, dedicated solely for POTABLE WATER use, and maintained in good repair.',
            },
            {
              section: '5.3.4.1.5',
              label: '5.3.4.1.5 - Locker Construction',
              title: 'Locker Construction',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hose lockers must be constructed of smooth, nontoxic, corrosion resistant, easy-to-clean material and must be maintained in good repair.',
            },
            {
              section: '5.3.4.1.6',
              label: '5.3.4.1.6 - Locker Identification',
              title: 'Locker Identification',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hose lockers must be labeled “POTABLE WATER HOSE AND FITTING STORAGE” in letters at least 13 millimeters (1/2 inch) high.',
            },
            {
              section: '5.3.4.1.7',
              label: '5.3.4.1.7 - Locker Height',
              title: 'Locker Height',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hose lockers must be mounted at least 460 millimeters (18 inches) above the deck and must be self-draining.',
            },
            {
              section: '5.3.4.1.8',
              label: '5.3.4.1.8 - Locker Closed',
              title: 'Locker Closed',
              critical: false,
              pointValue: 1,
              description: 'Locker doors must be closed when not in use.',
            },
            {
              section: '5.3.4.1.9',
              label: '5.3.4.1.9 - Locker Restriction',
              title: 'Locker Restriction',
              critical: false,
              pointValue: 1,
              description:
                'The locker must not be used for any other purpose than storing POTABLE WATER equipment such as hoses, fittings, sanitizing buckets, SANITIZER solution, etc.',
            },
            {
              section: '5.3.4.2.1',
              label: '5.3.4.2.1 - Limit Use',
              title: 'Limit Use',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hoses must not be used for any other purpose.',
            },
            {
              section: '5.3.4.2.2',
              label: '5.3.4.2.2 - Handling',
              title: 'Handling',
              critical: false,
              pointValue: 1,
              description:
                'All hoses, fittings, water filters, buckets, equipment, and tools used for connection with the bunkering of POTABLE WATER must be handled and stored in a sanitary manner.',
            },
            {
              section: '5.3.4.2.3',
              label: '5.3.4.2.3 - Contamination Prevention',
              title: 'Contamination Prevention',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hoses must be handled with care to prevent CONTAMINATION from dragging their ends on the ground, pier, or deck surfaces, or from dropping the hose into contaminated water, such as on the pier or in the HARBOR.',
            },
            {
              section: '5.3.4.2.4',
              label: '5.3.4.2.4 - Flush/Drain',
              title: 'Flush/Drain',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hoses must be flushed with POTABLE WATER before being used and must be drained after each use.',
            },
            {
              section: '5.3.4.2.5',
              label: '5.3.4.2.5 - Storage',
              title: 'Storage',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER hoses must be rolled tight with the ends capped, on reels or on racks or with ends coupled together and stowed in POTABLE WATER hose lockers.',
            },
            {
              section: '5.3.5.2',
              label: '5.3.5.2 - Inspection',
              title: 'Inspection',
              critical: false,
              pointValue: 1,
              description:
                'POTABLE WATER TANKS must be inspected, cleaned, and disinfected at least every 2 years.',
            },
            {
              section: '5.3.5.4',
              label: '5.3.5.4 - Flush',
              title: 'Flush',
              critical: false,
              pointValue: 1,
              description:
                'The disinfected parts of the system must be flushed with POTABLE WATER or otherwise dechlorinated until the free HALOGEN residual is less than or equal to',
            },
            {
              section: '5.3.5.5',
              label: '5.3.5.5 - Record Retention',
              title: 'Record Retention',
              critical: false,
              pointValue: 1,
              description:
                'Documentation of all inspections, maintenance, cleaning, and DISINFECTION must be maintained for 12 months and must be available for review during inspections.\nRecords must include method of DISINFECTION, concentration and contact time of the DISINFECTANT, and the measured free HALOGEN residual after flushing.',
            },
            {
              section: '5.3.6.1',
              label: '5.3.6.1 - Method for Disinfecting Filled Tanks',
              title: 'Method for Disinfecting Filled Tanks',
              critical: false,
              pointValue: 1,
              description:
                'DISINFECTION must be accomplished by increasing the free HALOGEN residual to at least 50 MG/L (ppm) throughout the tank and maintaining it for 4 hours. Maintain a PH value of 7.8 or less. The DISINFECTION concentration and contact time must be documented.\nVerify that the free HALOGEN residual level is less than or equal to 5.0 MG/L (ppm) and document the measured level before placing the tank back into service.',
            },
            {
              section: '5.3.6.2',
              label: '5.3.6.2 - Method for Disinfecting Empty Tanks',
              title: 'Method for Disinfecting Empty Tanks',
              critical: false,
              pointValue: 1,
              description:
                'The empty tank cleaning and DISINFECTION procedure is only APPROVED for routine cleaning and DISINFECTION. It is not APPROVED for known or suspected contaminated tanks.\n1. Remove (strip) all water from the tank.\n2. Clean all tank surfaces, including filling lines, with an appropriate detergent.\n3. Rinse all surfaces of the tank thoroughly with POTABLE WATER.\n4. Remove (strip) the rinse water from the tank.\n5. Wet all surfaces of the tank with at least a 200-MG/L (ppm) solution of chlorine (this can be done using new, clean mops, rollers, sprayers, etc.). Ensure the tank surfaces remain wet with the chlorine solution for at least 2 hours. Check, monitor, and document the DISINFECTION concentration and contact time.\n6. Refill the tank and verify the free HALOGEN residual is less than or equal to 5.0 MG/L (ppm) before placing the tank back into service. Document the free HALOGEN residual level.',
            },
            {
              section: '5.4.1.2.2',
              label: '5.4.1.2.2 - Controlled',
              title: 'Controlled',
              critical: false,
              pointValue: 1,
              description:
                'The amount of chemicals injected into the POTABLE WATER system must be controlled by an analyzer.',
            },
            {
              section: '5.7.1.1.3',
              label: '5.7.1.1.3 - Control/Program',
              title: 'Control/Program',
              critical: false,
              pointValue: 1,
              description:
                'The vessel must provide a comprehensive CROSS-CONNECTION control program (refer to Annex 13.6).\nThe following connections to the POTABLE WATER system must be protected against BACKFLOW (BACKSIPHONAGE or BACKPRESSURE) with AIR GAPS or mechanical BACKFLOW PREVENTION DEVICES:\n• RWFs such as SWIMMING POOLS, CHILDREN’S/WADING POOLS, WHIRLPOOL SPAS, and similar facilities.\n• Decorative water features/fountains.\n• Cabin shower hoses, toilets, JETTED TUBS, and similar facilities.\n• Photographic laboratory developing machines and UTILITY SINKS.\n• Beauty and barber shop spray-rinse hoses.\n• Spa steam generators where essential oils can be added.\n• Hose bibb connections.\n• Garbage grinders and FOOD WASTE SYSTEMS.\n• Automatic galley hood washing systems.\n• FOOD service EQUIPMENT such as coffee machines, ice machines, juice dispensers, combination ovens, and similar EQUIPMENT.\n• Mechanical WAREWASHING machines.\n• Detergent dispensers.\n• Hospital and laundry EQUIPMENT.\n• Air conditioning expansion tanks.\n• Boiler feed water tanks.\n• Fire system.\n• Public toilets, urinals, and shower hoses.\n• POTABLE WATER, bilge, and pumps that require priming.\n• Freshwater or saltwater ballast systems.\n• International fire and fire sprinkler water connections. An RP ASSEMBLY is the only allowable device for this connection.\n• POTABLE WATER supply to automatic window washing systems that can be used with chemicals or chemical mix tanks.\n• Water softeners for nonpotable fresh water.\n• Water softener and mineralizer drain lines including backwash drain lines. The only allowable protections for these lines are an AIR GAP or an RP ASSEMBLY.\n• High saline discharge line from evaporators. The only allowable protections for these lines are an AIR GAP or an RP ASSEMBLY.\n• Chemical tanks.\n• Other connections between the POTABLE WATER system and a nonpotable water system such as the GRAY WATER system, laundry system, or TECHNICAL WATER system. The only allowable forms of protection for these connections are an AIR GAP or an RP ASSEMBLY.\n• BLACK WATER or combined GRAY WATER/BLACK WATER systems. An AIR GAP is the only allowable protection for these connections.\n• Any other connection to the POTABLE WATER system where CONTAMINATION or BACKFLOW can occur.\n• Hi-Fog or similar suppression systems connected to POTABLE WATER TANKS.',
            },
            {
              section: '5.7.1.1.4',
              label: '5.7.1.1.4 - Log',
              title: 'Log',
              critical: false,
              pointValue: 1,
              description:
                'A CROSS-CONNECTION control program must include, at a minimum, a complete listing of CROSS-CONNECTIONS and the BACKFLOW prevention method or device for each so there is a match to the PLUMBING SYSTEM component and location. AIR GAPS must be included in the listing. The required information can be found in Annex 13.6.5.\nAIR GAPS on faucet taps do not need to be included on the CROSS- CONNECTION control program listing.\nThe program must set a schedule for inspection frequency. Repeat devices such as toilets may be grouped under a single device type.\nA log documenting the inspection and maintenance in written or electronic form must be maintained and be available for review during inspections.',
            },
            {
              section: '5.7.1.2.1',
              label: '5.7.1.2.1 - Air Gaps and Backflow Prevention Devices',
              title: 'Air Gaps and Backflow Prevention Devices',
              critical: false,
              pointValue: 1,
              description:
                'AIR GAPS should be used where feasible and where water under pressure is not required.\nBACKFLOW PREVENTION DEVICES must be installed when AIR GAPS are impractical or when water under pressure is required. Provide an AIR GAP for the atmospheric vent of all BACKFLOW PREVENTION DEVICES.\nA mechanical BACKFLOW PREVENTION DEVICE must have an atmospheric vent.',
            },
            {
              section: '5.7.1.2.2',
              label: '5.7.1.2.2 - 2X Diameter',
              title: '2X Diameter',
              critical: false,
              pointValue: 1,
              description:
                'AIR GAPS must be at least twice the diameter of the delivery fixture opening and a minimum of 25 millimeters (1 inch).',
            },
            {
              section: '5.7.1.2.3',
              label: '5.7.1.2.3 - Flood-Level Rim',
              title: 'Flood-Level Rim',
              critical: false,
              pointValue: 1,
              description:
                'An ATMOSPHERIC VACUUM BREAKER must be installed at least 150 millimeters (6 inches) above the flood-level rim of the fixtures.',
            },
            {
              section: '5.7.1.2.4',
              label: '5.7.1.2.4 - After Valve',
              title: 'After Valve',
              critical: false,
              pointValue: 1,
              description:
                'An ATMOSPHERIC VACUUM BREAKER must be installed only in the supply line on the discharge side of the last control valve.',
            },
            {
              section: '5.7.1.2.5',
              label: '5.7.1.2.5 - Continuous Pressure',
              title: 'Continuous Pressure',
              critical: false,
              pointValue: 1,
              description:
                'A CONTINUOUS PRESSURE BACKFLOW PREVENTION DEVICE must be installed when a valve is located downstream from the BACKFLOW PREVENTION DEVICE.',
            },
            {
              section: '5.7.1.2.6',
              label: '5.7.1.2.6 - Backflow Prevention Devices',
              title: 'Backflow Prevention Devices',
              critical: false,
              pointValue: 1,
              description:
                'BACKFLOW PREVENTION DEVICES must be provided on all fixtures using POTABLE WATER and that have submerged inlets.',
            },
            {
              section: '5.7.1.2.7',
              label: '5.7.1.2.7 - Vacuum Toilets',
              title: 'Vacuum Toilets',
              critical: false,
              pointValue: 1,
              description:
                'An ATMOSPHERIC VACUUM BREAKER must be installed on a POTABLE WATER supply connected to a vacuum toilet system. An ATMOSPHERIC VACUUM BREAKER must be located on the discharge side of the last control valve (flushing device).',
            },
            {
              section: '5.7.1.2.8',
              label: '5.7.1.2.8 - Diversion Valves',
              title: 'Diversion Valves',
              critical: false,
              pointValue: 1,
              description:
                'Lines to divert POTABLE WATER to other systems by valves or interchangeable pipe fittings must have an AIR GAP after the valve.',
            },
            {
              section: '5.7.1.2.9',
              label: '5.7.1.2.9 - Location',
              title: 'Location',
              critical: false,
              pointValue: 1,
              description:
                'BACKFLOW PREVENTION DEVICES and AIR GAPS must be ACCESSIBLE for inspection, testing, service, and maintenance. If access panels are required, provide panels large enough for testing, service, and maintenance.',
            },
            {
              section: '5.7.1.3.1',
              label: '5.7.1.3.1 - Air Supply',
              title: 'Air Supply',
              critical: false,
              pointValue: 1,
              description:
                'A compressed air system that supplies pressurized air to both nonpotable and POTABLE WATER pneumatic tanks must be connected through a press-on (manual) air valve or hose.',
            },
            {
              section: '5.7.2.1.1',
              label: '5.7.2.1.1 - Maintained',
              title: 'Maintained',
              critical: false,
              pointValue: 1,
              description:
                'BACKFLOW PREVENTION DEVICES must be maintained in good repair.',
            },
            {
              section: '5.7.2.2.1',
              label: '5.7.2.2.1 - Schedule',
              title: 'Schedule',
              critical: false,
              pointValue: 1,
              description:
                'BACKFLOW PREVENTION DEVICES must be periodically inspected, and any failed units must be replaced.\nA visual check must be completed for all nontestable BACKFLOW PREVENTION DEVICES and AIR GAPS at least annually.',
            },
            {
              section: '5.7.2.2.2',
              label: '5.7.2.2.2 - Test Annually',
              title: 'Test Annually',
              critical: false,
              pointValue: 1,
              description:
                'BACKFLOW PREVENTION DEVICES requiring testing (for example, reduced pressure BACKFLOW PREVENTION DEVICES and PRESSURE VACUUM BREAKERS) must be inspected and tested with a test kit after installation and at least annually. Test results showing the pressure differences on both sides of the valves must be maintained for each device.',
            },
            {
              section: '5.7.2.2.3',
              label: '5.7.2.2.3 - Records',
              title: 'Records',
              critical: false,
              pointValue: 1,
              description:
                'The visual inspection and/or test results for BACKFLOW PREVENTION DEVICES and AIR GAPS must be retained for at least 12 months and must be available for review during inspections.',
            },
          ],
        },
      ],
    },
    {
      id: 'recreational-water-facilities',
      name: 'Recreational Water Facilities',
      items: [
        {
          itemNo: '09',
          label: '09 - RWF halogen residuals',
          pointValue: 3,
          critical: true,
          description: 'RWF halogen residuals',
          subItems: [
            {
              section: '6.1.1',
              label: '6.1.1 - Potable Water or Seawater',
              title: 'Potable Water or Seawater',
              critical: true,
              pointValue: 3,
              description:
                'The water source for all RWFs must be POTABLE WATER or RECREATIONAL SEAWATER.',
            },
            {
              section: '6.2.1.2.2',
              label: '6.2.1.2.2 - Halogen and pH',
              title: 'Halogen and pH',
              critical: true,
              pointValue: 3,
              description:
                'When switching from flow-through operations to recirculation operations, the RWF must be closed until the free HALOGEN residual and PH levels are within the acceptable limits of these standards. The sample must be taken from the body of the RWF, not only from the RWF EQUIPMENT ROOM or AREA.\nWhile the RWF is closed, batch HALOGEN and PH control chemicals may be used to obtain ADEQUATE free HALOGEN residuals and PH levels faster. Sufficient time should be allowed before opening the RWF for use to ensure proper mixing of batch chemicals.',
            },
            {
              section: '6.3.2.1.1',
              label: '6.3.2.1.1 - Residual',
              title: 'Residual',
              critical: true,
              pointValue: 3,
              description:
                'A free HALOGEN residual in the range detailed in the table below must be maintained in recirculated RWFs. RWFs not maintained within these HALOGEN ranges must be immediately closed.\nFree HALOGEN Residual, RECREATIONAL WATER FACILITY MG/L (ppm) SWIMMING POOL ≥ 1.0 and ≤ 5.0 CHILDREN’S POOL ≥ 1.0 and ≤ 5.0 WADING POOL ≥ 1.0 and ≤ 5.0 INTERACTIVE RWF (ACTIVITY POOL) ≥ 2.0 and ≤ 5.0\nBABY-ONLY WATER FACILITY ≥ 3.0 and ≤ 10.0 free chlorine ≥ 4.0 and ≤ 10.0 bromine WHIRLPOOL SPA and SPA POOL ≥ 3.0 and ≤ 10.0 free chlorine ≥ 4.0 and ≤ 10.0 bromine\nFor RWFs that meet the definition of more than one type of RWF, the more protective HALOGEN residual applies. For example, if a CHILDREN’S POOL also has features of an interactive RWF or ACTIVITY POOL, the HALOGEN range must be 2.0 to 5.0 MG/L (ppm). Finally, if an RWF is modified, the most protective HALOGEN residual applies.',
            },
            {
              section: '6.3.2.1.2',
              label: '6.3.2.1.2 - pH',
              title: 'pH',
              critical: true,
              pointValue: 3,
              description:
                'The PH level in all RWFs must be maintained between 7.0 and 7.8. Facilities not maintained within these PH ranges must be immediately closed.',
            },
          ],
        },
        {
          itemNo: '10',
          label: '10 - RWF maintenance, safety equipment',
          pointValue: 2,
          critical: false,
          description: 'RWF maintenance, safety equipment',
          subItems: [
            {
              section: '6.1.2',
              label: '6.1.2 - Kept Clean',
              title: 'Kept Clean',
              critical: false,
              pointValue: 2,
              description:
                'RWFs must be kept clean of debris, organic materials, and slime/biofilm in the water and on surfaces.\nPeriodic biofilm management is recommended for WHIRLPOOL SPAS, SPA POOLS, and interactive RWFs.',
            },
            {
              section: '6.2.1.1.1',
              label: '6.2.1.1.1 - 12 miles',
              title: '12 miles',
              critical: false,
              pointValue: 2,
              description:
                'Flow-through seawater supply systems for RWFs must be used only while the vessel is MAKING WAY and at sea beyond 20 kilometers (12 miles) from nearest land.',
            },
            {
              section: '6.2.1.2.1',
              label: '6.2.1.2.1 - Drained or Switched to Recirculation',
              title: 'Drained or Switched to Recirculation',
              critical: false,
              pointValue: 2,
              description:
                'Before arriving to a port or HARBOR, the RWF must be drained before the vessel reaches the 20-kilometer (12-mile) mark or any point of land- based discharge as detailed in section 6.2.1.1.1, and it must remain empty while in port or at anchor. OR\nThe RECREATIONAL SEAWATER filling system must be shut off 20 kilometers (12 miles) before reaching the nearest land or land-based discharge point, and a recirculation system must be used with appropriate filtration and halogenation systems.',
            },
            {
              section: '6.3.1.1',
              label: '6.3.1.1 - Fill Level and Turnover Rates',
              title: 'Fill Level and Turnover Rates',
              critical: false,
              pointValue: 2,
              description:
                'For RWFs with skim gutters, the water level must at least reach the skim gutter or overflow level for a portion of the pool perimeter to remove floating debris and the top portion of the pool water for treatment.\nRecirculation systems and EQUIPMENT, including chemical control EQUIPMENT, ultraviolet (UV) DISINFECTION systems, filters, and pumps, must be designed to maintain ADEQUATE water chemistry control while operating at the following TURNOVER rates:\nRECREATIONAL WATER FACILITY TURNOVER Rate SWIMMING POOL (VSP 2005 Construction Guidelines 6 hours or earlier) SWIMMING POOL (VSP 2011 Construction Guidelines 4 hours or later) CHILDREN’S POOL 0.5 hours WADING POOL 1 hour WHIRLPOOL SPA 0.5 hours SPA POOL 2 hours INTERACTIVE RWF or ACTIVITY POOL (less than 1 hour 600 millimeters [24 inches] deep) RECREATIONAL WATER FACILITY TURNOVER Rate INTERACTIVE RWF or ACTIVITY POOL (greater 2 hours than 600 millimeters [24 inches] deep) BABY-ONLY WATER FACILITY 0.5 hours\nSPA POOLS constructed before June 1, 2005, and originally considered SWIMMING POOLS may have a TURNOVER rate not to exceed 6 hours in a 24-hour period. This does not apply to SWIMMING POOLS that have been converted to SPA POOLS.\nInstall flow meters to monitor flow rates. Use flow rates from flow meters to calculate TURNOVER rates. Do not use the manufacturer’s pump rate to calculate TURNOVER rates.\nFor facilities that meet the definition of more than one type of RWF, the most protective TURNOVER rate applies. For example, if a CHILDREN’S POOL also has features of an interactive RWF or ACTIVITY POOL, the TURNOVER rate must be',
            },
            {
              section: '6.3.1.2.1',
              label: '6.3.1.2.1 - Filtered',
              title: 'Filtered',
              critical: false,
              pointValue: 2,
              description: 'Recirculated RWF water must be filtered.',
            },
            {
              section: '6.3.1.2.2',
              label: '6.3.1.2.2 - Filter Backwash and Cleaning',
              title: 'Filter Backwash and Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'Filter pressure differentials must be monitored. Granular filter media must be backwashed until the water viewed through a sight glass runs clear and at the following frequency:\n• WHIRLPOOL SPA and SPA POOL: every 72 hours, or sooner if the WHIRLPOOL SPA is drained.\n• BABY-ONLY WATER FACILITY: daily.\n• All other RWFs: at a frequency recommended by the manufacturer.\nFor automatic backwashing systems, an individual must be present in the filter room to ensure that backwashing is repeated as necessary until the water runs clear.\nCartridge filters must be cleaned according to the manufacturer’s recommendations.\nA written or electronic record of the filter backwashing and cleaning must be available for review during inspections.',
            },
            {
              section: '6.3.1.2.3',
              label:
                '6.3.1.2.3 - Granular Filter Inspection, Core Sample Test, and Filter Change',
              title:
                'Granular Filter Inspection, Core Sample Test, and Filter Change',
              critical: false,
              pointValue: 2,
              description:
                'Granular filter media must be examined for channels, mounds, or holes. A core sample of the filter media must be inspected for excessive organic material accumulation using a recommended sedimentation method.\nInspections and sedimentation tests must be conducted quarterly for all RWFs.\nInspection method:\nDrain the water from the filter housing and inspect the granular filter for channels, mounds, or holes.\nCore sample method:\n1. After inspection, take a sand sample from the filter core and place it in a clear container. A core sample can be taken by inserting a rigid hollow tube or pipe into the filter media.\n2. Add clean water to the container, cover, and shake.\n3. Allow the container to rest undisturbed for 30 minutes.\n4. Consider media replacement if after 30 minutes of settling, a measurable layer of sediment is within or on top of the filter media or fine, colored particles are suspended in the water.',
            },
            {
              section: '6.3.1.2.4',
              label:
                '6.3.1.2.4 - Cartridge Filter Inspection and Filter Change',
              title: 'Cartridge Filter Inspection and Filter Change',
              critical: false,
              pointValue: 2,
              description:
                'Cartridge or canister-type filters must be inspected weekly for WHIRLPOOL SPAS and SPA POOLS. For all other RWFs, cartridge filters must be inspected every 2 weeks, or in accordance with the manufacturer’s recommendation, whichever is more frequent.\nThe filters must be inspected for cracks, breaks, damaged components, and excessive organic accumulation. Cartridge or canister-type filters must be changed based on the inspection results, or as recommended by the manufacturer, whichever is more frequent.\nAt least one replacement cartridge or canister-type filter must be available.',
            },
            {
              section: '6.3.1.2.5',
              label: '6.3.1.2.5 - Other Filter Media',
              title: 'Other Filter Media',
              critical: false,
              pointValue: 2,
              description:
                'Inspect and change filters based on the manufacturer’s recommendations.',
            },
            {
              section: '6.3.1.2.6',
              label: '6.3.1.2.6 - Filter Housing Cleaning and Disinfection',
              title: 'Filter Housing Cleaning and Disinfection',
              critical: false,
              pointValue: 2,
              description:
                'The filter housing must be cleaned, rinsed, and disinfected before the new filter media is placed in it. Cartridge Filter Cleaning Procedures can be found in Annex 13.9.\nDISINFECTION must be accomplished with an appropriate HALOGEN- based DISINFECTANT. At a minimum, a 50-MG/L (ppm) solution for 1 minute, or equivalent CT VALUE, must be used. Records must be maintained on all inspection and cleaning procedures.\nThe filter housing must be cleaned, rinsed, and disinfected each time the filter media—including cartridge filter—is changed.',
            },
            {
              section: '6.3.1.2.7',
              label: '6.3.1.2.7 - Hair and Lint Strainer',
              title: 'Hair and Lint Strainer',
              critical: false,
              pointValue: 2,
              description:
                'The hair and lint strainer and hair and lint strainer housing on all RWFs must be cleaned, rinsed, and disinfected weekly.\nDISINFECTION must be accomplished with an appropriate HALOGEN- based DISINFECTANT. At a minimum, a 50-MG/L (ppm) solution for 1 minute, or equivalent CT VALUE, must be used. Records must be maintained on all inspection and cleaning procedures.\nIf there is not a hair and lint strainer but there is a filter before the pump, this filter must be cleaned, rinsed, and disinfected weekly.',
            },
            {
              section: '6.3.1.2.8',
              label: '6.3.1.2.8 - All Filters',
              title: 'All Filters',
              critical: false,
              pointValue: 2,
              description:
                'The manufacturer’s maintenance procedures and recommendations for all filters must be maintained on the vessel.',
            },
            {
              section: '6.3.1.3',
              label: '6.3.1.3 - Gauges',
              title: 'Gauges',
              critical: false,
              pointValue: 2,
              description:
                'RWF filter pressure gauges, flow meters, and valves must be replaced when they are defective.',
            },
            {
              section: '6.3.1.4',
              label: '6.3.1.4 - Manuals',
              title: 'Manuals',
              critical: false,
              pointValue: 2,
              description:
                'The operating manuals for all RWF components such as filters, pumps, halogenation and PH control systems, and ultraviolet (UV) DISINFECTION systems must be maintained in a location available to crew members responsible for the technical operation and maintenance of these facilities.',
            },
            {
              section: '6.3.1.5',
              label: '6.3.1.5 - Bather Loads',
              title: 'Bather Loads',
              critical: false,
              pointValue: 2,
              description:
                'Documentation must be maintained on the maximum bather load for each RWF. The maximum bather load must be based on the following factor: One person per 19 liters (5 gallons) per minute of recirculation flow.\nUse flow rates from flow meters to calculate bather loads. Do not use the manufacturer’s pump rate to calculate bather loads.',
            },
            {
              section: '6.3.1.6.1',
              label: '6.3.1.6.1 - Water Chemistry',
              title: 'Water Chemistry',
              critical: false,
              pointValue: 2,
              description:
                'RWF flow rates, free and combined HALOGEN levels, PH, total alkalinity, and clarity must be monitored and adjusted as recommended by the manufacturer to maintain optimum public health protection and water chemistry. To ensure clarity, drain covers must be clearly visible when the water is still.\nEvaluate bather load and adjust water parameters to maintain optimum water quality.\nOnly combined chlorine must be monitored. Combined bromine does not need to be monitored.\n“Manufacturer” refers to the RWF manufacturer and to manufacturers of the pumps, filters, flow meters, and any other associated EQUIPMENT.',
            },
            {
              section: '6.3.1.6.2',
              label: '6.3.1.6.2 - Fecal and Vomit Accident',
              title: 'Fecal and Vomit Accident',
              critical: false,
              pointValue: 2,
              description:
                'A fecal and vomit accident response procedure that meets or exceeds the procedure provided in Annex 13.8 must be available for review during inspections.',
            },
            {
              section: '6.3.1.6.3',
              label: '6.3.1.6.3 - Record of Fecal and Vomit Accidents',
              title: 'Record of Fecal and Vomit Accidents',
              critical: false,
              pointValue: 2,
              description:
                'A written or electronic record must be made of all accidents involving fecal material or vomit. The record must include the name of the RWF, date and time of the accident, type of accident, response steps taken, and free HALOGEN residual level, PH level, contact time, and temperature during DISINFECTION. For a fecal accident, the record must also include whether the fecal material was formed or loose.',
            },
            {
              section: '6.3.2.1',
              label:
                '6.3.2.1 - Residual Halogen: Halogen and pH Dosing Systems',
              title: 'Residual Halogen: Halogen and pH Dosing Systems',
              critical: false,
              pointValue: 2,
              description:
                'Automated systems must be installed for HALOGEN-based DISINFECTION and PH control dosing. The amounts injected must be controlled by flow meters or free HALOGEN and PH analyzers.\nWhen conducting manual tests, consider the HALOGEN and PH levels in the RWF over the HALOGEN and PH readings from the RWF EQUIPMENT ROOM or AREA. Initial chemistry balance can be achieved by manual dosing methods following events such as fecal or vomit accidents and when changing from flow-through seawater to recirculation mode.',
            },
            {
              section: '6.3.2.1.3',
              label: '6.3.2.1.3 - Maintenance',
              title: 'Maintenance',
              critical: false,
              pointValue: 2,
              description:
                'Halogenation and PH control systems must be maintained in good repair and operated in accordance with the manufacturer’s recommendations.',
            },
            {
              section: '6.3.2.1.5',
              label: '6.3.2.1.5 - Procedures for Water Sampling',
              title: 'Procedures for Water Sampling',
              critical: false,
              pointValue: 2,
              description:
                'Manual samples from the RWF tub must be compared to the analyzer samples in the RWF EQUIPMENT ROOM OR AREA to assess potential water quality differences in the RWF.\nRecommended manual sampling procedures—All samples should be obtained from a location with the following qualities:\n• At least 457 millimeters (18 inches) below the surface of the water AND\n• At water depth of 914 millimeters to 1.2 meters (3 to 4 feet) when available AND\n• Between water inlets.',
            },
            {
              section: '6.3.2.2.1',
              label: '6.3.2.2.1 - Test Kit',
              title: 'Test Kit',
              critical: false,
              pointValue: 2,
              description:
                'A test kit must be available for measuring water quality parameters including free and total HALOGEN levels (chlorine and bromine, where applicable), PH, and total alkalinity.\nTest kits must be accurate to within 0.2 MG/L (ppm) for HALOGEN and must have a testing range of free HALOGEN residual normally maintained in the RWF. Test kits for PH must be accurate to within 0.2.\nReagents must not be expired.',
            },
            {
              section: '6.3.2.2.2',
              label: '6.3.2.2.2 - Test Kit Maintenance and Verification',
              title: 'Test Kit Maintenance and Verification',
              critical: false,
              pointValue: 2,
              description:
                'Where available, appropriate secondary standards for test kits are available on the vessel to verify test kit operation.',
            },
            {
              section: '6.3.2.2.3',
              label:
                '6.3.2.2.3 - Automated Free Halogen Residual and pH Testing',
              title: 'Automated Free Halogen Residual and pH Testing',
              critical: false,
              pointValue: 2,
              description:
                'Install chart recorders or electronic data loggers with CERTIFIED DATA SECURITY FEATURES that record PH and HALOGEN measurements for each individual RWF. The sample line for the analyzer probe (monitoring) must be either directly from the RWF or on the return line from each RWF and before the compensation tank. Install appropriate sample taps for analyzer calibration. In the event of EQUIPMENT failure, free HALOGEN residual and PH must be measured by a manual test kit at the RWF or return line at least hourly for WHIRLPOOL SPAS, SPA POOLS, CHILDREN’S POOLS, and WADING POOLS and every 4 hours for all other RWFs. Manual readings must be recorded on a chart or log, retained for at least 12 months, and available for review during inspections.\nIf two RWFs are combined and the water for one RWF comes from and returns to the basin of the other RWF (not the compensation tank), separate monitoring systems are not required. Monitoring is then only required on the main RWF (for example, a slide and SWIMMING POOL). Note: During operational inspections, VSP will take manual samples of both RWFs.\nManual samples from the RWF tub should be compared to the analyzer samples in the RWF EQUIPMENT ROOM or AREA to assess potential water quality differences in the RWF.\nRepairs on malfunctioning HALOGEN analyzer-chart recorders must be completed within 30 days of EQUIPMENT failure.\nProvide an audible alarm in a continuously occupied watch station (for example, the engine control room) to indicate low and high free HALOGEN and PH readings in each RWF.',
            },
            {
              section: '6.3.2.2.4',
              label: '6.3.2.2.4 - Whirlpool and Spa Pool Probes',
              title: 'Whirlpool and Spa Pool Probes',
              critical: false,
              pointValue: 2,
              description:
                'For WHIRLPOOL SPAS and SPA POOLS, the analyzer probes for dosing and recording systems must be capable of measuring and recording levels up to 10 MG/L (10 ppm).',
            },
            {
              section: '6.3.2.2.5',
              label: '6.3.2.2.5 - Analyzer-Chart Recorder',
              title: 'Analyzer-Chart Recorder',
              critical: false,
              pointValue: 2,
              description:
                'The HALOGEN and PH analyzer-chart recorder must be properly maintained and operated in accordance with the manufacturer’s instructions.\nA manual comparison test must be conducted before opening the RWF to verify calibration for free HALOGEN residual and PH. The analyzer reading must be within 0.2 MG/L (ppm) for free HALOGEN and 0.2 for PH.\nFor RWFs open longer than 24 hours, a manual comparison test must be conducted every 24 hours.',
            },
            {
              section: '6.3.2.2.6',
              label: '6.3.2.2.6 - Data Logger',
              title: 'Data Logger',
              critical: false,
              pointValue: 2,
              description:
                'If an electronic data logger is used instead of a chart recorder, it must have CERTIFIED DATA SECURITY FEATURES.\nManual comparison tests for free HALOGEN residual and PH must be conducted before opening the RWF to verify calibration. The analyzer reading must be within 0.2 MG/L (ppm) for free HALOGEN and 0.2 for PH.\nFor RWFs open longer than 24 hours, a manual comparison test must be conducted every 24 hours.\nElectronic data logging must be in increments of less than or equal to 15 minutes.',
            },
            {
              section: '6.3.2.2.7',
              label: '6.3.2.2.7 - Charts',
              title: 'Charts',
              critical: false,
              pointValue: 2,
              description:
                'HALOGEN analyzer-chart recorder charts must be initialed, dated, and changed daily.\nStrip recorder charts must be initialed and dated daily and 24-hour increments must be indicated.\nDual-scale HALOGEN analyzer-chart recorder charts used in RWFs must be able to measure the full range of HALOGEN and PH of the facility type to which the chart is installed; for example, chlorine or bromine to',
            },
            {
              section: '6.3.2.2.8',
              label: '6.3.2.2.8 - Logs',
              title: 'Logs',
              critical: false,
              pointValue: 2,
              description:
                'Logs and charts must contain notations outlining actions taken when the free HALOGEN residual or PH levels are outside of the acceptable ranges in these standards.\nAdditionally, records must include any major maintenance work on the filtration and halogenation systems and UV DISINFECTION systems.',
            },
            {
              section: '6.3.2.2.9',
              label: '6.3.2.2.9 - Retention',
              title: 'Retention',
              critical: false,
              pointValue: 2,
              description:
                'A written or electronic log of RWF filter inspection results, granular filter sedimentation test results, backwashing frequency and length of backwashing, and date and time of water dumping must be available for review during inspections. Logs and charts must be retained for 12 months.',
            },
            {
              section: '6.4.1.1',
              label: '6.4.1.1 - Filter Replacement',
              title: 'Filter Replacement',
              critical: false,
              pointValue: 2,
              description:
                'At least one replacement cartridge or canister-type filter must be available.',
            },
            {
              section: '6.4.1.2',
              label: '6.4.1.2 - Water Quality',
              title: 'Water Quality',
              critical: false,
              pointValue: 2,
              description:
                'The WHIRLPOOL SPA water, including compensation tank, filter housing, and associated piping, must be changed every 72 hours, provided that the system is operated continuously and that the correct water chemistry levels are maintained during that period, including daily shock halogenation. SPA POOL water must be changed as often as necessary to maintain proper water chemistry. The water must be changed at least every 30 days.\nThe date and time of WHIRLPOOL SPA and SPA POOL water changes must be recorded in the log.',
            },
            {
              section: '6.4.2.1.1',
              label: '6.4.2.1.1 - Prolonged Maintenance',
              title: 'Prolonged Maintenance',
              critical: false,
              pointValue: 2,
              description:
                'For facilities undergoing maintenance for longer than 72 hours, the free HALOGEN residual and PH levels must be maintained, or the entire system must be drained completely of all water. This includes the WHIRLPOOL SPA and SPA POOL tubs, compensation tanks, filter housings, and all associated piping and blowers.\nRecords must be maintained for the free HALOGEN and PH levels or the complete draining of the system.',
            },
            {
              section: '6.4.2.1.2',
              label: '6.4.2.1.2 - Shock Halogenation',
              title: 'Shock Halogenation',
              critical: false,
              pointValue: 2,
              description:
                'The free HALOGEN residual must be increased to at least 10.0 MG/L (ppm) and circulated for at least 1 hour every 24 hours.\nThe free HALOGEN residual must be tested at both the start and completion of shock halogenation.\nThe water in the entire RWF system must be superhalogenated to 10 ppm to include the WHIRLPOOL SPA/SPA POOL tub, compensation tank, filter housing, and all associated piping before starting the 1-hour timing.\nHEATED ACTIVITY POOLS, including interactive RWFs, that have features that break the water surface of the RWF or create a mist must be shock halogenated as described. This includes ACTIVITY POOLS and INTERACTIVE RWFs that are HEATED by the sun and/or exterior temperature to a water temperature exceeding 32°C (90°F).\nBatch halogenation of the tub and compensation tank may help in reaching the minimum 10 ppm residual quickly.\nRWFs filled only with seawater are exempt from this requirement.',
            },
            {
              section: '6.4.2.1.3',
              label: '6.4.2.1.3 - Records',
              title: 'Records',
              critical: false,
              pointValue: 2,
              description:
                'A written or electronic record of the date and time of water dumping and shock halogenation (concentration in MG/L [ppm] at the start and completion and time) must be available for review during inspections.',
            },
            {
              section: '6.4.2.1.4',
              label: '6.4.2.1.4 - Retention',
              title: 'Retention',
              critical: false,
              pointValue: 2,
              description:
                'Records must be retained on the vessel for 12 months. 6.5. Maintenance and Operating Standards for Combined RWFs',
            },
            {
              section: '6.5.1',
              label: '6.5.1 - Pool with Attached Whirlpool Spa',
              title: 'Pool with Attached Whirlpool Spa',
              critical: false,
              pointValue: 2,
              description:
                'For any pool with an attached WHIRLPOOL SPA where the water, recirculation system EQUIPMENT, or filters are shared with the spa, all elements of the WHIRLPOOL SPA standards must apply to the pool.',
            },
            {
              section: '6.5.1.2',
              label: '6.5.1.2 - Fecal Accidents',
              title: 'Fecal Accidents',
              critical: false,
              pointValue: 2,
              description:
                'For combined RWFs subject to fecal accidents, fecal accident procedures must include all features of these combined RWFs.',
            },
            {
              section: '6.6.1',
              label: '6.6.1 - Draining, Cleaning, and Disinfection',
              title: 'Draining, Cleaning, and Disinfection',
              critical: false,
              pointValue: 2,
              description:
                'JETTED TUBS must be drained once per day. They must be cleaned and disinfected every 7 days or between occupancies, whatever is more frequent. DISINFECTION must be accomplished with an appropriate HALOGEN-based DISINFECTANT at 10 ppm for 60 minutes, or an equivalent CT VALUE.\nHEATED JETTED TUBS Option 1: HEATED JETTED TUBS must be drained, cleaned, and disinfected once per day. This option requires a safety sign at the tub entrance advising users that using HEATED JETTED TUBS without the use of automated halogen and PH control may increase the risk of respiratory infections. Pictograms may replace words, as appropriate or available.\nOption 2 (approved variance required): HEATED JETTED TUBS must be drained every 3 days. Tubs must be cleaned and disinfected twice a week or between occupancies, whatever is more frequent. No signage is required.\nVariance requirement for option 2: HEATED JETTED TUBS that are drained every 3 days require an approved variance for operation that includes the use of automated halogen and PH injection with recorded free halogen residuals and PH levels at least every 4 hours. Acceptable halogen levels are (3.0 to 10.0 free chlorine or 4.0 to 10.0 bromine). Acceptable PH levels are 7.0 to 7.8. The variance template can be found at www.cdc.gov/vessel-sanitation/php/communication- resources/index.html.',
            },
            {
              section: '6.6.2',
              label: '6.6.2 - Records',
              title: 'Records',
              critical: false,
              pointValue: 2,
              description:
                'Manufacturer’s operation and maintenance instructions must be available to personnel who service these JETTED TUBS and HEATED JETTED TUBS. Retain maintenance records on the vessel for 12 months. Records must include\n• Cleaning and DISINFECTION frequency.\n• DISINFECTANT type, concentration, and contact time.\n• Draining frequency (only for HEATED JETTED TUBS).',
            },
            {
              section: '6.7.1',
              label: '6.7.1 - Cleaning',
              title: 'Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'Individual hydrotherapy pools must be cleaned and disinfected, including associated recirculation systems, between customers. DISINFECTION must be accomplished with an appropriate HALOGEN-based DISINFECTANT at 10 ppm for 60 minutes, or an equivalent CT VALUE.',
            },
            {
              section: '6.7.2',
              label: '6.7.2 - Maintenance',
              title: 'Maintenance',
              critical: false,
              pointValue: 2,
              description:
                'Manufacturer’s operation and maintenance instructions must be available to personnel who service the units.',
            },
            {
              section: '6.7.3',
              label: '6.7.3 - Records',
              title: 'Records',
              critical: false,
              pointValue: 2,
              description:
                'A record must be maintained outlining the frequency of cleaning and DISINFECTION. The record must include the type, concentration, and contact time of the DISINFECTANT.\nRecords must be retained on the vessel for 12 months.',
            },
            {
              section: '6.8.1.1',
              label: '6.8.1.1 - RWF Safety Signs',
              title: 'RWF Safety Signs',
              critical: false,
              pointValue: 2,
              description:
                'Must be provided for all RWFs. Signs must be in a prominent and unobstructed location for all users of the RWF.\nThe signs at a minimum must include the following words:\n• Do not use these facilities if you are experiencing diarrhea, vomiting, or fever.\n• No children in diapers or who are not toilet trained.\n• Shower before entering the facility.\n• Bather load #.\nPictograms may replace words, as appropriate or available.\nFor children’s RWF safety signs, include the exact wording “TAKE CHILDREN ON FREQUENT BATHROOM BREAKS” or “TAKE CHILDREN ON FREQUENT TOILET BREAKS.”\nFor BABY-ONLY WATER FACILITY signs, refer to section 13.7.8.\nIt is advisable to post additional cautions and concerns on signs.\nRefer to section 6.3.1.5 for bather load calculations.',
            },
            {
              section: '6.8.1.2',
              label: '6.8.1.2 - Depth Markers',
              title: 'Depth Markers',
              critical: false,
              pointValue: 2,
              description:
                'The depth of each RWF deeper than 1 meter (3.3 feet) must be displayed prominently so it can be seen from the deck and in the pool. Depth markers must be labeled in both feet and meters. Additionally, depth markers must be installed for every 1-meter (3.3-feet) change in depth. For vessels constructed to the VSP 2025 Construction Standards or later, depth markers must have letters and numbers with a minimum height of 100 millimeters (4 inches) of a color contrasting with background.',
            },
            {
              section: '6.8.1.3',
              label: '6.8.1.3 - Spas',
              title: 'Spas',
              critical: false,
              pointValue: 2,
              description:
                'In addition to the safety sign requirements in section 6.8.1.2, install a sign at each WHIRLPOOL SPA and SPA POOL entrance listing precautions and risks associated with use of these facilities. At a minimum, include cautions against use by the following:\n• Individuals who are immunocompromised.\n• Individuals on medication or who have underlying medical conditions such as cardiovascular disease, diabetes, or high or low blood pressure.\n• Children, pregnant women, and elderly persons.\nAdditionally, caution against exceeding 15 minutes of use.\nVessels can submit existing signs for review by VSP.\nPosting additional cautions and concerns on signs is advisable.\nThose under 16 years of age are considered children for the purpose of whirlpool safety sign requirements.',
            },
            {
              section: '6.8.2.1',
              label: '6.8.2.1 - Life Saving',
              title: 'Life Saving',
              critical: false,
              pointValue: 2,
              description:
                'A rescue or shepherd’s hook and an APPROVED flotation device must be provided at a prominent location (visible from the full perimeter of the pool) at each RWF that has a depth of 1 meter (3.3 feet) or greater. These devices must be mounted in a manner that allows for easy access during an emergency.\n• The pole of the rescue or shepherd’s hook must be long enough to reach the center of the deepest portion of the pool from the side plus',
            },
            {
              section: '6.8.2.2',
              label: '6.8.2.2 - Antientrapment Covers',
              title: 'Antientrapment Covers',
              critical: false,
              pointValue: 2,
              description:
                'Requirements for ANTIENTRAPMENT COVERS, ANTIENTANGLEMENT COVERS, and SUCTION FITTINGS in RWFs are shown in Table 6.8.2.2. This does not apply to RWFs with zero depth where the drains are not under direct suction.\nTesting of manufactured drain covers must be done by a nationally or internationally recognized testing laboratory.\nEach manufactured ANTIENTRAPMENT COVER must be stamped with the following information. Alternatively, documentation from the manufacturer confirming compliance with these requirements must be provided.\n• Certification standard and year.\n• Type of drain use (single or multiple).\n• Maximum flow rate (in gallons or liters per minute) [or according to current Virginia Graeme Baker Act].\n• Type of fitting (suction outlet).\n• Life expectancy of cover.\n• Mounting orientation (wall, floor, or both).\n• Manufacturer’s name or trademark.\n• Model designation.\nVSP is aware that the requirements shown in Table 6.8.1.2.2 for existing vessels may not fully meet the letter of the Virginia Graeme Baker Act, but we also recognize the life-safety concerns for rapid dumping of RWFs in conditions of instability at sea.\nThe design of field fabricated ASME compliant drain covers, and SUCTION FITTINGS must be fully specified by a REGISTERED DESIGN PROFESSIONAL in accordance with ASME A112.19.8-2007. The specifications must fully address cover/grate loadings, durability, hair, finger and limb entrapment issues, cover/grate secondary layer of protection, related sump design, and features specific to the RWF.\nA letter from the shipyard must accompany each field fabricated ASME compliant drain and suction cover fitting. At a minimum the letter must specify the shipyard, name of the vessel, specifications and dimensions of the drain cover, as detailed above, as well as the exact location of the RWF for which it was designed. The name of and contact information for the REGISTERED DESIGN PROFESSIONAL and signature must be on the letter.\nTable 6.8.2.2 Antientrapment Requirements for RWFs Secondary Drainage/Recirculation Antientrapment Option* Type System Cover Design Requirement** 1 GRAVITY Multiple drains (two or Not ASME compliant - Alarm only more drains greater than manufactured or field 1 meter [3.3 feet] apart) fabricated 2 GRAVITY Multiple drains (two or ASME compliant - None only more drains greater than manufactured or field 1 meter [3.3 feet] apart) fabricated 3 GRAVITY Single UNBLOCKABLE drain Not ASME compliant - Alarm only (per ASME A112.19.8) manufactured or field fabricated 4 GRAVITY Single UNBLOCKABLE drain ASME compliant - None only (per ASME A112.19.8) manufactured or field fabricated 5 GRAVITY Single BLOCKABLE drain or ASME compliant - GDS only multiple drains (less than manufactured or field 1 meter [3.3 feet] apart) fabricated Secondary Drainage/Recirculation Antientrapment Option* Type System Cover Design Requirement** 6 SUCTION Multiple drains (two or ASME compliant - None FITTING more drains per pump with manufactured or field drains greater than 1 fabricated meter [3.3 feet] apart) 7 SUCTION Single UNBLOCKABLE drain ASME compliant - SVRS or APS FITTING (per ASME A112.19.8) manufactured or field fabricated 8 SUCTION Single BLOCKABLE drain or ASME compliant - SVRS or APS FITTING multiple drains (less than manufactured or field 1 meter [3.3 feet] apart) fabricated *Options 1 through 5 are for fittings not under direct suction. These include both fittings to drain the RWF and fittings used to recirculate the water. Options 6 through 8 are for fittings under direct suction. These include fittings to drain the RWF and fittings used to recirculate the water.\n**Notes:\n• Alarm: The audible alarm must sound in a continuously manned space AND at the RWF. This alarm is for all draining: accidental, routine, and emergency.\n• APS (automatic pump shut-off system): A manual shut-off near the RWF does not qualify as an APS.\n• GDS (GRAVITY DRAINAGE SYSTEM): There is no direct suction at the RWF.\n• SVRS (safety vacuum release system): System must be tested by an independent third party and found to conform with ASTM standard F2387 or ANSI/ASME A112.19.17.',
            },
            {
              section: '6.8.2.3',
              label: '6.8.2.3 - Temperature',
              title: 'Temperature',
              critical: false,
              pointValue: 2,
              description:
                'A temperature-control mechanism to prevent the temperature from exceeding 40°C (104°F) must be provided on WHIRLPOOL SPAS and SPA POOLS. The water temperature must be measured within the WHIRLPOOL SPA or SPA POOL tub itself.',
            },
            {
              section: '6.9.1',
              label: '6.9.1 - Temperature and Location',
              title: 'Temperature and Location',
              critical: false,
              pointValue: 2,
              description:
                'Showers must provide POTABLE WATER at a temperature not to exceed 43°C (110°F) during normal operations. Showers must be installed within 10 meters (33 feet) of every entry point to each RWF. For beach entry RWFs, a minimum of one showerhead must be installed per 10 meter (33 feet) of perimeter within 10 meters (33 feet) of the beach perimeter. A minimum of one shower must be installed at each water slide staircase entrance.',
            },
            {
              section: '6.9.2',
              label: '6.9.2 - Showers for Children',
              title: 'Showers for Children',
              critical: false,
              pointValue: 2,
              description:
                'RWFs designed for use by children under 6 years of age must have appropriately sized shower facilities. Standard height is acceptable, but the mechanism to operate the flow of water must not be more than 1 meter (3.3 feet) above the deck.',
            },
            {
              section: '6.9.3',
              label: '6.9.3 - Toilet Facilities',
              title: 'Toilet Facilities',
              critical: false,
              pointValue: 2,
              description:
                'Toilet facilities must be located within no more than 60 meters (197 feet) walking distance of each RWF and on the same deck or adjacent decks if there is no obstruction between RWF area and entrances to the toilets. If toilets are not located on the same deck, then they must be easily visible and nearby the RWF area. A minimum of two separate toilet rooms (either two unisex or one male and one female) must be installed. Each toilet facility must include a toilet and a handwashing station. Urinals may be installed in addition to the required toilet but may not replace the toilet.',
            },
            {
              section: '6.10.1',
              label: '6.10.1 - Diapers',
              title: 'Diapers',
              critical: false,
              pointValue: 2,
              description:
                'Children in diapers or who are not toilet trained must be prohibited from using any RWF not specifically designed and APPROVED for use by children in diapers.\nSpecifications and requirements for BABY-ONLY WATER FACILITIES can be found in Annex 13.7.',
            },
          ],
        },
      ],
    },
    {
      id: 'food-safety-personnel',
      name: 'Food Safety - Personnel',
      items: [
        {
          itemNo: '11',
          label: "11 - Food handler's infections, communicable diseases",
          pointValue: 5,
          critical: true,
          description: "Food handler's infections, communicable diseases",
          subItems: [
            {
              section: '4.4.2.1',
              label: '4.4.2.1 - Isolate Ill Passengers',
              title: 'Isolate Ill Passengers',
              critical: true,
              pointValue: 5,
              description:
                'Advise symptomatic passengers and those meeting the case definition to remain isolated in their cabins for a minimum of 24 hours after their symptoms resolve.\nFollow-up by infirmary personnel is advised.',
            },
            {
              section: '7.1.2.1.1',
              label: '7.1.2.1.1 - Communicable Diseases',
              title: 'Communicable Diseases',
              critical: true,
              pointValue: 5,
              description:
                'FOOD EMPLOYEES suspected of, diagnosed with, or exposed to any communicable disease caused by Salmonella typhi, Shigella spp., E. coli O157:H7, hepatitis A virus, norovirus, or other communicable diseases that can be transmitted by FOOD, must be excluded from working in any FOOD or FOOD-related areas or operations, including working with exposed FOOD, WAREWASHING, EQUIPMENT, UTENSILS, LINENS, SINGLE-SERVICE ARTICLES, and SINGLE-USE ARTICLES. The excluded individual must not be allowed to return to the above duties until they are symptom free for a minimum of 48 hours.',
            },
            {
              section: '7.1.2.1.2',
              label: '7.1.2.1.2 - Other Symptoms',
              title: 'Other Symptoms',
              critical: true,
              pointValue: 5,
              description:
                'FOOD EMPLOYEES who have conditions or symptoms of boils, open sores, infected wounds, diarrhea, jaundice, fever, vomiting, sore throat with fever, or discharges from the nose or mouth must report these conditions or symptoms to the vessel’s medical staff and must be restricted from working with exposed FOOD, WAREWASHING, clean EQUIPMENT, UTENSILS, LINENS, and unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.1.2.1.3',
              label: '7.1.2.1.3 - Sneeze/Cough',
              title: 'Sneeze/Cough',
              critical: true,
              pointValue: 5,
              description:
                'FOOD EMPLOYEES experiencing persistent sneezing, coughing, or a runny nose that causes discharges from the eyes, nose, or mouth must not work with exposed FOOD; WAREWASHING; clean EQUIPMENT, UTENSILS, and LINENS; or unwrapped SINGLE-SERVICE ARTICLES or SINGLE-USE ARTICLES.',
            },
            {
              section: '7.1.2.1.4',
              label: '7.1.2.1.4 - Restrictions Removal',
              title: 'Restrictions Removal',
              critical: true,
              pointValue: 5,
              description:
                'The restriction must not be removed until the supervisor or PERSON IN CHARGE of the FOOD operation obtains written approval from the vessel’s physician or equivalent medical staff.',
            },
          ],
        },
        {
          itemNo: '12',
          label: '12 - Hands washed; Hygienic practices',
          pointValue: 4,
          critical: true,
          description: 'Hands washed; Hygienic practices',
          subItems: [
            {
              section: '7.1.3.1.1',
              label: '7.1.3.1.1 - Hands and Arms Clean',
              title: 'Hands and Arms Clean',
              critical: true,
              pointValue: 4,
              description:
                'FOOD EMPLOYEES must keep their hands and exposed portions of their arms clean.',
            },
            {
              section: '7.1.3.1.2',
              label: '7.1.3.1.2 - Cleaning Procedures',
              title: 'Cleaning Procedures',
              critical: true,
              pointValue: 4,
              description:
                'FOOD EMPLOYEES must clean their hands and exposed portions of their arms with a cleaning compound in a handwashing sink by vigorously rubbing together the surfaces of their lathered hands and arms for at least 20 seconds and thoroughly rinsing with clean water. Employees must pay particular attention to the areas underneath the fingernails and between the fingers.',
            },
            {
              section: '7.1.3.1.3',
              label: '7.1.3.1.3 - When to Wash Hands',
              title: 'When to Wash Hands',
              critical: true,
              pointValue: 4,
              description:
                'FOOD EMPLOYEES must clean their hands and exposed portions of their arms immediately before engaging in FOOD preparation, including working with exposed FOOD, clean EQUIPMENT and UTENSILS, and unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES AND\n• After touching bare human body parts other than clean hands and clean, exposed portions of arms.\n• After using the toilet room.\n• After coughing, sneezing, using a handkerchief or disposable tissue, using tobacco, eating, or drinking.\n• After handling soiled EQUIPMENT or UTENSILS.\n• During FOOD preparation (as often as necessary to remove soil and CONTAMINATION and to prevent CROSS-CONTAMINATION when changing tasks).\n• When switching between working with raw FOOD and working with READY-TO-EAT FOOD.\n• Before putting on gloves for working with FOOD or clean EQUIPMENT and between glove changes.\n• After engaging in other activities that contaminate the hands.',
            },
            {
              section: '7.1.3.1.5',
              label: '7.1.3.1.5 - Apply to Clean Hands',
              title: 'Apply to Clean Hands',
              critical: true,
              pointValue: 4,
              description:
                'HAND ANTISEPTIC, HAND ANTISEPTIC used as a hand dip, or HAND ANTISEPTIC soap must only be applied to hands cleaned as described in section 7.1.3.1.2.',
            },
            {
              section: '7.1.4.1',
              label: '7.1.4.1 - Eating, Drinking, and Using Tobacco',
              title: 'Eating, Drinking, and Using Tobacco',
              critical: true,
              pointValue: 4,
              description:
                'An employee must not eat, drink, or use any form of tobacco in designated areas where the CONTAMINATION of exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS; unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES; or other items needing protection can occur.',
            },
            {
              section: '7.2.3.1.1',
              label: '7.2.3.1.1 - Wash Hands',
              title: 'Wash Hands',
              critical: true,
              pointValue: 4,
              description: 'FOOD EMPLOYEES must wash their hands.',
            },
            {
              section: '7.2.3.1.2',
              label: '7.2.3.1.2 - Ready-to-Eat Food – Hand Contact Prohibited',
              title: 'Ready-to-Eat Food – Hand Contact Prohibited',
              critical: true,
              pointValue: 4,
              description:
                'Except when washing fruits and vegetables or when otherwise APPROVED, FOOD EMPLOYEES must not contact exposed, READY-TO- EAT FOOD with their bare hands. They must use suitable UTENSILS such as deli tissue, spatulas, tongs, SINGLE-USE gloves, or dispensing EQUIPMENT.',
            },
            {
              section: '7.2.3.1.4',
              label: '7.2.3.1.4 - Tasting',
              title: 'Tasting',
              critical: true,
              pointValue: 4,
              description:
                'A FOOD EMPLOYEE must not use the same UTENSIL more than once to taste FOOD that will be served.',
            },
            {
              section: '10.3.1',
              label: '10.3.1 - Employee Handwashing',
              title: 'Employee Handwashing',
              critical: true,
              pointValue: 4,
              description:
                'Childcare providers must wash their hands before giving FOOD or BEVERAGES to children.',
            },
          ],
        },
        {
          itemNo: '13',
          label: '13 - Management, knowledge, monitoring',
          pointValue: 3,
          critical: true,
          description: 'Management, knowledge, monitoring',
          subItems: [
            {
              section: '7.1.1.1',
              label: '7.1.1.1 - Food Safety Knowledge',
              title: 'Food Safety Knowledge',
              critical: true,
              pointValue: 3,
              description:
                'Because of the risks for foodborne illness inherent to the FOOD operation, the supervisor or PERSON IN CHARGE of FOOD operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of foodborne disease prevention, application of the HAZARD ANALYSIS CRITICAL CONTROL POINT (HACCP) principles, and FOOD safety standards. The supervisor or PERSON IN CHARGE must demonstrate this knowledge\n• By compliance with these standards,\n• By being a domestically or foreign certified FOOD protection manager who has shown proficiency of required information through passing a test that is part of an ACCREDITED PROGRAM, or\n• By responding correctly to the inspector’s questions as they relate to the specific FOOD operation.\nThe areas of knowledge must include\n• Describing the relation between prevention of foodborne disease and personal hygiene of a FOOD EMPLOYEE.\n• Explaining the responsibility of the supervisor or PERSON IN CHARGE to prevent TRANSMISSION of foodborne disease by a FOOD EMPLOYEE who has a disease or medical condition that may cause foodborne disease.\n• Describing the symptoms associated with diseases transmissible through FOOD.\n• Explaining the significance of the relation between maintaining the time and temperature of TCS/PHF and the prevention of foodborne illness.\n• Explaining the HAZARDS involved in the consumption of raw or undercooked MEAT, POULTRY, eggs, and FISH.\n• Stating the required FOOD temperatures and times for safe cooking of TCS/PHF, including MEAT, POULTRY, eggs, and FISH.\n• Stating the required temperatures and times for the safe refrigerated storage, hot holding, cooling, and reheating of TCS/PHF.\n• Describing the relation between prevention of foodborne illness and management and control of the following: CROSS-CONTAMINATION, hand contact with READY-TO-EAT FOOD, handwashing, and maintaining FOOD operations in a clean condition and in good repair.\n• Explaining the relation between FOOD safety and providing EQUIPMENT that is sufficient in number and capacity as well as properly designed, constructed, located, installed, operated, maintained, and cleaned.\n• Explaining correct procedures for cleaning and sanitizing UTENSILS and FOOD-CONTACT SURFACES of EQUIPMENT.\n• Identifying POISONOUS OR TOXIC MATERIALS on the vessel and the procedures necessary to ensure they are safely stored, dispensed, used, and disposed of according to LAW.\n• Identifying critical-control points in the operation from purchasing through service that when not controlled may contribute to the TRANSMISSION of foodborne illness and explaining steps taken to ensure the points are controlled in accordance with these standards.',
            },
            {
              section: '7.1.1.2',
              label: '7.1.1.2 - Food Safety Monitoring Duties',
              title: 'Food Safety Monitoring Duties',
              critical: true,
              pointValue: 3,
              description:
                'The supervisor or PERSON IN CHARGE of FOOD operations on the vessel must ensure that\n• FOOD operations are not conducted in a room used as living or sleeping quarters.\n• Persons unnecessary to the FOOD operation are not allowed in the FOOD PREPARATION, FOOD STORAGE, or WAREWASHING areas. However, brief visits and tours may be authorized if steps are taken to ensure that exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS; and unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES are protected from CONTAMINATION.\n• Employees and other persons such as delivery and maintenance persons and PESTICIDE applicators entering the FOOD PREPARATION, FOOD STORAGE, and WAREWASHING areas comply with these standards.\n• FOOD EMPLOYEES are effectively cleaning their hands (by routinely monitoring the employees’ handwashing).\n• Employees are observing FOOD as they are received to determine that they are from APPROVED sources, delivered at the required temperatures, protected from CONTAMINATION, unadulterated, and accurately presented (by routinely monitoring the employees’ observations and periodically evaluating FOOD as they are received).\n• Employees are properly cooking TCS/PHF, being particularly careful in cooking FOOD known to cause severe foodborne illness and death, such as eggs and COMMINUTED MEATS (through daily oversight of the employees’ routine monitoring of the cooking temperatures using appropriate properly scaled and calibrated temperature measuring devices [TMDs]).\n• Employees are using proper methods to rapidly cool TCS/PHF that is not held hot or are not for consumption within 4 hours (through daily oversight of the employees’ routine monitoring of FOOD temperatures during cooling).\n• CONSUMERS who order raw or partially cooked READY-TO-EAT FOOD of animal origin are informed that the FOOD is not cooked sufficiently to ensure its safety.\n• Employees are properly sanitizing cleaned multiuse EQUIPMENT and UTENSILS before they are reused (through routine monitoring of solution temperature and exposure time for hot water sanitizing, and chemical concentration, PH, temperature, and exposure time for chemical sanitizing).\n• CONSUMERS are notified that clean TABLEWARE is to be used when they return to self-service areas such as salad bars and buffets.\n• Employees are preventing CROSS-CONTAMINATION of READY-TO-EAT FOOD with bare hands by properly using suitable UTENSILS such as deli tissue, spatulas, tongs, SINGLE-USE gloves, or dispensing EQUIPMENT.\n• Employees are properly trained in FOOD safety, including FOOD allergy awareness, as it relates to their assigned duties.\n• FOOD EMPLOYEES are informed of their responsibility to report to the supervisor or PERSON IN CHARGE information about their health and activities as they relate to diseases transmissible through FOOD.',
            },
          ],
        },
        {
          itemNo: '14',
          label: '14 - Outer clothing clean; Jewelry, Hair, Hand Sanitizers',
          pointValue: 1,
          critical: false,
          description: 'Outer clothing clean; Jewelry, Hair, Hand Sanitizers',
          subItems: [
            {
              section: '7.1.3.1.4',
              label: '7.1.3.1.4 - Hand Antiseptic',
              title: 'Hand Antiseptic',
              critical: false,
              pointValue: 1,
              description:
                'A HAND ANTISEPTIC, a HAND ANTISEPTIC used as a hand dip, or a HAND ANTISEPTIC soap must comply with applicable formulation and use LAWS under FDA 21 CFR 170.39, 178, 182, 184, or 186.',
            },
            {
              section: '7.1.3.2',
              label: '7.1.3.2 - Fingernails',
              title: 'Fingernails',
              critical: false,
              pointValue: 1,
              description:
                'FOOD EMPLOYEES must keep their fingernails trimmed, filed, and maintained so the edges and surfaces are cleanable and not rough. A FOOD EMPLOYEE must not wear fingernail polish or artificial fingernails when preparing exposed FOOD.',
            },
            {
              section: '7.1.3.3',
              label: '7.1.3.3 - Jewelry',
              title: 'Jewelry',
              critical: false,
              pointValue: 1,
              description:
                'While preparing FOOD, FOOD EMPLOYEES, including bartenders, must not wear jewelry on their arms and hands. FOOD EMPLOYEES may wear a plain ring such as a SMOOTH simple wedding band.',
            },
            {
              section: '7.1.3.4',
              label: '7.1.3.4 - Food Service Uniform or Apron',
              title: 'Food Service Uniform or Apron',
              critical: false,
              pointValue: 1,
              description:
                'FOOD EMPLOYEES must wear a clean uniform or apron to prevent CONTAMINATION of FOOD, EQUIPMENT, UTENSILS, LINENS, and SINGLE- SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.1.4.2',
              label: '7.1.4.2 - Hair Restraints',
              title: 'Hair Restraints',
              critical: false,
              pointValue: 1,
              description:
                'FOOD EMPLOYEES must wear hair restraints—such as hats, hair coverings or nets, beard restraints—and clothing that covers body hair. These items must be designed and worn to effectively keep their hair from contacting exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS; and unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.\nThis applies to partial beards (such as goatees) and to heavy, pronounced mustaches.\nThis section does not apply to FOOD EMPLOYEES such as counter staff who serve only BEVERAGES and wrapped or PACKAGED FOOD, hostesses, and waitstaff if they present a minimal risk for contaminating exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS; and unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.1.4.3',
              label: '7.1.4.3 - Toilet Rooms Use',
              title: 'Toilet Rooms Use',
              critical: false,
              pointValue: 1,
              description:
                'While in working uniforms, FOOD EMPLOYEES must not use toilet rooms designated for passenger use.\nThis section does not apply to FOOD EMPLOYEES at bars where the only FOOD preparation includes garnish slicing in the pantry, and drink making in the pantry and bar.',
            },
          ],
        },
      ],
    },
    {
      id: 'food-safety-food',
      name: 'Food Safety - Food',
      items: [
        {
          itemNo: '15',
          label: '15 - Food source, sound condition; Food re-service',
          pointValue: 5,
          critical: true,
          description: 'Food source, sound condition; Food re-service',
          subItems: [
            {
              section: '7.2.1.1',
              label: '7.2.1.1 - Safe and Unadulterated',
              title: 'Safe and Unadulterated',
              critical: true,
              pointValue: 5,
              description: 'FOOD must be safe and unadulterated.',
            },
            {
              section: '7.2.2.1.1',
              label: '7.2.2.1.1 - Comply with Law',
              title: 'Comply with Law',
              critical: true,
              pointValue: 5,
              description:
                'FOOD must be obtained from sources that comply with applicable local, state, federal, or country of origin’s statutes, regulations, and ordinances.',
            },
            {
              section: '7.2.2.1.2',
              label: '7.2.2.1.2 - Food from Private Home',
              title: 'Food from Private Home',
              critical: true,
              pointValue: 5,
              description:
                'FOOD prepared in a private home must not be used or offered for human consumption on a vessel.',
            },
            {
              section: '7.2.2.1.4',
              label: '7.2.2.1.4 - Steaks',
              title: 'Steaks',
              critical: true,
              pointValue: 5,
              description:
                'WHOLE-MUSCLE, INTACT BEEF steaks intended for consumption in an undercooked form without a CONSUMER advisory must be\n• Obtained from a FOOD-PROCESSING PLANT that packages the steaks and labels them to indicate they meet the definition of WHOLE-MUSCLE, INTACT BEEF; or\n• Cut individually on a vessel from WHOLE-MUSCLE, INTACT BEEF labeled by a FOOD-PROCESSING PLANT to indicate the beef meets the definition of WHOLE-MUSCLE, INTACT BEEF, and prepared so they remain intact.',
            },
            {
              section: '7.2.2.1.5',
              label: '7.2.2.1.5 - Hermetically Sealed Container',
              title: 'Hermetically Sealed Container',
              critical: true,
              pointValue: 5,
              description:
                'FOOD in a HERMETICALLY SEALED CONTAINER must be obtained from a FOOD-PROCESSING PLANT regulated by the FOOD regulatory agency that has jurisdiction over the plant.',
            },
            {
              section: '7.2.2.1.6',
              label: '7.2.2.1.6 - Milk',
              title: 'Milk',
              critical: true,
              pointValue: 5,
              description:
                'Fluid milk and milk products by U.S. suppliers must be obtained from sources that comply with GRADE A STANDARDS as specified in LAW. Milk received in interstate commerce must be from sources listed in the FDA Interstate Milk Shippers List. Non-U.S. sourced fluid milk and milk products must be obtained from sources that meet or exceed the standards of the health authorities from the source country.',
            },
            {
              section: '7.2.2.1.7',
              label: '7.2.2.1.7 - Fish and Molluscan Shellfish Sources',
              title: 'Fish and Molluscan Shellfish Sources',
              critical: true,
              pointValue: 5,
              description:
                'FISH received for service must be commercially and legally caught or harvested or otherwise APPROVED for service by VSP through an APPROVED VARIANCE.\nRecreationally caught MOLLUSCAN SHELLFISH must not be received for service.\nMOLLUSCAN SHELLFISH must be obtained from sources according to LAW and the requirements specified in the FDA National Shellfish Sanitation Program Guide for the Control of MOLLUSCAN SHELLFISH, or equivalent standards. MOLLUSCAN SHELLFISH received in interstate commerce must be from sources listed in the FDA Interstate Certified Shellfish Shippers List or equivalent foreign certified shellfish listing.',
            },
            {
              section: '7.2.2.1.8',
              label: '7.2.2.1.8 - Wild Mushrooms',
              title: 'Wild Mushrooms',
              critical: true,
              pointValue: 5,
              description:
                'Mushroom species picked in the wild must be obtained from sources where each mushroom is individually inspected and found to be safe by an APPROVED mushroom identification expert.\nThis section does not apply to\n• Cultivated wild mushroom species grown, harvested, and processed in an operation regulated by the FOOD regulatory agency that has jurisdiction over the operation.\n• Wild mushroom species if they are in PACKAGED form and are the product of a FOOD-PROCESSING PLANT regulated by the FOOD regulatory agency that has jurisdiction over the plant.',
            },
            {
              section: '7.2.2.1.9',
              label: '7.2.2.1.9 - Game Animals',
              title: 'Game Animals',
              critical: true,
              pointValue: 5,
              description:
                'If GAME ANIMALS are received for sale or service, they must be\n• Commercially raised for FOOD and raised, slaughtered, and processed under LAW; or\n• Under a voluntary inspection program administered by USDA for GAME ANIMALS such as exotic animals (reindeer, elk, deer, antelope, water buffalo, or bison) inspected and APPROVED in accordance with 9 CFR 352 (Voluntary Exotic Animal Program) or rabbits inspected and certified in accordance with 9 CFR 354 (Rabbit Inspection Program); or\n• Under an equivalent regulatory program specifically for GAME ANIMALS. The packaging must also be stamped/labeled by the REGULATORY AUTHORITY.\nA GAME ANIMAL must not be received for service if it is a species of wildlife listed in 50 CFR 17 Endangered and Threatened Wildlife and Plants.',
            },
            {
              section: '7.2.2.2.2',
              label: '7.2.2.2.2 - Food Additives',
              title: 'Food Additives',
              critical: true,
              pointValue: 5,
              description:
                'FOOD must not contain unapproved FOOD ADDITIVES or ADDITIVES that exceed amounts specified in LAW, as specified in the current version of the FDA FOOD Code, including annexes.',
            },
            {
              section: '7.2.2.2.3',
              label: '7.2.2.2.3 - Shell Eggs',
              title: 'Shell Eggs',
              critical: true,
              pointValue: 5,
              description:
                'Shell eggs must be received clean and sound and must not exceed the restricted egg tolerances specified in LAW, as specified in the current version of the FDA FOOD Code, including annexes.',
            },
            {
              section: '7.2.2.2.4',
              label: '7.2.2.2.4 - Egg and Milk Products, Pasteurized',
              title: 'Egg and Milk Products, Pasteurized',
              critical: true,
              pointValue: 5,
              description:
                'Eggs and milk products must be received as follows:\n• Liquid, frozen, and dry eggs and egg products must be obtained pasteurized.\n• Fluid and dry milk and milk products complying with GRADE A STANDARDS as specified in LAW must be obtained pasteurized.\n• Frozen milk products, such as ice cream, must be obtained pasteurized as specified in 21 CFR 135 Frozen Desserts.\n• Cheese must be obtained pasteurized unless alternative procedures to pasteurization are specified in the CFR, such as 21 CFR 133 Cheeses and Related Cheese Products for curing certain cheese varieties.',
            },
            {
              section: '7.2.2.2.5',
              label: '7.2.2.2.5 - Package Integrity',
              title: 'Package Integrity',
              critical: true,
              pointValue: 5,
              description:
                'FOOD packages must be in good condition and protect the integrity of the contents, so the FOOD is not exposed to adulteration or potential contaminants. Canned goods with dents on end or side SEAMS must not be used.',
            },
            {
              section: '7.2.2.2.6',
              label: '7.2.2.2.6 - Ice',
              title: 'Ice',
              critical: true,
              pointValue: 5,
              description:
                'Ice for use as a FOOD or a cooling medium must be made from DRINKING WATER.',
            },
            {
              section: '7.2.2.2.7',
              label: '7.2.2.2.7 - Shucked Shellfish',
              title: 'Shucked Shellfish',
              critical: true,
              pointValue: 5,
              description:
                'Raw SHUCKED SHELLFISH must be obtained in nonreturnable packages that bear a legible label as specified in the FDA National Shellfish Sanitation Program Guide for the Control of MOLLUSCAN SHELLFISH.',
            },
            {
              section: '7.2.2.2.8',
              label: '7.2.2.2.8 - Shellstock Shellfish',
              title: 'Shellstock Shellfish',
              critical: true,
              pointValue: 5,
              description:
                'SHELLSTOCK must be obtained in containers bearing legible source identification tags or labels affixed by the harvester and by each dealer that depurates (cleanses), ships, or reships the SHELLSTOCK, as specified in the National Shellfish Sanitation Program Guide for the Control of MOLLUSCAN SHELLFISH.',
            },
            {
              section: '7.2.2.3.1',
              label: '7.2.2.3.1 - Shucked Identification',
              title: 'Shucked Identification',
              critical: true,
              pointValue: 5,
              description:
                'Shucked MOLLUSCAN SHELLFISH must not be removed from the container in which they are received other than immediately before preparation for service.',
            },
            {
              section: '7.2.2.3.2',
              label: '7.2.2.3.2 - Shellstock Identification',
              title: 'Shellstock Identification',
              critical: true,
              pointValue: 5,
              description:
                'SHELLSTOCK shellfish tags must\n• Remain attached to the container in which the SHELLSTOCK are received until the container is empty.\n• Be maintained by retaining SHELLSTOCK tags or labels for 90 calendar days from the date the container is emptied by using an APPROVED record-keeping system that keeps the tags or labels in chronologic order correlated to the date when the SHELLSTOCK are served. The date when the last SHELLSTOCK from the container is served must be recorded on the tag, label, or invoice.',
            },
            {
              section: '7.2.3.6.5',
              label: '7.2.3.6.5 - Food Reservice',
              title: 'Food Reservice',
              critical: true,
              pointValue: 5,
              description:
                'After being served and in the possession of a CONSUMER or being placed on a buffet service line, FOOD that is unused or returned by the CONSUMER must not be offered as FOOD for human consumption.\nExceptions:\n• A container of FOOD that is not TCS/PHF may be transferred from one CONSUMER to another if the FOOD is dispensed so that it is protected from CONTAMINATION and the container is closed between uses (such as a narrow-neck bottle containing catsup, steak sauce, or wine) OR the FOOD (such as crackers, salt, or pepper) is in an unopened original package and is maintained in sound condition.\n• Reservice for FOOD served to passengers from a fully enclosed display case and under strict employee monitoring, strict temperature control of hot and cold TCS/PHF, proper cooling and reheating of hot-held TCS/PHF, and complete protection from any other CONTAMINATION sources, including pests.',
            },
          ],
        },
        {
          itemNo: '16',
          label: '16 - Potentially hazardous food temperatures',
          pointValue: 5,
          critical: true,
          description: 'Potentially hazardous food temperatures',
          subItems: [
            {
              section: '7.2.2.2.1',
              label: '7.2.2.2.1 - Receiving Temperatures',
              title: 'Receiving Temperatures',
              critical: true,
              pointValue: 5,
              description:
                'Receiving temperatures must be as follows:\n• Refrigerated TCS/PHF must be at a temperature of 5°C (41°F) or below when received. If a temperature other than 5°C (41°F) for a TCS/PHF is specified in LAW governing its distribution, such as LAWS governing milk, MOLLUSCAN SHELLFISH, and shell eggs, the FOOD may be received at the specified temperature. For example, shell eggs can be received at 7°C (45°F) or less.\n• TCS/PHF that is cooked and received hot must be at a temperature of 57°C (135°F) or above.\n• A FOOD labeled and shipped frozen by a FOOD-PROCESSING PLANT must be received frozen.\n• Upon receipt, TCS/PHF must be free of evidence of previous temperature abuse.',
            },
            {
              section: '7.2.4.1.1',
              label: '7.2.4.1.1 - Cooking',
              title: 'Cooking',
              critical: true,
              pointValue: 5,
              description:
                'Raw animal FOOD such as eggs, FISH, MEAT, and POULTRY—and FOOD containing these raw animal FOOD—must be cooked to heat all parts of the FOOD to a temperature and for a time that complies with one of the following methods based on the FOOD being cooked:\n• 63°C (145°F) or above for 15 seconds for o Raw shell eggs broken and prepared in response to CONSUMERS’ orders and for immediate service. o FISH, MEAT, and pork, including GAME ANIMALS commercially raised for FOOD and GAME ANIMALS under a voluntary inspection program.\n• 68°C (155°F) or above for 15 seconds or equivalent temperature-time combination for o RATITES, MECHANICALLY TENDERIZED and INJECTED MEATS, and raw eggs not prepared for immediate service. o COMMINUTED FISH, MEAT, GAME ANIMALS commercially raised for FOOD, and GAME ANIMALS under a voluntary inspection program.\n• 74°C (165°F) or above for 15 seconds for o POULTRY. o Wild GAME ANIMALS not specified in above bullet for 68°C (155°F). o Stuffed FISH, MEAT, pasta, POULTRY, or RATITES. o Stuffing containing FISH, MEAT, POULTRY, or RATITES.\n• Whole beef roasts, corned beef roasts, pork roasts, and cured pork roasts such as ham, must be cooked as detailed in sections a and b below.\na. FOOD cooked in an oven preheated to the temperature specified below for the roast’s weight and oven type AND Roast Weight Less Roast Weight Than 4.5 Kilograms 4.5 Kilograms Oven Type (10 Pounds) (10 Pounds) or More Still dry Preheat to 177°C Preheat to 121°C (350°F) or more (250°F) or more Convection Preheat to 163°C Preheat to 121°C (325°F) or more (250°F) or more High Preheat to 121°C Preheat to 121°C humidity* (250°F) (250°F)\n*Relative humidity greater than 90% for at least 1 hour as measured in the cooking chamber or exit of the oven or in a moisture-impermeable bag that provides 100% humidity.\nb. All parts of the FOOD heated to a temperature and for the holding time corresponding to that temperature: Temperature Time* 54.4°C (130°F) 112 minutes 55.0°C (131°F) 89 minutes 56.1°C (133°F) 56 minutes 57.2°C (135°F) 36 minutes 57.8°C (136°F) 28 minutes 58.9°C (138°F) 18 minutes 60.0°C (140°F) 12 minutes 61.1°C (142°F) 8 minutes Temperature Time* 62.2°C (144°F) 5 minutes 62.8°C (145°F) 4 minutes 63.9°C (147°F) 134 seconds 65.0°C (149°F) 85 seconds 66.1°C (151°F) 54 seconds 67.2°C (153°F) 34 seconds 68.3°C (155°F) 22 seconds 69.4°C (157°F) 14 seconds 70.0°C (158°F) 0 seconds\n*Holding time may include post oven heat rise.\n• A raw or undercooked WHOLE-MUSCLE, INTACT BEEF steak may be served or offered for sale in a READY-TO-EAT form if the steak is labeled to indicate that it meets the definition of “WHOLE-MUSCLE, INTACT BEEF” and the steak is cooked on both the top and bottom to a surface temperature of 63°C (145°F) or above and a cooked color change is achieved on all external surfaces.\n• A raw animal FOOD (such as raw FISH, raw-marinated FISH, raw MOLLUSCAN SHELLFISH, or steak tartare) or a partially cooked FOOD (such as lightly cooked FISH, soft-cooked eggs, or rare MEAT other than WHOLE-MUSCLE, INTACT BEEF steaks) may be served or offered for sale in a READY-TO-EAT form if the CONSUMER is informed by the written CONSUMER advisory or if VSP grants a VARIANCE from the cooking recommendations based on a HACCP PLAN submitted by the vessel. The HACCP PLAN must document scientific data or other information showing that a lesser time and temperature regimen results in a safe FOOD. The HACCP PLAN must verify that EQUIPMENT and procedures for FOOD preparation and training of FOOD EMPLOYEES meet the conditions of the VARIANCE.',
            },
            {
              section: '7.2.4.1.2',
              label: '7.2.4.1.2 - Microwave',
              title: 'Microwave',
              critical: true,
              pointValue: 5,
              description:
                'Raw animal FOOD cooked in a microwave oven must be\n• Rotated or stirred throughout or midway during cooking to compensate for uneven distribution of heat.\n• Covered to retain surface moisture.\n• Heated to a temperature of at least 74°C (165°F) in all parts of the FOOD.\n• Allowed to stand covered for 2 minutes after cooking to obtain temperature equilibrium.',
            },
            {
              section: '7.2.4.2.1',
              label: '7.2.4.2.1 - Parasite Destruction',
              title: 'Parasite Destruction',
              critical: true,
              pointValue: 5,
              description:
                'Before service in READY-TO-EAT form, raw, raw-marinated, partially cooked, or marinated-partially cooked FISH and fishery products other than MOLLUSCAN SHELLFISH must be frozen throughout according to one of these methods:\n• Frozen and stored at a temperature of -20°C (-4°F) or below for a minimum of 168 hours (7 days) in a freezer.\n• Frozen at -35°C (-31°F) or below until solid and stored at -35°C (-31°F) or below for a minimum of 15 hours.\n• Frozen at -35°C (-31°F) or below until solid and stored at -20°C (-4°F) or below for a minimum of 24 hours.\nThis does not apply to\n• Molluscan shellfish.\n• A scallop product consisting only of the shucked adductor muscle.\n• Tuna of the species Thunnus alalunga, Thunnus albacares (yellowfin tuna), Thunnus atlanticus, Thunnus maccoyii (southern bluefin tuna), Thunnus obesus (bigeye tuna), or Thunnus thynnus (northern bluefin tuna).\n• Aquacultured fish, such as salmon, that\n– Are raised in net pens (in open water) or in ponds, tanks, or other land-based operations.\n– Are fed formulated feed, such as pellets, that contains no live parasites infective to the aquacultured FISH.\n• FISH eggs that have been removed from the skein and rinsed.',
            },
            {
              section: '7.2.4.3.2',
              label: '7.2.4.3.2 - Reheating for Hot Holding',
              title: 'Reheating for Hot Holding',
              critical: true,
              pointValue: 5,
              description:
                'TCS/PHF that is cooked, cooled, and reheated for hot holding must be reheated so that all parts of the FOOD reach a temperature of at least 74°C (165°F) for 15 seconds.',
            },
            {
              section: '7.2.4.3.3',
              label: '7.2.4.3.3 - Microwave Heating',
              title: 'Microwave Heating',
              critical: true,
              pointValue: 5,
              description:
                'If reheated in a microwave oven for hot holding, TCS/PHF must be reheated so that all parts of the FOOD reach a temperature of at least 74°C (165°F) and the FOOD is rotated or stirred, covered, and allowed to stand covered for 2 minutes after reheating.',
            },
            {
              section: '7.2.4.3.4',
              label: '7.2.4.3.4 - Commercial Products',
              title: 'Commercial Products',
              critical: true,
              pointValue: 5,
              description:
                "READY-TO-EAT FOOD and TCS/PHF taken from a commercially processed, HERMETICALLY SEALED CONTAINER, or from an intact package from a FOOD-PROCESSING PLANT inspected by the FOOD REGULATORY AUTHORITY that has jurisdiction over the plant, must be heated to a temperature of at least 57°C (135°F) for hot holding.\nCommercially PACKAGED FOOD must be cooked to heat all parts of the FOOD to a temperature at or above the manufacturer's instructions printed on the packaging.\nCommercially PACKAGED FOOD for which the manufacturer has provided information that it has not been processed to control pathogens must be cooked according to a time and temperature appropriate for the FOOD according to section 7.2.4.1.1.",
            },
            {
              section: '7.2.4.3.5',
              label: '7.2.4.3.5 - Rapid Reheat',
              title: 'Rapid Reheat',
              critical: true,
              pointValue: 5,
              description:
                'Reheating for hot holding must be done rapidly. The time the FOOD is between 5°C (41°F) and 74°C (165°F) must not exceed 2 hours.',
            },
            {
              section: '7.2.5.2.1',
              label: '7.2.5.2.1 - Cooling Times/Temperatures',
              title: 'Cooling Times/Temperatures',
              critical: true,
              pointValue: 5,
              description:
                'Cooked TCS/PHF must be cooled\n• From 57°C (135°F) to 21°C (70°F) within 2 hours and\n• From 21°C (70°F) to 5°C (41°F) or less within 4 hours.',
            },
            {
              section: '7.2.5.2.2',
              label: '7.2.5.2.2 - Cooling Prepared Food',
              title: 'Cooling Prepared Food',
              critical: true,
              pointValue: 5,
              description:
                'TCS/PHF must be cooled within 4 hours to 5°C (41°F) or less if prepared from ingredients at ambient temperature (such as reconstituted FOOD and canned tuna).',
            },
            {
              section: '7.2.5.2.3',
              label: '7.2.5.2.3 - Cooling Received Food',
              title: 'Cooling Received Food',
              critical: true,
              pointValue: 5,
              description:
                'A TCS/PHF received in compliance with LAWS allowing a temperature above 5°C (41°F) during shipment from the supplier, such as milk and MOLLUSCAN SHELLFISH, must be cooled within 4 hours to 5°C (41°F) or less. This does not apply to shell eggs, which can be held at 7°C (45°F) or less.',
            },
            {
              section: '7.2.5.3.1',
              label: '7.2.5.3.1 - Holding Temperature/Time',
              title: 'Holding Temperature/Time',
              critical: true,
              pointValue: 5,
              description:
                'Except during preparation, cooking, or cooling, or when time is used as the public health control, TCS/PHF must be maintained at\n• 57°C (135°F) or above, except that roasts may be held at a temperature of 54°C (130°F); or\n• 5°C (41°F) or less.\nShell eggs that have not been treated to destroy all viable Salmonella spp. must be maintained at 7°C (45°F) or less.',
            },
            {
              section: '7.2.5.3.2',
              label:
                '7.2.5.3.2 - Ready-to-Eat TCS/PHF Shelf-Life: Date Marking',
              title: 'Ready-to-Eat TCS/PHF Shelf-Life: Date Marking',
              critical: true,
              pointValue: 5,
              description:
                'Refrigerated, READY-TO-EAT FOOD, TCS/PHF\n• Prepared on a vessel and held refrigerated for more than 24 hours must be clearly marked at the time of preparation to indicate the date or day by which the FOOD must be consumed (7 calendar days or fewer from the day the FOOD is prepared). The day of preparation is counted as day 1.\n• Prepared and PACKAGED by a FOOD-PROCESSING PLANT and held on the vessel after opening for more than 24 hours must be clearly marked at the time the original container is opened to indicate the date by which the FOOD must be consumed (7 calendar days or fewer after the original container is opened). The day of package opening is counted as day 1.\nThe date marking requirement can be accomplished with a calendar date, day of the week, color-code, or other system, provided it is effective.\nThe date marking requirement does not apply to the following FOOD prepared and PACKAGED by a FOOD-PROCESSING PLANT inspected by a REGULATORY AUTHORITY:\n• Deli salads (such as ham salad, seafood salad, chicken salad, egg salad, pasta salad, potato salad, and macaroni salad) manufactured in accordance with 21 CFR 117.\n• Hard cheeses containing not more than 39% moisture as defined in 21 CFR 133 (such as cheddar, gruyere, parmesan and Reggiano, and Romano). Additional examples can be found in Annex 13.13.\n• Semisoft cheeses containing more than 39% moisture but not more than 50% moisture, as defined in 21 CFR 133 (such as blue, edam, gorgonzola, gouda, and monterey jack).\n• Cultured dairy products as defined in 21 CFR 131 (such as yogurt, sour cream, and buttermilk).\n• Preserved FISH products (such as pickled herring and dried or salted cod) and other acidified FISH products defined in 21 CFR 114.\n• Shelf stable, dry fermented sausages (such as pepperoni and Genoa salami) not labeled “keep refrigerated” as specified in 9 CFR 317. [Retain the original casing on the product.]\n• Shelf stable salt-cured products (such as prosciutto and Parma [ham]) not labeled “keep refrigerated” as specified in 9 CFR 317.\nThese products are exempted from date marking even after being opened, cut, shredded, etc.',
            },
            {
              section: '7.2.5.3.3',
              label: '7.2.5.3.3 - Discarding Ready-to-Eat TCS/PHF',
              title: 'Discarding Ready-to-Eat TCS/PHF',
              critical: true,
              pointValue: 5,
              description:
                'Refrigerated, READY-TO-EAT TCS/PHF must be discarded if not consumed within 7 calendar days from the date of preparation or opening.',
            },
            {
              section: '7.2.5.3.4',
              label: '7.2.5.3.4 - Retain Date',
              title: 'Retain Date',
              critical: true,
              pointValue: 5,
              description:
                'A refrigerated READY-TO-EAT TCS/PHF ingredient or a portion of a refrigerated READY-TO-EAT TCS/PHF subsequently combined with additional ingredients or portions of FOOD must retain the date marking of the earliest or first-prepared ingredient.',
            },
            {
              section: '7.2.5.3.5',
              label: '7.2.5.3.5 - Time as a Public Health Control',
              title: 'Time as a Public Health Control',
              critical: true,
              pointValue: 5,
              description:
                'Refer to Annex 13.10 for food cooking temperature alternatives.\nTime without temperature control can be used as a public health control for TCS/PHF before cooking or for READY-TO-EAT TCS/PHF that is displayed or held for immediate consumption.\nTime—maximum up to 4 hours If time without temperature control is used as the public health control with a maximum of 4 hours, then the FOOD\n• Must have an initial temperature of 5°C (41°F) or less or 57°C (135°F) or greater before placement on time control. Note: FOOD may have an initial temperature of 21°C (70°F) or less before placement on time control if o It is a READY-TO-EAT fruit or vegetable that becomes a TCS/PHF after cutting OR it is a READY-TO-EAT hermetically sealed FOOD that becomes a TCS/PHF after opening. AND o The FOOD temperature does not exceed 21°C (70°F) within 4 hours from the time it became a TCS/PHF. o The FOOD is marked or otherwise identified to indicate 4 hours past the time it became a TCS/PHF (regardless of whether the time between service setup and closing exceeds 4 hours).\n• Must not be placed on temperature control again.\n• Must be marked or otherwise identified to indicate the time 4 hours past the time when the FOOD is removed from temperature control IF the time between service set-up and closing is greater than 4 hours, o If FOOD is on/in a time control unit (bains-marie, cold basins, soup wells) and service is under 4 hours, a 4- hour discard label is not needed. o If the time control unit meant to be cold holding or hot holding is not operational or used as intended, the unit is considered a counter, and the FOOD stored within it must be labeled with its 4-hour discard time. This does not apply to self-service buffet lines. o Containers of TCS/PHF under time control and placed on preparation counters must be labeled with the discard time, even if the outlet is open less than 4 hours.\n• Must be discarded within 4 hours of placement on time control.\nTime – maximum up to 6 hours If time without temperature control is used as the public health control up to a maximum of 6 hours, then the FOOD\n• Must have an initial temperature of 5°C (41°F) or less when removed from temperature control and the FOOD temperature must not exceed 21°C (70°F).\n• Must be monitored to ensure the warmest portion of the FOOD does not exceed 21°C (70°F).\n• Must be marked or otherwise identified to indicate the time when the FOOD is removed from temperature control and the time 6 hours later (regardless of whether the time between service setup and closing exceeds 6 hours).\n• Must be discarded within 6 hours of placement on time control or if the FOOD temperature exceeds 21°C (70°F).\n• Must not be placed on temperature control again.',
            },
            {
              section: '7.2.6.1.1',
              label: '7.2.6.1.1 - Raw Shell Egg Preparations',
              title: 'Raw Shell Egg Preparations',
              critical: true,
              pointValue: 5,
              description:
                'Raw shell egg preparations are prohibited in uncooked products as described in 7.2.3.2.3.',
            },
            {
              section: '7.2.6.1.2',
              label: '7.2.6.1.2 - Animal Food',
              title: 'Animal Food',
              critical: true,
              pointValue: 5,
              description:
                'If an animal FOOD such as beef, eggs, FISH, lamb, milk, pork, POULTRY, or shellfish that is raw, undercooked, or not otherwise processed to eliminate pathogens is offered in a READY-TO-EAT form or as a raw ingredient in another READY-TO-EAT FOOD, the CONSUMER must be informed by way of disclosure (as specified below using menu advisories, placards, or other easily visible written means) of the significantly increased risk for foodborne illnesses to certain people especially vulnerable CONSUMERS eating such FOOD in raw or undercooked form. The advisory must be located at the outlets where these types of FOOD are served.\nDisclosure must be made by one of the two following methods:\n• On a sign describing the animal-derived FOOD (for example, “oysters on the half-shell,” “hamburgers,” “steaks,” or “eggs”) AND that they can be cooked to order or may be served raw or undercooked AND a statement indicating that consuming raw or undercooked MEATS, seafood, shellfish, eggs, milk, or POULTRY may increase your risk for foodborne illness, especially if you have certain medical conditions. The advisory must be posted at the specific station where the FOOD is served raw, undercooked, or cooked to order.\nOR\n• On a menu using an asterisk at the animal-derived FOOD requiring disclosure and a footnote with a statement indicating that consuming raw or undercooked MEATS, seafood, shellfish, eggs, milk, or POULTRY may increase your risk for foodborne illness, especially if you have certain medical conditions.\nIt is acceptable to limit the list of animal-derived FOOD in the CONSUMER advisory to only the type(s) of animal-derived FOOD served raw, undercooked, or cooked to order at a specific location. For example, at a sushi counter, the CONSUMER advisory might only refer to seafood.\nA CONSUMER advisory is not required for raw or undercooked WHOLE-MUSCLE, INTACT BEEF steak as specified under section 7.2.2.1.4.',
            },
          ],
        },
        {
          itemNo: '17',
          label: '17 - Temperature practices; Thawing',
          pointValue: 2,
          critical: false,
          description: 'Temperature practices; Thawing',
          subItems: [
            {
              section: '7.2.4.1.3',
              label: '7.2.4.1.3 - Fruits/Vegetables',
              title: 'Fruits/Vegetables',
              critical: false,
              pointValue: 2,
              description:
                'Fruits and vegetables cooked for hot holding must be cooked to a temperature of 57°C (135°F).',
            },
            {
              section: '7.2.4.2.2',
              label: '7.2.4.2.2 - Records',
              title: 'Records',
              critical: false,
              pointValue: 2,
              description:
                'If raw, raw-marinated, partially cooked, or marinated partially cooked FISH are served in READY-TO-EAT form,\n• The supervisor or PERSON IN CHARGE must record the freezing temperature and time to which the FISH are subjected and must retain the records on the vessel for 90 calendar days beyond the time of service or sale of the FISH OR\n• If the FISH are frozen by a supplier, a written letter from the supplier must specify the FISH species involved and both the temperature to which the FISH was frozen and the total time at that temperature. If the supplier provides any of the same FISH species to the vessel in a fresh state, the outer packaging must designate which one is the parasite-free FISH.\nFor FISH exempt from freezing requirements based on section 7.2.4.2.1, a written letter from the supplier must state both the species of FISH and the conditions in which they were raised and fed.',
            },
            {
              section: '7.2.5.1.1',
              label: '7.2.5.1.1 - Store Frozen Food Frozen',
              title: 'Store Frozen Food Frozen',
              critical: false,
              pointValue: 2,
              description: 'Stored frozen FOOD must be maintained frozen.',
            },
            {
              section: '7.2.5.1.2',
              label: '7.2.5.1.2 - Slacking',
              title: 'Slacking',
              critical: false,
              pointValue: 2,
              description:
                'Frozen TCS/PHF that is slacked to moderate the temperature must be held\n• Under refrigeration that maintains the FOOD temperature at 5°C (41°F) or less; or\n• At any temperature if the FOOD remains frozen.',
            },
            {
              section: '7.2.5.1.3',
              label: '7.2.5.1.3 - Thawing',
              title: 'Thawing',
              critical: false,
              pointValue: 2,
              description:
                'TCS/PHF must be thawed by one of the following:\n• Under refrigeration that maintains the FOOD temperature at 5°C (41°F) or less.\n• Completely submerged under running water at a water temperature of 21°C (70°F) or below, with sufficient water velocity to agitate and float off loose particles in an overflow, and for a period that does not allow thawed portions of READY- TO-EAT FOOD to rise above 5°C (41°F).\n• Completely submerged under running water at a water temperature of 21°C (70°F) or below with sufficient water velocity to agitate and float off loose particles in an overflow and for a period of time that does not allow thawed portions of a raw animal FOOD requiring cooking to be above 5°C (41°F) for more than 4 hours, including o The time the FOOD is exposed to the running water and the time needed for preparation for cooking OR o The time it takes under refrigeration to lower the FOOD temperature to 5°C (41°F).\n• As part of a cooking process if the frozen FOOD is cooked or thawed in a microwave oven.\nIf a portion of frozen READY-TO-EAT FOOD is thawed and prepared for immediate service in response to an individual CONSUMER’S order, it can be thawed using any procedure.',
            },
            {
              section: '7.2.5.2.4',
              label: '7.2.5.2.4 - Cooling Methods',
              title: 'Cooling Methods',
              critical: false,
              pointValue: 2,
              description:
                'Cooling must be accomplished using one or more of the following methods based on the type of FOOD being cooled:\n• Placing the FOOD in shallow pans.\n• Separating the FOOD into smaller or thinner portions.\n• Using BLAST CHILLERS, freezers, or other rapid cooling EQUIPMENT.\n• Stirring the FOOD in a container placed in an ice water bath.\n• Using containers that facilitate heat transfer.\n• Adding ice as an ingredient.\n• Using other effective methods.\nWhen placed in cooling or cold-holding EQUIPMENT, FOOD containers in which FOOD is being cooled must be arranged in the EQUIPMENT to provide maximum heat transfer through the container walls and must be loosely covered—or uncovered if protected from overhead CONTAMINATION—during the cooling period to facilitate heat transfer from the surface of the FOOD.',
            },
            {
              section: '7.2.5.2.5',
              label: '7.2.5.2.5 - Cooling Logs',
              title: 'Cooling Logs',
              critical: false,
              pointValue: 2,
              description:
                'Logs documenting cooked TCS/PHF cooling temperatures and times from the starting points designated in section 7.2.5.2.1 through the control points at 2 and 6 hours must be maintained on the vessel for 30 days from the date the FOOD was placed in a cooling process. Logs documenting cooling of TCS/PHF prepared from ingredients at ambient temperatures, with the start time to the time when 5°C (41°F) is reached, must also be maintained for 30 days beginning with the day of preparation.',
            },
            {
              section: '7.2.5.3.6',
              label: '7.2.5.3.6 - Time Control Plan',
              title: 'Time Control Plan',
              critical: false,
              pointValue: 2,
              description:
                'A written time control plan that ensures compliance with these standards must be maintained on the vessel and made available for review during inspections (Annex 13.12). A time control plan must be posted and available to all crew working at each outlet where time control is used.\nThe plan(s) must\n• Include set-up and discard times for each outlet. Include all services and events where TCS/PHF is kept on time control. If used, colored labels must correspond to the discard times stated in the plan.\n• List all units (to include refrigeration and hot holding units, preparation counters, cabinets, compartments, and EQUIPMENT) on time control. Cabinets and compartments on time control must be physically labeled.\nEQUIPMENT that does not have cabinets or compartments does not need to be labeled as time control. Such EQUIPMENT includes bains-marie, cold tops, and soup wells.\n• Describe or show the flow of TCS/PHF from when last in temperature control to placement in time control and discard.',
            },
          ],
        },
        {
          itemNo: '18',
          label: '18 - Cross-contamination',
          pointValue: 3,
          critical: true,
          description: 'Cross-contamination',
          subItems: [
            {
              section: '7.2.3.2.1',
              label: '7.2.3.2.1 - Cross-Contamination',
              title: 'Cross-Contamination',
              critical: true,
              pointValue: 3,
              description:
                'FOOD must be protected from cross-CONTAMINATION or other sources of CONTAMINATION by the following methods:\n• Physically separating raw animal FOOD during storage, preparation, holding, and display from raw READY-TO-EAT FOOD (including other raw animal FOOD such as FISH for sushi or MOLLUSCAN SHELLFISH, or other raw READY-TO-EAT FOOD such as vegetables, and cooked READY-TO-EAT FOOD) so products do not physically touch and so that one product does not drip into another.\n• Physically separating READY-TO-EAT FOOD from other READY- TO-EAT FOOD that include raw or undercooked animal products, including meat and FISH, so that products do not physically touch and so that one FOOD cannot drip into another.\n• Separating types of raw animal FOOD such as beef, FISH, lamb, pork, and POULTRY from each other—except when combined as ingredients—during storage, preparation, holding, and display by using separate EQUIPMENT for each type, or by arranging each type of FOOD in EQUIPMENT so that CROSS- CONTAMINATION of one type with another is prevented, or by preparing each type of FOOD at different times or in separate areas. Frozen, commercially processed and PACKAGED raw animal FOOD may be stored or displayed with or above frozen, commercially processed and PACKAGED, READY-TO-EAT FOOD.\n• Cleaning and sanitizing EQUIPMENT and UTENSILS.\n• Storing the FOOD in packages, covered containers, or wrappings.\n• Cleaning visible soil on HERMETICALLY SEALED CONTAINERS of FOOD before opening.\n• Protecting FOOD containers that are received PACKAGED together in a case or overwrapped from cuts when the case or overwrap is opened.\n• Separating damaged, spoiled, or recalled FOOD being held on the vessel.\n• Separating unwashed fruits and vegetables from READY-TO-EAT FOOD.\nStorage exceptions: Storing the FOOD in packages, covered containers, or wrappings does not apply to\n• Whole, uncut, raw fruits and vegetables and nuts in the shell that require peeling or hulling before consumption.\n• PRIMAL CUTS, quarters, or sides of raw MEAT or slab bacon hung on clean, sanitized hooks or placed on clean, sanitized racks.\n• Whole, uncut, processed MEATS such as country hams, and smoked or cured sausages placed on clean, sanitized racks.\n• FOOD being cooled.\n• SHELLSTOCK.',
            },
            {
              section: '7.2.3.2.3',
              label: '7.2.3.2.3 - Pasteurized Eggs',
              title: 'Pasteurized Eggs',
              critical: true,
              pointValue: 3,
              description:
                'Pasteurized eggs or egg products must be substituted for raw shell eggs in the preparation of FOOD such as Caesar salad, hollandaise or béarnaise sauce mayonnaise, eggnog, ice cream, and egg-fortified BEVERAGES or dessert items that are not cooked.',
            },
            {
              section: '7.2.7.1.1',
              label: '7.2.7.1.1 - Unsafe/Adulterated',
              title: 'Unsafe/Adulterated',
              critical: true,
              pointValue: 3,
              description:
                'A FOOD that is unsafe or ADULTERATED must be discarded.',
            },
            {
              section: '7.2.7.1.2',
              label: '7.2.7.1.2 - Unapproved Source',
              title: 'Unapproved Source',
              critical: true,
              pointValue: 3,
              description:
                'FOOD that is not from an APPROVED source must be discarded.',
            },
            {
              section: '7.2.7.1.3',
              label: '7.2.7.1.3 - Restricted or Excluded Employee',
              title: 'Restricted or Excluded Employee',
              critical: true,
              pointValue: 3,
              description:
                'READY-TO-EAT FOOD that may have been contaminated by an employee who has been restricted or excluded for FOOD EMPLOYEE health issues must be discarded.',
            },
            {
              section: '7.2.7.1.4',
              label: '7.2.7.1.4 - Contaminated by Others',
              title: 'Contaminated by Others',
              critical: true,
              pointValue: 3,
              description:
                'FOOD that is contaminated by FOOD EMPLOYEES, CONSUMERS, or other persons through contact with their hands, bodily discharges (such as nasal or oral discharges), or other means must be discarded.',
            },
          ],
        },
        {
          itemNo: '19',
          label:
            '19 - Food protected; Original containers, labeling; In-use food dispensing, preparation utensils',
          pointValue: 2,
          critical: false,
          description:
            'Food protected; Original containers, labeling; In-use food dispensing, preparation utensils',
          subItems: [
            {
              section: '7.2.2.2.9',
              label: '7.2.2.2.9 - Shellstock Condition',
              title: 'Shellstock Condition',
              critical: false,
              pointValue: 2,
              description:
                'SHELLSTOCK must be reasonably free of mud, dead shellfish, and shellfish with broken shells when received by a vessel. Dead shellfish or SHELLSTOCK with badly broken shells must be discarded.',
            },
            {
              section: '7.2.3.1.3',
              label: '7.2.3.1.3 - Not Ready-to-Eat Food – Minimize Contact',
              title: 'Not Ready-to-Eat Food – Minimize Contact',
              critical: false,
              pointValue: 2,
              description:
                'FOOD EMPLOYEES must minimize bare hand and arm contact with exposed FOOD not in a READY-TO-EAT form.',
            },
            {
              section: '7.2.3.2.2',
              label: '7.2.3.2.2 - Container Identity',
              title: 'Container Identity',
              critical: false,
              pointValue: 2,
              description:
                'Containers holding FOOD or FOOD ingredients removed from their original packages for use on the vessel, such as cooking oils, flour, herbs, potato flakes, salt, spices, and sugar must be identified with the common name of the FOOD.\nContainers holding FOOD that can be readily and unmistakably recognized such as dry pasta do not need to be identified.\nIngredients located at active cooking or preparation stations do not need to be identified.',
            },
            {
              section: '7.2.3.2.4',
              label: '7.2.3.2.4 - Wash Fruits/Vegetables',
              title: 'Wash Fruits/Vegetables',
              critical: false,
              pointValue: 2,
              description:
                'Raw fruits and vegetables must be thoroughly rinsed in water to remove soil and other contaminants before being cut, combined with other ingredients, cooked, served, or offered for human consumption in READY-TO-EAT form.\nFruits and vegetables may be washed by using chemicals specified under 21 CFR 173.315 (Annex 13.11).',
            },
            {
              section: '7.2.3.3.1',
              label: '7.2.3.3.1 - Ice Used as a Coolant',
              title: 'Ice Used as a Coolant',
              critical: false,
              pointValue: 2,
              description:
                'Ice must not be used as FOOD after use as a medium for cooling the exterior surfaces of FOOD such as melons or FISH, PACKAGED FOOD such as canned BEVERAGES, or cooling coils and tubes of EQUIPMENT.',
            },
            {
              section: '7.2.3.3.2',
              label: '7.2.3.3.2 - Coolant',
              title: 'Coolant',
              critical: false,
              pointValue: 2,
              description:
                'PACKAGED FOOD must not be stored in direct contact with ice or water if the FOOD is subject to the entry of water because of the nature of its packaging, wrapping, or container, or its positioning in the ice or water.',
            },
            {
              section: '7.2.3.3.3',
              label: '7.2.3.3.3 - Undrained Ice',
              title: 'Undrained Ice',
              critical: false,
              pointValue: 2,
              description:
                'Except as specified in sections 7.2.3.3.4 through 7.2.3.3.6, unpackaged FOOD must not be stored in direct contact with undrained ice.',
            },
            {
              section: '7.2.3.4.2',
              label: '7.2.3.4.2 - Storage During Use',
              title: 'Storage During Use',
              critical: false,
              pointValue: 2,
              description:
                'During pauses in FOOD preparation or dispensing, FOOD preparation and dispensing UTENSILS must be stored in one of the following ways:\n• In the FOOD with their handles above the top of the FOOD and the container.\n• In FOOD that is not TCS/PHF with their handles above the top of the FOOD within containers or EQUIPMENT that can be closed, such as bins of sugar, flour, or cinnamon.\n• On a clean portion of the FOOD preparation table or cooking EQUIPMENT only if the in-use UTENSIL and the FOOD-CONTACT SURFACE of the FOOD preparation table or cooking EQUIPMENT are cleaned and sanitized at least every 4 hours.\n• In running water of sufficient velocity to flush particulates to the drain (if used with moist FOOD such as ice cream or mashed potatoes).\n• In a clean, protected location (if the UTENSILS, such as ice scoops, are used only with a FOOD that is not TCS/PHF).\n• In a container of water (if the water is maintained at a temperature of at least 57°C [135°F] and the container is frequently cleaned and sanitized).',
            },
            {
              section: '7.2.3.4.3',
              label: '7.2.3.4.3 - Linen/Napkins',
              title: 'Linen/Napkins',
              critical: false,
              pointValue: 2,
              description:
                'LINENS and napkins must not be used in contact with FOOD unless they are used to line a container for the service of FOOD and the LINENS and napkins are replaced each time the container is refilled for a new CONSUMER.',
            },
            {
              section: '7.2.3.4.5',
              label: '7.2.3.4.5 - Glove Use',
              title: 'Glove Use',
              critical: false,
              pointValue: 2,
              description:
                'Gloves must be used as follows:\n• Single-use gloves must be used for only one hand-contact task with READY-TO-EAT FOOD and no other purpose. These single- use gloves must be discarded when damaged or soiled or when operation is interrupted.\n• Slash-resistant gloves used to protect hands during operations requiring cutting must be used in direct contact only with FOOD that is subsequently cooked (such as frozen FOOD or a PRIMAL CUT of MEAT).\n• Slash-resistant gloves may be used with READY-TO-EAT FOOD that will not be subsequently cooked if the slash-resistant gloves have a SMOOTH, durable, and nonabsorbent outer surface or if the slash-resistant gloves are covered with a SMOOTH, durable, nonabsorbent glove or a SINGLE-USE glove.\n• Cloth gloves must not be used in direct contact with FOOD unless the FOOD is subsequently cooked (such as frozen FOOD or a PRIMAL CUT of MEAT).',
            },
            {
              section: '7.2.3.4.6',
              label: '7.2.3.4.6 - Second Portions and Refills',
              title: 'Second Portions and Refills',
              critical: false,
              pointValue: 2,
              description:
                'Procedures for second portions and refills must be as follows:\n• FOOD EMPLOYEES must not use TABLEWARE soiled by the CONSUMER—including SINGLE-SERVICE ARTICLES—except for refilling a CONSUMER’S drinking cup or container without contact between the pouring UTENSIL and the lip-contact area of the drinking cup or container.\n• Self-service CONSUMERS must not be allowed to use soiled TABLEWARE—including SINGLE-SERVICE ARTICLES—to obtain additional FOOD from the display and serving EQUIPMENT except as specified in the bullet below.\n• Self-service CONSUMERS may reuse drinking cups and containers if refilling is a CONTAMINATION-free process.',
            },
            {
              section: '7.2.3.5.1',
              label: '7.2.3.5.1 - Storage Protection',
              title: 'Storage Protection',
              critical: false,
              pointValue: 2,
              description:
                'FOOD must be protected from CONTAMINATION by storing it as follows:\n• Covered or otherwise protected;\n• In a clean, dry location;\n• Where it is not exposed to splash, dust, or other CONTAMINATION; and\n• At least 150 millimeters (6 inches) above the deck. FOOD in packages and working containers on pallets, skids, and racks may be 127 millimeters (5 inches) above the deck. Shelving units must be 150 millimeters (6 inches) above the deck.',
            },
            {
              section: '7.2.3.5.2',
              label: '7.2.3.5.2 - Prohibited Storage',
              title: 'Prohibited Storage',
              critical: false,
              pointValue: 2,
              description:
                'FOOD must not be stored as follows:\n• In locker rooms.\n• In toilet rooms.\n• In dressing rooms.\n• In garbage rooms.\n• In mechanical rooms.\n• Under sewer lines that are not continuously sleeve welded.\n• Under leaking water lines, including leaking automatic fire sprinkler heads, or under lines on which water has condensed.\n• Under open stairwells.\n• Under other sources of CONTAMINATION from nonfood items such as ice blocks, ice carvings, and flowers.\n• In areas not finished in accordance with sections 7.6.5 and 7.6.6 for FOOD STORAGE AREAS.',
            },
            {
              section: '7.2.3.5.3',
              label: '7.2.3.5.3 - Vending Machines',
              title: 'Vending Machines',
              critical: false,
              pointValue: 2,
              description:
                'TCS/PHF dispensed through a vending machine must be in the package in which it was placed in the galley or FOOD-PROCESSING PLANT at which it was prepared.',
            },
            {
              section: '7.2.3.5.4',
              label: '7.2.3.5.4 - Preparation',
              title: 'Preparation',
              critical: false,
              pointValue: 2,
              description:
                'During preparation, unpackaged FOOD must be protected from environmental sources of CONTAMINATION such as rain.',
            },
            {
              section: '7.2.3.6.1',
              label: '7.2.3.6.1 - Display Preparation',
              title: 'Display Preparation',
              critical: false,
              pointValue: 2,
              description:
                'FOOD on display must be protected from CONTAMINATION using packaging; counter, service line, or salad bar FOOD guards; display cases; self-closing hinged lids; or other effective means. Install side protection for sneeze guards if the distance between exposed FOOD and where CONSUMERS are expected to stand is less than 1 meter (39 inches).',
            },
            {
              section: '7.2.3.6.2',
              label: '7.2.3.6.2 - Condiments',
              title: 'Condiments',
              critical: false,
              pointValue: 2,
              description:
                'Condiments must be protected from CONTAMINATION by being kept in one of the following:\n• Dispensers designed to provide protection.\n• Protected FOOD displays provided with the proper UTENSILS.\n• Original containers designed for dispensing.\n• Individual packages or portions.\nCondiments at a vending machine location must be in individual packages or provided in dispensers that are filled at an APPROVED location, such as the galley that provides FOOD to the vending machine location, a FOOD-PROCESSING PLANT, or a properly equipped facility located on the site of the vending machine location.',
            },
            {
              section: '7.2.3.6.3',
              label: '7.2.3.6.3 - Self-Service',
              title: 'Self-Service',
              critical: false,
              pointValue: 2,
              description:
                'CONSUMER self-service operations, such as salad bars and buffets, for unpackaged READY-TO-EAT FOOD must be\n• Provided with suitable UTENSILS or effective dispensing methods that protect the FOOD from CONTAMINATION.\n• Monitored by FOOD EMPLOYEES trained in safe operating procedures.\nSelf-service of scooped frozen dessert must be out of shallow pans no deeper than 100 millimeters (4 inches) and no longer than 300 millimeters (12 inches).',
            },
            {
              section: '7.2.3.7.1',
              label: '7.2.3.7.1 - Other Contaminants',
              title: 'Other Contaminants',
              critical: false,
              pointValue: 2,
              description:
                'FOOD must be protected from CONTAMINATION that may result from a physical, chemical, biological origin.',
            },
            {
              section: '7.5.2.4.4',
              label: '7.5.2.4.4 - Nontoxic Tracking Powders',
              title: 'Nontoxic Tracking Powders',
              critical: false,
              pointValue: 2,
              description:
                'A nontoxic tracking powder such as talcum or flour, if used, must not contaminate FOOD.',
            },
            {
              section: '7.6.4.1.1',
              label: '7.6.4.1.1 - Drain Lines',
              title: 'Drain Lines',
              critical: false,
              pointValue: 2,
              description:
                'Drain lines from all fixtures; sinks; appliances; compartments; refrigeration units; or devices used, designed for, or intended to be used in the a) preparation, b) processing, c) storage, or d) handling of FOOD, ice, or drinks must be indirectly connected to appropriate waste systems by means of an AIR GAP or AIR BREAK.\nDrain lines from handwashing and mop sinks may be directly connected to the appropriate waste system.',
            },
            {
              section: '7.6.4.1.2',
              label: '7.6.4.1.2 - Overhead',
              title: 'Overhead',
              critical: false,
              pointValue: 2,
              description:
                'Drain lines carrying SEWAGE or other liquid waste must not pass directly overhead or horizontally through spaces used for preparing, serving, or storing FOOD or for washing or storing UTENSILS and EQUIPMENT. Drain lines that are unavoidable in these FOOD AREAS must be sleeve- welded and must not have mechanical couplings.',
            },
            {
              section: '8.2.2.2.2',
              label: '8.2.2.2.2 - Food Protection',
              title: 'Food Protection',
              critical: false,
              pointValue: 2,
              description:
                'Insect-control devices such as insect light traps must not be located over FOOD STORAGE AREAS, FOOD PREPARATION AREAS, FOOD SERVICE AREAS, or clean EQUIPMENT. Dead insects and insect fragments must be prevented from falling on exposed FOOD.',
            },
          ],
        },
      ],
    },
    {
      id: 'food-safety-equipment',
      name: 'Food Safety - Equipment',
      items: [
        {
          itemNo: '20',
          label:
            '20 - PHF temp maintenance facilities; Food-contact surfaces; Food TMDs',
          pointValue: 2,
          critical: false,
          description:
            'PHF temp maintenance facilities; Food-contact surfaces; Food TMDs',
          subItems: [
            {
              section: '7.3.1.1.2',
              label: '7.3.1.1.2 - Food-Contact Surfaces',
              title: 'Food-Contact Surfaces',
              critical: false,
              pointValue: 2,
              description:
                'Materials used in the construction of multiuse UTENSILS and FOOD- CONTACT SURFACES of EQUIPMENT must be as follows:\n• Durable, corrosion-resistant, and nonabsorbent.\n• Sufficient in weight and thickness to withstand repeated WAREWASHING.\n• Finished to have a SMOOTH, EASILY CLEANABLE surface.\n• Resistant to pitting, chipping, crazing, scratching, scoring, distortion, and decomposition.',
            },
            {
              section: '7.3.1.1.3',
              label: '7.3.1.1.3 - Cast Iron',
              title: 'Cast Iron',
              critical: false,
              pointValue: 2,
              description:
                'Cast iron must not be used for UTENSILS or FOOD-CONTACT SURFACES of EQUIPMENT. Cast iron may be used as a surface for cooking. Cast iron may be used in UTENSILS for serving FOOD if the UTENSILS are used only as part of an uninterrupted process from cooking through service.\nCast iron FOOD DISPLAY dishes heated to a temperature of 74°C (165°F) for 15 seconds may be used for the immediate service of FOOD.',
            },
            {
              section: '7.3.1.1.7',
              label: '7.3.1.1.7 - Wood',
              title: 'Wood',
              critical: false,
              pointValue: 2,
              description:
                'Wood use must be limited as follows:\n• Wood and wood wicker must not be used as a FOOD-CONTACT SURFACE.\n• Hard maple or an equivalently hard, close-grained wood may be used for cutting boards; cutting blocks; bakers’ tables; UTENSILS such as rolling pins, doughnut dowels, serving dishes, and chopsticks; and wooden paddles used in confectionery operations for pressure scraping kettles when manually preparing confections at a temperature of 110°C (230°F) or above.\n• Whole, uncut, raw fruits and vegetables, and nuts in the shell may be kept in the wood shipping container in which they were received until the fruits, vegetables, or nuts are used.\n• If the nature of the FOOD requires removal of rinds, peels, husks, or shells before consumption, the whole, uncut, raw FOOD may be kept in untreated wood containers or in treated wood containers if the containers are treated with a preservative that meets the requirements specified in 21 CFR 178.3800 Preservatives for Wood.',
            },
            {
              section: '7.3.1.1.8',
              label: '7.3.1.1.8 - Coatings',
              title: 'Coatings',
              critical: false,
              pointValue: 2,
              description:
                'Multiuse KITCHENWARE such as frying pans, griddles, saucepans, cookie sheets, and waffle bakers that have a perfluorocarbon resin coating must be used with nonscoring or nonscratching UTENSILS and cleaning aids.',
            },
            {
              section: '7.3.1.2.2',
              label: '7.3.1.2.2 - No Colors/Odors/Taste',
              title: 'No Colors/Odors/Taste',
              critical: false,
              pointValue: 2,
              description:
                'Materials used to make SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES must not impart colors, odors, or tastes to FOOD and must be clean.',
            },
            {
              section: '7.3.2.1.1',
              label: '7.3.2.1.1 - Food-Contact Durability/Strength',
              title: 'Food-Contact Durability/Strength',
              critical: false,
              pointValue: 2,
              description:
                'FOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS must be designed and constructed to be durable and to retain their characteristic qualities under normal use conditions.',
            },
            {
              section: '7.3.2.2.1',
              label: '7.3.2.2.1 - Multiuse Food-Contact Surfaces',
              title: 'Multiuse Food-Contact Surfaces',
              critical: false,
              pointValue: 2,
              description:
                'Multiuse FOOD-CONTACT SURFACES must be\n• SMOOTH.\n• Free of breaks, open SEAMS greater than 0.8 millimeters (1/32 inch), cracks, chips, inclusions, pits, and similar imperfections.\n• Free of sharp internal angles, corners, and crevices.\n• Finished to have SMOOTH welds and joints.\n• ACCESSIBLE for cleaning and inspection by one of the following methods. Note: This does not apply to cooking oil storage tanks, distribution lines for cooking oils, or BEVERAGE syrup lines or tubes. o Without being disassembled, o by disassembling without the use of tools, or o by easy disassembling with the use of handheld tools commonly available to maintenance and cleaning personnel (such as screwdrivers, pliers, open-end wrenches, and Allen wrenches).\nUse low profile, nonslotted, NONCORRODING, and easy-to-clean fasteners on FOOD-CONTACT SURFACES and in splash zones. The use of exposed slotted screws, Phillips head screws, or pop rivets in these areas is prohibited.',
            },
            {
              section: '7.3.2.2.2',
              label:
                '7.3.2.2.2 - Cleaned-in-Place Equipment Design/Construction',
              title: 'Cleaned-in-Place Equipment Design/Construction',
              critical: false,
              pointValue: 2,
              description:
                'CIP EQUIPMENT must be designed and constructed so cleaning and sanitizing solutions circulate throughout a fixed system and contact all interior FOOD-CONTACT SURFACES and so the system self-drains or can be completely drained of cleaning and sanitizing solutions.\nCIP EQUIPMENT not designed to be disassembled for cleaning must be designed with inspection access points to ensure that all interior FOOD- CONTACT SURFACES throughout the fixed system are being effectively cleaned.',
            },
            {
              section: '7.3.2.2.4',
              label: '7.3.2.2.4 - Oil Filtering Equipment',
              title: 'Oil Filtering Equipment',
              critical: false,
              pointValue: 2,
              description:
                'Hot oil filtering EQUIPMENT must be ACCESSIBLE for filter replacement and filter cleaning.',
            },
            {
              section: '7.3.2.2.5',
              label: '7.3.2.2.5 - Can Openers',
              title: 'Can Openers',
              critical: false,
              pointValue: 2,
              description:
                'Cutting or piercing parts of can openers must be readily REMOVABLE for cleaning and replacement.',
            },
            {
              section: '7.3.2.3',
              label: '7.3.2.3 - Food Temperature-Measuring Devices',
              title: 'Food Temperature-Measuring Devices',
              critical: false,
              pointValue: 2,
              description:
                'FOOD TMDs must be provided and available for use in ensuring attainment and maintenance of FOOD temperatures. Tip-sensitive TMDs, such as a thermocouple or thermistor, must be used for measuring thin FOOD products.',
            },
            {
              section: '7.3.2.3.1',
              label: '7.3.2.3.1 - Food Temperature-Measuring Devices Accuracy',
              title: 'Food Temperature-Measuring Devices Accuracy',
              critical: false,
              pointValue: 2,
              description:
                'FOOD TMDs\n• Scaled only in Celsius or dually scaled in Celsius and Fahrenheit must be accurate to ± 1°C (± 2°F) in the intended range of use.\n• Scaled only in Fahrenheit must be accurate to ± 2°F in the intended range of use.\nFOOD TMDs must have a numerical scale, printed record, or digital readout in increments no greater than 1°C (2°F) in the intended range of use.',
            },
            {
              section: '7.3.2.4.2',
              label: '7.3.2.4.2 - Equipment Openings, Closures, and Deflectors',
              title: 'Equipment Openings, Closures, and Deflectors',
              critical: false,
              pointValue: 2,
              description:
                'EQUIPMENT openings, closures, and deflectors must conform to the following:\n• A cover or lid for EQUIPMENT must overlap the opening and be sloped to drain.\n• An opening in the top of a unit of EQUIPMENT and designed for use with a cover or lid must be flanged upward at least 5 millimeters (1/5 of an inch).\n• Fixed piping, TMDs, rotary shafts, and other parts extending into EQUIPMENT must be provided with a watertight joint at the point where the item enters the EQUIPMENT.\n• If a watertight joint is not provided, the piping, TMDs, rotary shafts, and other parts extending through the openings must be equipped with an apron designed to deflect condensation, drips, and dust from openings into the FOOD; the opening must be flanged at least 5 millimeters (1/5 of an inch).',
            },
            {
              section: '7.3.2.4.3',
              label: '7.3.2.4.3 - Beverage/Ice Dispensing',
              title: 'Beverage/Ice Dispensing',
              critical: false,
              pointValue: 2,
              description:
                'In EQUIPMENT that dispenses liquid FOOD or ice in unpackaged form,\n• The delivery tube, chute, orifice, and splash surfaces directly above the container receiving the FOOD must be designed in a manner (such as with barriers, baffles, or drip aprons) so that drips from condensation and splash are diverted from the opening of the container receiving the FOOD.\n• The delivery tube, chute, and orifice must be protected from manual contact (such as by being recessed).\n• The delivery tube or chute and orifice of EQUIPMENT used to vend liquid FOOD or ice in unpackaged form to self-service CONSUMERS must be designed so the delivery tube or chute and orifice are protected from dust, insects, rodents, and other CONTAMINATION by a self-closing door if the EQUIPMENT o Is in an outside area that does not otherwise afford protection against rain, windblown debris, insects, rodents, and other contaminants present in the environment OR o Is available for self-service during hours when it is not under the full-time supervision of a FOOD EMPLOYEE.\nThe dispensing EQUIPMENT actuating lever or mechanism and filling device of CONSUMER self-service BEVERAGE dispensing EQUIPMENT must be designed to prevent contact with the lip-contact surface of glasses or cups that are refilled.',
            },
            {
              section: '7.3.2.4.5',
              label: '7.3.2.4.5 - Beverage Line Cooling',
              title: 'Beverage Line Cooling',
              critical: false,
              pointValue: 2,
              description:
                'BEVERAGE tubing and cold-plate BEVERAGE cooling devices must not be installed in contact with stored ice. This does not apply to cold plates constructed integrally without SEAMS in an ice storage bin.',
            },
            {
              section: '7.3.2.4.7',
              label: '7.3.2.4.7 - Drain Lines',
              title: 'Drain Lines',
              critical: false,
              pointValue: 2,
              description:
                'Liquid waste drain lines must not pass through an ice machine or ice storage bin.',
            },
            {
              section: '7.3.2.5.1',
              label: '7.3.2.5.1 - Food-Contact Equipment Standards',
              title: 'Food-Contact Equipment Standards',
              critical: false,
              pointValue: 2,
              description:
                'FOOD-CONTACT SURFACES of FOOD EQUIPMENT must comply with American National Standards Institute (ANSI), National Sanitation Foundation International (NSF International), or other internationally accredited FOOD EQUIPMENT sanitation standards for materials, design, and construction.',
            },
            {
              section: '7.3.3.1',
              label: '7.3.3.1 - Cold/Hot Holding Capacity',
              title: 'Cold/Hot Holding Capacity',
              critical: false,
              pointValue: 2,
              description:
                'EQUIPMENT for cooling and heating FOOD and for holding cold and hot FOOD must be sufficient in number and capacity to maintain temperatures in TCS/PHF.',
            },
            {
              section: '7.3.5.1.1',
              label: '7.3.5.1.1 - Food-Contact Equipment in Good Repair',
              title: 'Food-Contact Equipment in Good Repair',
              critical: false,
              pointValue: 2,
              description:
                'FOOD-contact EQUIPMENT must be maintained in good repair and proper adjustment, including the following:\n• EQUIPMENT must be maintained in a state of repair and condition that meets the materials, design, construction, and operation specifications of these standards.\n• Can opener parts that cut or pierce must be kept sharp to minimize the creation of metal fragments that can contaminate FOOD when the container is opened.',
            },
            {
              section: '7.3.5.1.3',
              label: '7.3.5.1.3 - Cutting Boards',
              title: 'Cutting Boards',
              critical: false,
              pointValue: 2,
              description:
                'Surfaces such as cutting blocks and boards subject to scratching and scoring must be resurfaced if they can no longer be effectively cleaned and sanitized or discarded if they cannot be resurfaced.',
            },
            {
              section: '7.3.5.1.4',
              label: '7.3.5.1.4 - Microwave Ovens',
              title: 'Microwave Ovens',
              critical: false,
              pointValue: 2,
              description:
                'Microwave ovens must meet the safety standards specified in 21 CFR 1030.10 Microwave Ovens, or equivalent.',
            },
            {
              section: '7.3.5.3.2',
              label: '7.3.5.3.2 - Bulk Milk Tubes',
              title: 'Bulk Milk Tubes',
              critical: false,
              pointValue: 2,
              description:
                'Bulk milk container dispensing tubes must be cut on the diagonal, leaving no more than 25 millimeters (1 inch) protruding from the chilled dispensing head.',
            },
          ],
        },
        {
          itemNo: '21',
          label: '21 - Nonfood-contact surfaces; Ambient TMDs',
          pointValue: 1,
          critical: false,
          description: 'Nonfood-contact surfaces; Ambient TMDs',
          subItems: [
            {
              section: '7.3.1.1.9',
              label: '7.3.1.1.9 - Nonfood-Contact Surfaces',
              title: 'Nonfood-Contact Surfaces',
              critical: false,
              pointValue: 1,
              description:
                'NONFOOD-CONTACT SURFACES of EQUIPMENT that are exposed to splash, spillage, or other FOOD soiling or that require frequent cleaning must be constructed of a corrosion-resistant, nonabsorbent, and SMOOTH material.',
            },
            {
              section: '7.3.2.1.2',
              label: '7.3.2.1.2 - Nonfood-Contact Durability/Strength',
              title: 'Nonfood-Contact Durability/Strength',
              critical: false,
              pointValue: 1,
              description:
                'NONFOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS must be designed and constructed to be durable and to retain their characteristic qualities under normal use conditions.',
            },
            {
              section: '7.3.2.2.6',
              label: '7.3.2.2.6 - Nonfood-Contact Design',
              title: 'Nonfood-Contact Design',
              critical: false,
              pointValue: 1,
              description:
                'NONFOOD-CONTACT SURFACES must be free of unnecessary ledges, projections, and crevices, and must be designed and constructed to allow easy cleaning and facilitate maintenance.',
            },
            {
              section: '7.3.2.2.7',
              label: '7.3.2.2.7 - Kick Plates',
              title: 'Kick Plates',
              critical: false,
              pointValue: 1,
              description:
                'Kick plates must be designed so that the areas behind them are ACCESSIBLE for inspection and cleaning by being easily REMOVABLE or capable of being rotated open without unlocking EQUIPMENT doors.',
            },
            {
              section: '7.3.2.2.8',
              label: '7.3.2.2.8 - Grease Filters',
              title: 'Grease Filters',
              critical: false,
              pointValue: 1,
              description:
                'If not designed to be CLEANED IN PLACE, filters or other grease- extracting EQUIPMENT must be designed to be readily REMOVABLE for cleaning and replacement.',
            },
            {
              section: '7.3.2.3.2',
              label:
                '7.3.2.3.2 - Ambient Air Temperature-Measuring Devices Accuracy',
              title: 'Ambient Air Temperature-Measuring Devices Accuracy',
              critical: false,
              pointValue: 1,
              description:
                'Ambient air TMDs\n• Scaled in Celsius or dually scaled in Celsius and Fahrenheit must be designed to be easily readable and accurate to ± 1.5°C (± 3°F) in the intended range of use.\n• Scaled only in Fahrenheit must be accurate to ± 3°F in the intended range of use.',
            },
            {
              section: '7.3.2.4.4',
              label: '7.3.2.4.4 - Bearings and Gears',
              title: 'Bearings and Gears',
              critical: false,
              pointValue: 1,
              description:
                'EQUIPMENT containing bearings and gears that require lubricants must be designed and constructed so the lubricant cannot leak, drip, or be forced into FOOD or onto FOOD-CONTACT SURFACES.',
            },
            {
              section: '7.3.2.4.6',
              label: '7.3.2.4.6 - Equipment Drainage',
              title: 'Equipment Drainage',
              critical: false,
              pointValue: 1,
              description:
                'EQUIPMENT compartments subject to accumulation of moisture because of conditions such as condensation, FOOD or BEVERAGE drip, or water from melting ice must be sloped to an outlet that allows complete draining.',
            },
            {
              section: '7.3.2.4.8',
              label: '7.3.2.4.8 - Condenser Unit',
              title: 'Condenser Unit',
              critical: false,
              pointValue: 1,
              description:
                'If a condenser unit is an integral component of EQUIPMENT, the condenser unit must be separated from the FOOD and FOOD STORAGE space by a dustproof barrier.',
            },
            {
              section: '7.3.2.4.9',
              label: '7.3.2.4.9 - Ambient Air Temperature-Measuring Devices',
              title: 'Ambient Air Temperature-Measuring Devices',
              critical: false,
              pointValue: 1,
              description:
                'TMDs must be easily readable. They must conform to the following:\n• In a mechanically refrigerated FOOD storage unit, the sensor of a TMD must be located to measure the air temperature in the warmest part of the unit.\n• In a hot-FOOD storage unit, the sensor of a TMD must be located to measure the air temperature in the coolest part of the unit.\n• Cold or hot holding EQUIPMENT used for TCS/PHF must be designed to include and must be equipped with at least one integral or affixed TMD that is located to allow easy viewing of the device’s temperature display.\nThe above bullet does not apply to EQUIPMENT for which the placement of a TMD is not a practical means for measuring the ambient air surrounding the FOOD because of the design, type, and use of the EQUIPMENT (such as calrod units, heat lamps, cold plates, bains-marie, steam tables, insulated FOOD transport containers, and salad bars).',
            },
            {
              section: '7.3.2.5.2',
              label: '7.3.2.5.2 - Nonfood-Contact Equipment Standards',
              title: 'Nonfood-Contact Equipment Standards',
              critical: false,
              pointValue: 1,
              description:
                'NONFOOD-CONTACT SURFACES of FOOD EQUIPMENT must comply with American National Standards Institute (ANSI), National Sanitation Foundation International (NSF International), or other internationally accredited FOOD EQUIPMENT sanitation standards for materials, design, and construction.',
            },
            {
              section: '7.3.4.1.1',
              label: '7.3.4.1.1 - Fixed Equipment Installation',
              title: 'Fixed Equipment Installation',
              critical: false,
              pointValue: 1,
              description:
                'EQUIPMENT that is fixed because it is not EASILY MOVABLE must be installed in one of the following ways:\n• Spaced to allow access for cleaning along the sides, behind, under, and above the EQUIPMENT.\n• Spaced from adjoining EQUIPMENT, BULKHEAD, and DECKHEAD at a distance of no more than 0.8 millimeters (1/32 inch).\n• Sealed to adjoining EQUIPMENT or BULKHEAD (if the EQUIPMENT is exposed to spillage or seepage).',
            },
            {
              section: '7.3.4.1.2',
              label: '7.3.4.1.2 - Table-Mounted Sealed or Elevated',
              title: 'Table-Mounted Sealed or Elevated',
              critical: false,
              pointValue: 1,
              description:
                'TABLE-MOUNTED EQUIPMENT that is not EASILY MOVABLE must be installed to allow cleaning of the EQUIPMENT and areas underneath and around the EQUIPMENT in one of the following ways:\n• Sealed to the table.\n• Elevated on legs.',
            },
            {
              section: '7.3.4.2.1',
              label: '7.3.4.2.1 - Deck-Mounted Sealed or Elevated',
              title: 'Deck-Mounted Sealed or Elevated',
              critical: false,
              pointValue: 1,
              description:
                'Deck-mounted EQUIPMENT that is not EASILY MOVABLE must be sealed to the deck or elevated on legs that provide at least 150 millimeters (6 inches) of clearance between the deck and the EQUIPMENT.',
            },
            {
              section: '7.3.4.2.3',
              label: '7.3.4.2.3 - Table-Mounted Elevated',
              title: 'Table-Mounted Elevated',
              critical: false,
              pointValue: 1,
              description:
                'TABLE-MOUNTED EQUIPMENT that is not EASILY MOVABLE must be elevated on legs that provide at least 100 millimeters (4 inches) of clearance between the table and the EQUIPMENT.',
            },
            {
              section: '7.3.5.1.2',
              label: '7.3.5.1.2 - Nonfood-Contact Equipment in Good Repair',
              title: 'Nonfood-Contact Equipment in Good Repair',
              critical: false,
              pointValue: 1,
              description:
                'Nonfood-contact EQUIPMENT must be maintained in good repair and proper adjustment, including the following:\n• EQUIPMENT must be maintained in a state of repair and condition that meets the materials, design, construction, and operation specifications of these standards.\n• EQUIPMENT components such as doors, seals, hinges, fasteners, and kick plates must be kept intact and tight and adjusted in accordance with manufacturer’s specifications.',
            },
          ],
        },
        {
          itemNo: '22',
          label: '22 - Warewashing facilities; TMDs; Test kits',
          pointValue: 2,
          critical: false,
          description: 'Warewashing facilities; TMDs; Test kits',
          subItems: [
            {
              section: '7.4.1.1',
              label: '7.4.1.1 - Warewashing Measuring Device Accuracy',
              title: 'Warewashing Measuring Device Accuracy',
              critical: false,
              pointValue: 2,
              description:
                'Provide a maximum registering TMD to verify the temperature in the WAREWASHING machines and the three-compartment sink.',
            },
            {
              section: '7.4.1.1.1',
              label: '7.4.1.1.1 - Water Temperature-Measuring Devices Accuracy',
              title: 'Water Temperature-Measuring Devices Accuracy',
              critical: false,
              pointValue: 2,
              description:
                'Water TMDs scaled\n• In Celsius or dually scaled in Celsius and Fahrenheit must be designed to be accurate to ± 1.5°C (± 3°F) in the intended range of use.\n• Only in Fahrenheit must be designed to be accurate to ± 3°F in the intended range of use.',
            },
            {
              section: '7.4.1.1.2',
              label: '7.4.1.1.2 - Pressure Gauge Accuracy',
              title: 'Pressure Gauge Accuracy',
              critical: false,
              pointValue: 2,
              description:
                'Pressure measuring devices that display pressures in the water supply line for the fresh hot water sanitizing rinse must have increments of 7 kilopascals (1 pound per square inch or 0.07 bar) or smaller and must be accurate to ±14 kilopascals (±2 pounds per square inch or ±0.14 bar) in the 100–170 kilopascals (15–25 pounds per square inch or 1.03–1.72 bars) range.',
            },
            {
              section: '7.4.1.2.1',
              label: '7.4.1.2.1 - Water Temperature-Measuring Devices Readable',
              title: 'Water Temperature-Measuring Devices Readable',
              critical: false,
              pointValue: 2,
              description: 'Water TMDs must be designed to be easily readable.',
            },
            {
              section: '7.4.1.2.2',
              label:
                '7.4.1.2.2 - Water Temperature-Measuring Devices Readable Scale',
              title: 'Water Temperature-Measuring Devices Readable Scale',
              critical: false,
              pointValue: 2,
              description:
                'Water TMDs on WAREWASHING machines must have a numerical scale, printed record, or digital readout in increments no greater than 1°C (2°F) in the intended range of use.',
            },
            {
              section: '7.4.1.2.3',
              label: '7.4.1.2.3 - Data Plate',
              title: 'Data Plate',
              critical: false,
              pointValue: 2,
              description:
                'A WAREWASHING machine must be provided with a readable data plate affixed to or posted adjacent to the machine that indicates the machine’s design and operating specifications including the\n• Wash tank, rinse tank(s) if present, and final sanitizing rinse temperatures.\n• Pressure required for the freshwater sanitizing rinse unless the machine is designed to use only a pumped sanitizing rinse.\n• Conveyor speed in meters or feet per minute or minimum transit time for belt conveyor machines, minimum transit time for rack conveyor machines, and wash and final sanitizing rinse times as specified by the manufacturer for stationary rack machines.',
            },
            {
              section: '7.4.1.2.4',
              label: '7.4.1.2.4 - Baffles/Curtains',
              title: 'Baffles/Curtains',
              critical: false,
              pointValue: 2,
              description:
                'WAREWASHING machine wash and rinse tanks must be equipped with baffles, curtains, or other means to minimize internal CROSS- CONTAMINATION of the solutions in wash and rinse tanks.',
            },
            {
              section: '7.4.1.2.5',
              label: '7.4.1.2.5 - Warewash Temperature Measuring Devices',
              title: 'Warewash Temperature Measuring Devices',
              critical: false,
              pointValue: 2,
              description:
                'A WAREWASHING machine must be equipped with a TMD that indicates the temperature of the water in each wash tank, in rinse tank(s) if present, and in the final sanitizing rinse manifold.',
            },
            {
              section: '7.4.1.2.6',
              label: '7.4.1.2.6 - Pressure Gauge',
              title: 'Pressure Gauge',
              critical: false,
              pointValue: 2,
              description:
                'WAREWASHING machines that provide a fresh hot water sanitizing rinse must be equipped with a pressure gauge or similar device such as a transducer that measures and displays the water pressure in the supply line immediately before entering the WAREWASHING machine.\nIf the flow pressure measuring device is upstream of the fresh hot water sanitizing rinse control valve, the device must be mounted in a 6.4- millimeter (1/4-inch) iron pipe size valve.\nThis does not apply to a machine that uses only a pumped or recirculated sanitizing rinse.',
            },
            {
              section: '7.4.1.2.7',
              label: '7.4.1.2.7 - Manual Sanitizing Booster Heater',
              title: 'Manual Sanitizing Booster Heater',
              critical: false,
              pointValue: 2,
              description:
                'If hot water is used for SANITIZATION in manual WAREWASHING operations, the sanitizing compartment of the sink must be designed with an integral heating device capable of maintaining water at a temperature not less than 77°C (171°F).\nProvide a maximum registering TMD to verify the temperature in the sink.',
            },
            {
              section: '7.4.1.2.8',
              label: '7.4.1.2.8 - Self-Draining',
              title: 'Self-Draining',
              critical: false,
              pointValue: 2,
              description:
                'Sinks and drainboards of WAREWASHING sinks and machines must be self-draining.',
            },
            {
              section: '7.4.2.1.1',
              label: '7.4.2.1.1 - Three-Compartment Sink',
              title: 'Three-Compartment Sink',
              critical: false,
              pointValue: 2,
              description:
                'A sink with at least three compartments must be provided for manually washing, rinsing, and sanitizing EQUIPMENT and UTENSILS.',
            },
            {
              section: '7.4.2.1.2',
              label: '7.4.2.1.2 - Size',
              title: 'Size',
              critical: false,
              pointValue: 2,
              description:
                'Sink compartments must be large enough to accommodate immersion of the largest EQUIPMENT and UTENSILS. If EQUIPMENT or UTENSILS are too large for the WAREWASHING sink, a WAREWASHING machine or alternative EQUIPMENT, such as a three-bucket system, must be used.',
            },
            {
              section: '7.4.2.2.1',
              label: '7.4.2.2.1 - Soiled/Clean Storage',
              title: 'Soiled/Clean Storage',
              critical: false,
              pointValue: 2,
              description:
                'Drainboards, UTENSIL racks, or tables large enough to accommodate all soiled and cleaned items that may accumulate during hours of operation must be provided for UTENSIL holding before cleaning and after sanitizing.',
            },
            {
              section: '7.4.2.3',
              label: '7.4.2.3 - Test Kit',
              title: 'Test Kit',
              critical: false,
              pointValue: 2,
              description:
                'A test kit or other device that accurately measures the concentration in milligrams per liter (parts per million) of sanitizing solutions must be provided.',
            },
            {
              section: '7.4.3.1.1',
              label: '7.4.3.1.1 - Warewashing Equipment Repair',
              title: 'Warewashing Equipment Repair',
              critical: false,
              pointValue: 2,
              description:
                'WAREWASHING EQUIPMENT must be maintained in good repair and proper adjustment, including the following:\n• WAREWASHING EQUIPMENT must be maintained in a state of repair and condition that meets the standards of the materials, design, and construction.\n• Water pressure and water TMDs must be maintained in good repair and be accurate within the intended range of use.',
            },
            {
              section: '7.4.3.1.2',
              label: '7.4.3.1.2 - Warewashing Equipment Cleaning',
              title: 'Warewashing Equipment Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'WAREWASHING machines, drainboards, and the compartments of sinks, basins, or other receptacles used for washing and rinsing EQUIPMENT, UTENSILS, or raw FOOD, or laundering wiping cloths must be cleaned as follows:\n• Before use.\n• Throughout the day at a frequency necessary to prevent recontamination and accumulation of debris and to ensure the EQUIPMENT performs its intended function.\n• At least every 24 hours (if used).',
            },
            {
              section: '7.4.3.1.3',
              label: '7.4.3.1.3 - Warewashing Equipment Operation',
              title: 'Warewashing Equipment Operation',
              critical: false,
              pointValue: 2,
              description:
                'A WAREWASHING machine and its auxiliary components must be operated in accordance with the machine’s data plate and other manufacturer’s instructions.\nA WAREWASHING machine’s conveyor speed or automatic cycle times must be maintained accurately timed in accordance with manufacturer’s specifications.',
            },
            {
              section: '7.4.3.1.4',
              label: '7.4.3.1.4 - Cleaners',
              title: 'Cleaners',
              critical: false,
              pointValue: 2,
              description:
                'When used for WAREWASHING, the wash compartment of a sink, mechanical warewasher, or wash receptacle of alternative manual WAREWASHING EQUIPMENT must contain a wash solution of soap, detergent, acid cleaner, alkaline cleaner, degreaser, abrasive cleaner, or other cleaning agent according to the cleaning agent manufacturer’s label instructions.',
            },
            {
              section: '7.4.3.1.5',
              label: '7.4.3.1.5 - Solution Clean',
              title: 'Solution Clean',
              critical: false,
              pointValue: 2,
              description:
                'The wash, rinse, and sanitize solutions must be kept clean.',
            },
            {
              section: '7.4.3.2.4',
              label: '7.4.3.2.4 - Alarm',
              title: 'Alarm',
              critical: false,
              pointValue: 2,
              description:
                'Warewash machines for vessels built to VSP 2005 Construction Guidelines or later or installed/replaced on existing vessels after July 2005 must be equipped with an audible or visual alarm that indicates when the sanitizing temperature or chemical SANITIZER level drops below the levels stated on the machine data plate.',
            },
            {
              section: '7.4.4.3.3',
              label: '7.4.4.3.3 - Racking',
              title: 'Racking',
              critical: false,
              pointValue: 2,
              description:
                'Soiled items to be cleaned in a WAREWASHING machine must be loaded into racks, trays, or baskets or onto conveyors in a position that\n• Exposes the items to the unobstructed spray from all cycles and\n• Allows the items to drain.',
            },
            {
              section: '7.4.4.4.2',
              label: '7.4.4.4.2 - Soil Specific',
              title: 'Soil Specific',
              critical: false,
              pointValue: 2,
              description:
                'The washing procedures selected must be based on the type and purpose of the EQUIPMENT or UTENSIL and on the type of soil to be removed.',
            },
            {
              section: '7.4.4.5.2',
              label: '7.4.4.5.2 - Sponges Limited',
              title: 'Sponges Limited',
              critical: false,
              pointValue: 2,
              description:
                'Sponges must not be used in contact with cleaned and sanitized or in- use FOOD-CONTACT SURFACES.',
            },
            {
              section: '7.4.5.2.3',
              label: '7.4.5.2.3 - Warewasher Hot-Water Sanitizing Pressure',
              title: 'Warewasher Hot-Water Sanitizing Pressure',
              critical: false,
              pointValue: 2,
              description:
                'The flow pressure of the fresh hot water sanitizing rinse in a WAREWASHING machine must not be less than 34.5 kilopascals (5 pounds per square inch or 0.34 bars) or more than 207 kilopascals (30 pounds per square inch or 2.07 bars) as measured in the water line immediately downstream or upstream from the fresh hot water sanitizing rinse control valve.',
            },
            {
              section: '7.4.5.3.4',
              label: '7.4.5.3.4 - Sanitizer Concentration Testing',
              title: 'Sanitizer Concentration Testing',
              critical: false,
              pointValue: 2,
              description:
                'Concentration of the sanitizing solution must be accurately determined by using a test kit or other device.',
            },
          ],
        },
        {
          itemNo: '23',
          label: '23 - Pre-wash; Wash and rinse solutions',
          pointValue: 2,
          critical: false,
          description: 'Pre-wash; Wash and rinse solutions',
          subItems: [
            {
              section: '7.4.3.2.1',
              label: '7.4.3.2.1 - Manual Wash Temperature',
              title: 'Manual Wash Temperature',
              critical: false,
              pointValue: 2,
              description:
                'The temperature of the wash solution in manual WAREWASHING EQUIPMENT must be maintained at not less than the temperature specified on the cleaning agent manufacturer’s label instructions.',
            },
            {
              section: '7.4.3.2.2',
              label: '7.4.3.2.2 - Warewashing Wash Temperatures',
              title: 'Warewashing Wash Temperatures',
              critical: false,
              pointValue: 2,
              description:
                'The temperature of the wash solution in spray type warewashers that use hot water to sanitize must not be less than\n• 74°C (165°F) for a stationary-rack, single-temperature machine.\n• 66°C (150°F) for a stationary-rack, dual-temperature machine.\n• 71°C (160°F) for a single-tank, conveyor, dual-temperature machine.\n• 66°C (150°F) for a multi-tank, conveyor, multitemperature machine.\nHigh wash temperatures do not compensate for low auxiliary rinse and/or hot water final rinse sanitizing temperatures.',
            },
            {
              section: '7.4.3.2.3',
              label: '7.4.3.2.3 - Wash Temperatures for Chemical Machines',
              title: 'Wash Temperatures for Chemical Machines',
              critical: false,
              pointValue: 2,
              description:
                'The temperature of the wash solution in spray-type warewashers that use chemicals to sanitize must not be less than 49°C (120°F).',
            },
            {
              section: '7.4.4.3.1',
              label: '7.4.4.3.1 - Precleaning/Scrapping',
              title: 'Precleaning/Scrapping',
              critical: false,
              pointValue: 2,
              description:
                'FOOD debris on EQUIPMENT and UTENSILS must be scrapped over a waste disposal unit, pulper, or garbage receptacle or must be removed in a WAREWASHING machine with a prewash cycle.',
            },
            {
              section: '7.4.4.3.2',
              label: '7.4.4.3.2 - Presoak/Scrubbed',
              title: 'Presoak/Scrubbed',
              critical: false,
              pointValue: 2,
              description:
                'If necessary for effective cleaning, EQUIPMENT and UTENSILS must be preflushed, presoaked, or scrubbed with abrasives.',
            },
            {
              section: '7.4.4.4.1',
              label: '7.4.4.4.1 - Washing',
              title: 'Washing',
              critical: false,
              pointValue: 2,
              description:
                'FOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS must be effectively washed to remove or completely loosen soils by using whatever manual or mechanical means is necessary (such as application of detergents containing wetting agents and emulsifiers; acid, alkaline, or abrasive cleaners; hot water; brushes; scouring pads; high-pressure sprays; or ultrasonic devices).',
            },
            {
              section: '7.4.4.5.1',
              label: '7.4.4.5.1 - Alternative Warewashing Procedures',
              title: 'Alternative Warewashing Procedures',
              critical: false,
              pointValue: 2,
              description:
                'If washing in sink compartments or a WAREWASHING machine is impractical (such as when the EQUIPMENT is fixed or the UTENSILS are too large), washing must be done by using alternative manual WAREWASHING EQUIPMENT in accordance with the following procedures:\n• EQUIPMENT must be disassembled as necessary to allow access of the detergent solution to all parts.\n• EQUIPMENT components and UTENSILS must be scrapped or rough-cleaned to remove FOOD particle accumulation.\n• EQUIPMENT and UTENSILS must be washed.',
            },
            {
              section: '7.4.4.6.1',
              label: '7.4.4.6.1 - Rinsing',
              title: 'Rinsing',
              critical: false,
              pointValue: 2,
              description:
                'Washed EQUIPMENT and UTENSILS must be rinsed so that abrasives are removed, and cleaning chemicals are removed or diluted with water by using one of the following procedures:\n• Use of a distinct, separate water rinse after washing and before sanitizing (if using a three-compartment sink, alternative manual WAREWASHING EQUIPMENT equivalent to a three- compartment sink, or a three-step washing, rinsing, and sanitizing procedure in a WAREWASHING system for CIP EQUIPMENT).\n• Use of a nondistinct water rinse integrated in the application of the sanitizing solution and wasted immediately after each application (if using a WAREWASHING machine that does not recycle the sanitizing solution or if using alternative manual WAREWASHING EQUIPMENT such as sprayers).\n• Use of a nondistinct water rinse integrated in the application of the sanitizing solution (if using a WAREWASHING machine that recycles the sanitizing solution for use in the next wash cycle).',
            },
          ],
        },
        {
          itemNo: '24',
          label: '24 - Sanitizing rinse',
          pointValue: 3,
          critical: true,
          description: 'Sanitizing rinse',
          subItems: [
            {
              section: '7.4.5.1',
              label: '7.4.5.1 - Food-Contact Surfaces',
              title: 'Food-Contact Surfaces',
              critical: true,
              pointValue: 3,
              description:
                'FOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS must be sanitized.',
            },
            {
              section: '7.4.5.2.1',
              label: '7.4.5.2.1 - Manual Hot-Water Sanitizing',
              title: 'Manual Hot-Water Sanitizing',
              critical: true,
              pointValue: 3,
              description:
                'In a manual operation, if immersion in hot water is used for sanitizing,\n• The temperature of the water must be maintained at 77°C (171°F) or above and\n• The FOOD-CONTACT SURFACE must be immersed for at least 30 seconds.',
            },
            {
              section: '7.4.5.2.2',
              label: '7.4.5.2.2 - Warewasher Hot-Water Sanitizing',
              title: 'Warewasher Hot-Water Sanitizing',
              critical: true,
              pointValue: 3,
              description:
                'In a mechanical operation, the temperature of the fresh hot water sanitizing rinse as it enters the manifold must not be more than 90°C (194°F) or less than\n• 74°C (165°F) for a stationary rack, single-temperature machine.\n• 82°C (180°F) for all other machines.\nThe UTENSIL surface temperature must not be less than 71°C (160°F) as measured by an irreversible registering temperature indicator.\nThe maximum temperature of 90°C (194°F) does not apply to the high pressure and temperature systems with wand-type, hand-held spraying devices used for in-place cleaning and sanitizing of EQUIPMENT such as MEAT saws.',
            },
            {
              section: '7.4.5.3.1',
              label: '7.4.5.3.1 - Chemical Sanitizing Solutions',
              title: 'Chemical Sanitizing Solutions',
              critical: true,
              pointValue: 3,
              description:
                'A chemical SANITIZER used in a sanitizing solution for a manual or mechanical operation must be listed in 40 CFR 180.940 Sanitizing Solutions.',
            },
            {
              section: '7.4.5.3.2',
              label: '7.4.5.3.2 - Chemical Sanitizing Exposure',
              title: 'Chemical Sanitizing Exposure',
              critical: true,
              pointValue: 3,
              description:
                'A chemical SANITIZER must be used in accordance with the EPA- APPROVED manufacturer’s label use instructions at a minimum temperature of 24°C (75°F) with an exposure time of 7 seconds for a chlorine solution and 30 seconds for other chemical SANITIZERS.',
            },
            {
              section: '7.4.5.3.3',
              label: '7.4.5.3.3 - Chemical Sanitizing Concentration',
              title: 'Chemical Sanitizing Concentration',
              critical: true,
              pointValue: 3,
              description:
                'Sanitizing solutions must be used with the following concentrations:\n• A chlorine solution must have a concentration between 50 MG/L (ppm) and 200 MG/L (ppm).\n• An iodine solution must have a PH of 5.0 or less or a PH no higher than the level for which the manufacturer specifies the solution is effective AND a concentration between 12.5 MG/L (ppm) and 25 MG/L (ppm).\n• A quaternary ammonium compound solution must have a concentration as specified in 40 CFR 180.940 Sanitizing Solutions AND as indicated by the manufacturer’s use directions included in the labeling.\nIf another solution concentration or PH of a chlorine, iodine, or quaternary ammonium compound is used, the vessel must demonstrate to VSP that the solution achieves SANITIZATION, and the use of the solution must be APPROVED.\nIf a chemical SANITIZER other than a chlorine, iodine, or quaternary ammonium compound is used, it must be applied in accordance with the manufacturer’s use directions included in the labeling.',
            },
          ],
        },
        {
          itemNo: '25',
          label: "25 - Wiping cloths / chef's towels",
          pointValue: 1,
          critical: false,
          description: "Wiping cloths / chef's towels",
          subItems: [
            {
              section: '7.2.3.4.4',
              label: '7.2.3.4.4 - Wiping Cloths',
              title: 'Wiping Cloths',
              critical: false,
              pointValue: 1,
              description:
                'Wiping cloths must be restricted to the following:\n• Cloths used for wiping FOOD spills must be used for no other purpose.\n• Cloths used for wiping FOOD spills must be dry and used for wiping FOOD spills from TABLEWARE and SINGLE-SERVICE ARTICLES OR wet and cleaned, stored in a chemical SANITIZER, and used for wiping spills from FOOD-CONTACT and NONFOOD- CONTACT SURFACES of EQUIPMENT.\n• Dry or wet cloths used with raw animal FOOD must be kept separate from cloths used for other purposes. Wet cloths used with raw animal FOOD must be kept in a separate sanitizing solution.\n• Wet wiping cloths used with a freshly made sanitizing solution and dry wiping cloths must be free of FOOD debris and visible soil.',
            },
          ],
        },
        {
          itemNo: '26',
          label:
            '26 - Food-contact surfaces, equipment/utensils clean; Safe materials',
          pointValue: 3,
          critical: true,
          description:
            'Food-contact surfaces, equipment/utensils clean; Safe materials',
          subItems: [
            {
              section: '7.2.3.4.1',
              label: '7.2.3.4.1 - Cleaned/Sanitized',
              title: 'Cleaned/Sanitized',
              critical: true,
              pointValue: 3,
              description:
                'FOOD must only contact surfaces of cleaned and sanitized EQUIPMENT and UTENSILS.',
            },
            {
              section: '7.3.1.1.1',
              label: '7.3.1.1.1 - Safe Food-Contact Materials',
              title: 'Safe Food-Contact Materials',
              critical: true,
              pointValue: 3,
              description:
                'Materials used in the construction of multiuse UTENSILS and FOOD- CONTACT SURFACES of EQUIPMENT must not allow the migration of deleterious substances or impart colors, odors, or tastes to FOOD and must be safe under normal use conditions.',
            },
            {
              section: '7.3.1.1.4',
              label: '7.3.1.1.4 - Lead',
              title: 'Lead',
              critical: true,
              pointValue: 3,
              description:
                'Limitation of lead use must be as follows:\n• Ceramic, china, crystal UTENSILS, and decorative UTENSILS such as hand-painted ceramic or china used in contact with FOOD must be lead-free or contain levels of lead not exceeding the limits for specific UTENSIL categories as allowed by LAW.\n• Pewter alloys containing more than 0.05% lead must not be used as a FOOD-CONTACT SURFACE.\n• Solder and flux containing more than 0.2% lead must not be used as a FOOD-CONTACT SURFACE.',
            },
            {
              section: '7.3.1.1.5',
              label: '7.3.1.1.5 - Copper/Brass',
              title: 'Copper/Brass',
              critical: true,
              pointValue: 3,
              description:
                'Copper and copper alloys such as brass must not be used in contact with a FOOD that has a PH below 6 (such as vinegar, fruit juice, or wine) or for a fitting or tubing installed between a BACKFLOW PREVENTION DEVICE and a carbonator.\nCopper and copper alloys may be used in contact with beer brewing ingredients that have a PH below 6 in the prefermentation and fermentation steps of a beer brewing operation such as a brewpub or microbrewery.',
            },
            {
              section: '7.3.1.1.6',
              label: '7.3.1.1.6 - Galvanized',
              title: 'Galvanized',
              critical: true,
              pointValue: 3,
              description:
                'Galvanized metal must not be used for UTENSILS or FOOD-CONTACT SURFACES of EQUIPMENT.',
            },
            {
              section: '7.3.1.2.1',
              label: '7.3.1.2.1 - Single-Service Materials Safe',
              title: 'Single-Service Materials Safe',
              critical: true,
              pointValue: 3,
              description:
                'Materials used to make SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES must not allow the migration of deleterious substances and must be safe.',
            },
            {
              section: '7.3.2.1.3',
              label: '7.3.2.1.3 - Glass Temperature Measuring Devices',
              title: 'Glass Temperature Measuring Devices',
              critical: true,
              pointValue: 3,
              description:
                'FOOD TMDs must not have sensors or stems constructed of glass, except for thermometers with glass sensors or stems encased in a shatterproof coating (such as candy thermometers).',
            },
            {
              section: '7.4.4.1.1',
              label: '7.4.4.1.1 - Food-Contact Surfaces Clean',
              title: 'Food-Contact Surfaces Clean',
              critical: true,
              pointValue: 3,
              description:
                'FOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS must be clean to sight and touch.',
            },
            {
              section: '7.4.4.1.2',
              label: '7.4.4.1.2 - Encrusted',
              title: 'Encrusted',
              critical: true,
              pointValue: 3,
              description:
                'FOOD-CONTACT SURFACES of cooking EQUIPMENT and pans must be kept free of encrusted grease deposits and other soil accumulations.',
            },
            {
              section: '7.4.4.1.4',
              label: '7.4.4.1.4 - Food-Contact Cleaning Frequency',
              title: 'Food-Contact Cleaning Frequency',
              critical: true,
              pointValue: 3,
              description:
                'FOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS must be washed, rinsed, and sanitized as follows:\n• Before each use with a different type of raw animal FOOD such as beef, FISH, lamb, pork, or POULTRY.\n• Each time there is a change from working with raw FOOD to working with READY-TO-EAT FOOD.\n• Between uses with raw fruits and vegetables and with TCS/PHF.\n• Before using or storing a FOOD TMD.\n• Any time during the operation when CONTAMINATION might have occurred.',
            },
          ],
        },
        {
          itemNo: '27',
          label: '27 - Equipment/utensils non-food-contact surfaces clean',
          pointValue: 1,
          critical: false,
          description: 'Equipment/utensils non-food-contact surfaces clean',
          subItems: [
            {
              section: '7.4.4.1.3',
              label: '7.4.4.1.3 - Nonfood-Contact Surfaces',
              title: 'Nonfood-Contact Surfaces',
              critical: false,
              pointValue: 1,
              description:
                'NONFOOD-CONTACT SURFACES of EQUIPMENT must be kept free of an accumulation of dust, dirt, FOOD residue, and other debris.',
            },
          ],
        },
        {
          itemNo: '28',
          label:
            '28 - Equipment/utensil/linen/single-service storage, handling, dispensing, cleaning frequency',
          pointValue: 2,
          critical: false,
          description:
            'Equipment/utensil/linen/single-service storage, handling, dispensing, cleaning frequency',
          subItems: [
            {
              section: '7.3.5.3.1',
              label: '7.3.5.3.1 - No Reuse',
              title: 'No Reuse',
              critical: false,
              pointValue: 2,
              description:
                'SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES must not be reused.',
            },
            {
              section: '7.3.5.3.3',
              label: '7.3.5.3.3 - Shell Reuse',
              title: 'Shell Reuse',
              critical: false,
              pointValue: 2,
              description:
                'Mollusk and crustacean shells must not be used more than once as serving containers.',
            },
            {
              section: '7.4.4.1.5',
              label: '7.4.4.1.5 - In-Use Food-Contact Equipment',
              title: 'In-Use Food-Contact Equipment',
              critical: false,
              pointValue: 2,
              description:
                'If used with TCS/PHF, FOOD-CONTACT SURFACES of EQUIPMENT and UTENSILS used on a continuing basis must be washed, rinsed, and sanitized at least every 4 hours.',
            },
            {
              section: '7.4.4.1.6',
              label: '7.4.4.1.6 - Dispensing Equipment Cleaning',
              title: 'Dispensing Equipment Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'Cleaning of EQUIPMENT such as ice bins; BEVERAGE dispensing nozzles; and enclosed components of EQUIPMENT such as ice makers, cooking oil storage tanks, and distribution lines, BEVERAGE dispensing lines, and syrup dispensing lines or tubes; and coffee bean grinders must be conducted\n• At a frequency specified by the manufacturer, or\n• In the absence of manufacturer specifications, at a frequency necessary to preclude accumulation of soil or mold.',
            },
            {
              section: '7.4.4.1.7',
              label: '7.4.4.1.7 - Cooking/Baking Equipment Cleaning',
              title: 'Cooking/Baking Equipment Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'Cooking and baking EQUIPMENT must be cleaned as follows:\n• FOOD-CONTACT SURFACES of cooking and baking EQUIPMENT must be cleaned at least every 24 hours.\n• Cavities and door seals of microwave ovens must be cleaned at least every 24 hours by using the manufacturer’s recommended cleaning procedure.',
            },
            {
              section: '7.4.4.2',
              label: '7.4.4.2 - Dry Cleaning',
              title: 'Dry Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'If dry cleaning is used, it must be conducted as follows:\n• Methods such as brushing, scraping, and vacuuming must contact only surfaces soiled with dry FOOD residues that are not TCS/PHF.\n• Cleaning EQUIPMENT used in dry cleaning FOOD-CONTACT SURFACES must not be used for any other purpose.',
            },
            {
              section: '7.4.6.1.1',
              label: '7.4.6.1.1 - Air Dried/Drained',
              title: 'Air Dried/Drained',
              critical: false,
              pointValue: 2,
              description:
                'After cleaning and sanitizing, EQUIPMENT and UTENSILS must be air dried or ADEQUATELY drained before contact with FOOD. Cleaned, sanitized, and air-dried dishware, glassware, and UTENSILS may be polished with a clean, dry, lint-free cloth.',
            },
            {
              section: '7.4.6.2.1',
              label: '7.4.6.2.1 - Lubricating',
              title: 'Lubricating',
              critical: false,
              pointValue: 2,
              description:
                'Lubricants must be applied to FOOD-CONTACT SURFACES that require lubrication in a manner that does not contaminate FOOD-CONTACT SURFACES.',
            },
            {
              section: '7.4.6.2.2',
              label: '7.4.6.2.2 - Assembling',
              title: 'Assembling',
              critical: false,
              pointValue: 2,
              description:
                'EQUIPMENT must be reassembled so that FOOD-CONTACT SURFACES are not contaminated.',
            },
            {
              section: '7.4.6.3.1',
              label: '7.4.6.3.1 - Storing Protected',
              title: 'Storing Protected',
              critical: false,
              pointValue: 2,
              description:
                'Cleaned EQUIPMENT and UTENSILS, laundered LINENS, and SINGLE- SERVICE ARTICLES and SINGLE-USE ARTICLES must be stored\n• In a clean, dry location.\n• In a location where they are not exposed to splash, dust, or other CONTAMINATION.\n• At least 150 millimeters (6 inches) above the deck unless in packages on pallets, skids, or racks, which may be 127 millimeters (5 inches) above the deck.\nEQUIPMENT can be stored in unfinished lockers or rooms in sealed or resealed original packaging, plastic boxes with complete coverage (plastic wrap if needed), and/or wrapped in plastic. EQUIPMENT must be off the deck (rack/deck stand/pallet) and the deck must be clean.\nEQUIPMENT and shelving in unfinished lockers or rooms must be kept clean. Shelving does not need to meet EASILY CLEANABLE standard.\nSINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES must not be stored in unfinished lockers or rooms.\nChemicals must not be stored in unfinished lockers with FOOD equipment.\nExposed GRAY WATER and BLACK WATER lines must not be in unfinished EQUIPMENT lockers if they are push-fit or coupled. These lines can be solid pipe, butt-welded, or sleeve-welded. If plastic pipe, the lines can be heat fused or chemically welded.',
            },
            {
              section: '7.4.6.3.2',
              label: '7.4.6.3.2 - Storing Inverted',
              title: 'Storing Inverted',
              critical: false,
              pointValue: 2,
              description:
                'Clean EQUIPMENT and UTENSILS must be stored\n• In a self-draining position that allows air drying.\n• Covered or inverted.',
            },
            {
              section: '7.4.6.3.3',
              label: '7.4.6.3.3 - Preset Tableware',
              title: 'Preset Tableware',
              critical: false,
              pointValue: 2,
              description:
                'TABLEWARE preset longer than 4 hours before the beginning of service must be protected from CONTAMINATION by being wrapped, covered, or inverted.\nWhen TABLEWARE is preset, exposed unused settings must be\n• removed at the time a CONSUMER is seated or\n• washed, rinsed, and sanitized before further use if the settings are not removed when a CONSUMER is seated.',
            },
            {
              section: '7.4.6.3.4',
              label: '7.4.6.3.4 - Original Package',
              title: 'Original Package',
              critical: false,
              pointValue: 2,
              description:
                'SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES must be kept in the original protective package or stored by using other means that afford protection from CONTAMINATION until used.',
            },
            {
              section: '7.4.6.3.5',
              label: '7.4.6.3.5 - Utensil Dispensing',
              title: 'Utensil Dispensing',
              critical: false,
              pointValue: 2,
              description:
                'Eating UTENSILS dispensed at a CONSUMER self-service unit such as a buffet or salad bar must be protected from CONTAMINATION.',
            },
            {
              section: '7.4.7.1.1',
              label: '7.4.7.1.1 - Laundry Equipment',
              title: 'Laundry Equipment',
              critical: false,
              pointValue: 2,
              description:
                'If LINENS used in FOOD AREAS are laundered on the vessel, a mechanical clothes washer and dryer must be provided and used.',
            },
            {
              section: '7.4.7.1.2',
              label: '7.4.7.1.2 - Laundry Operations Location',
              title: 'Laundry Operations Location',
              critical: false,
              pointValue: 2,
              description:
                'Laundry operations must be located to protect operations from CONTAMINATION and only located where there are none of the following: Exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS; or unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.4.7.2.1',
              label: '7.4.7.2.1 - Laundry Frequency',
              title: 'Laundry Frequency',
              critical: false,
              pointValue: 2,
              description:
                'LINENS that do not come in direct contact with FOOD must be laundered between operations if they become wet, sticky, or visibly soiled.',
            },
            {
              section: '7.4.7.2.2',
              label: '7.4.7.2.2 - Cloth Gloves',
              title: 'Cloth Gloves',
              critical: false,
              pointValue: 2,
              description:
                'Cloth gloves must be laundered before being used with a different type of raw animal FOOD such as beef, lamb, pork, and FISH.',
            },
            {
              section: '7.4.7.2.3',
              label: '7.4.7.2.3 - Linens/Napkins',
              title: 'Linens/Napkins',
              critical: false,
              pointValue: 2,
              description:
                'LINENS and napkins used to line FOOD-service containers and cloth napkins must be laundered between each use.',
            },
            {
              section: '7.4.7.2.4',
              label: '7.4.7.2.4 - Wet Wiping Cloths',
              title: 'Wet Wiping Cloths',
              critical: false,
              pointValue: 2,
              description: 'Wet wiping cloths must be laundered daily.',
            },
            {
              section: '7.4.7.2.5',
              label: '7.4.7.2.5 - Dry Wiping Cloths',
              title: 'Dry Wiping Cloths',
              critical: false,
              pointValue: 2,
              description:
                'Dry wiping cloths must be laundered as necessary to prevent CONTAMINATION of FOOD and clean serving UTENSILS.',
            },
            {
              section: '7.4.7.2.6',
              label: '7.4.7.2.6 - Soiled Linens',
              title: 'Soiled Linens',
              critical: false,
              pointValue: 2,
              description:
                'Soiled LINENS must be kept in clean, nonabsorbent receptacles or clean, washable laundry bags and stored and transported to prevent CONTAMINATION of FOOD, clean EQUIPMENT, clean UTENSILS, and SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.4.7.2.7',
              label: '7.4.7.2.7 - Washing',
              title: 'Washing',
              critical: false,
              pointValue: 2,
              description: 'LINENS must be mechanically washed.',
            },
            {
              section: '7.5.2.4.5',
              label: '7.5.2.4.5 - Prevent Contamination',
              title: 'Prevent Contamination',
              critical: false,
              pointValue: 2,
              description:
                'If a nontoxic tracking powder such as talcum or flour is used, it must not contaminate EQUIPMENT, UTENSILS, LINENS, or SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.6.4.1.3',
              label: '7.6.4.1.3 - Warewash Sink/Machine Drains',
              title: 'Warewash Sink/Machine Drains',
              critical: false,
              pointValue: 2,
              description:
                'All drain lines from WAREWASHING sinks or machines must drain through an AIR GAP or AIR BREAK to a drain or SCUPPER.',
            },
            {
              section: '8.2.2.2.3',
              label: '8.2.2.2.3 - Utensil Protection',
              title: 'Utensil Protection',
              critical: false,
              pointValue: 2,
              description:
                'Insect-control devices must not be located over WAREWASHING, UTENSIL storage areas, EQUIPMENT, UTENSILS, LINENS, or unwrapped SINGLE-SERVICE ARTICLES or SINGLE-USE ARTICLES. Dead insects and insect fragments must be prevented from falling on clean items.',
            },
          ],
        },
      ],
    },
    {
      id: 'food-safety-toilet-and-handwashing-facilities',
      name: 'Food Safety - Toilet and Handwashing Facilities',
      items: [
        {
          itemNo: '29',
          label: '29 - Facilities convenient, accessible, design, installation',
          pointValue: 3,
          critical: true,
          description:
            'Facilities convenient, accessible, design, installation',
          subItems: [
            {
              section: '7.6.1.1.1',
              label: '7.6.1.1.1 - Convenient',
              title: 'Convenient',
              critical: true,
              pointValue: 3,
              description:
                'Each FOOD PREPARATION AREA, bar, WAREWASHING area, and garbage-processing area must have at least one handwashing station.\nThis also applies to self-service and served candy shops where employees serve candy, refill self-service containers, etc.',
            },
            {
              section: '7.6.1.1.2',
              label: '7.6.1.1.2 - 8 Meters/26 Feet',
              title: '8 Meters/26 Feet',
              critical: true,
              pointValue: 3,
              description:
                'The handwashing station must be within 8 meters (26 feet) of all parts of the area and must not be in an adjacent area that requires passage through a closed door where users make hand contact with the door.\nHandwashing sinks must be at least 750 millimeters (30 inches) above the deck, so users do not have to reach excessively to wash their hands.\nCounter-mounted handwashing sinks may be installed a minimum of 600 millimeters (24 inches) above the deck, as measured at the counter level.',
            },
            {
              section: '7.6.1.1.3',
              label: '7.6.1.1.3 - Tempered Water',
              title: 'Tempered Water',
              critical: true,
              pointValue: 3,
              description:
                'A handwashing sink must be equipped to provide water at a temperature of at least 29.4°C (85°F) through a mixing valve or combination faucet. For handwashing sinks with electronic sensors and other types of handwashing sinks where users cannot make temperature adjustments, the temperature after the mixing valve must not exceed 49°C (120°F).',
            },
            {
              section: '7.6.1.2.1',
              label: '7.6.1.2.1 - Convenient',
              title: 'Convenient',
              critical: true,
              pointValue: 3,
              description:
                'Toilet rooms must be provided and conveniently located.',
            },
            {
              section: '7.6.1.2.2',
              label: '7.6.1.2.2 - Handwashing Facilities',
              title: 'Handwashing Facilities',
              critical: true,
              pointValue: 3,
              description:
                'Handwashing facilities must be in or immediately adjacent to toilet rooms or vestibules.',
            },
            {
              section: '7.6.1.2.6',
              label: '7.6.1.2.6 - Unlocked',
              title: 'Unlocked',
              critical: true,
              pointValue: 3,
              description:
                'Toilet facilities intended for use by FOOD EMPLOYEES must not be locked when the FOOD AREA is in service.',
            },
            {
              section: '7.6.1.3.1',
              label: '7.6.1.3.1 - Accessible',
              title: 'Accessible',
              critical: true,
              pointValue: 3,
              description:
                'Handwashing facilities must be used for no other purpose and must always be accessible.',
            },
          ],
        },
        {
          itemNo: '30',
          label:
            '30 - Hand cleanser, sanitary towels, waste receptacles, handwash signs; Maintenance',
          pointValue: 1,
          critical: false,
          description:
            'Hand cleanser, sanitary towels, waste receptacles, handwash signs; Maintenance',
          subItems: [
            {
              section: '7.6.1.1.4',
              label: '7.6.1.1.4 - Metered Faucet',
              title: 'Metered Faucet',
              critical: false,
              pointValue: 1,
              description:
                'A self-closing, slow-closing, or metering faucet must provide a flow of water for at least 15 seconds without the need to reactivate the faucet.',
            },
            {
              section: '7.6.1.1.5',
              label: '7.6.1.1.5 - Automatic Systems',
              title: 'Automatic Systems',
              critical: false,
              pointValue: 1,
              description:
                'An automatic handwashing station must be installed in accordance with manufacturer’s instructions.',
            },
            {
              section: '7.6.1.1.6',
              label: '7.6.1.1.6 - Dispenser/Receptacle',
              title: 'Dispenser/Receptacle',
              critical: false,
              pointValue: 1,
              description:
                'A handwashing station must include a sink, soap dispenser, single-use towel dispenser, and waste receptacle.',
            },
            {
              section: '7.6.1.1.7',
              label: '7.6.1.1.7 - Sign',
              title: 'Sign',
              critical: false,
              pointValue: 1,
              description:
                'A sign stating “wash hands often,” “wash hands frequently,” or similar wording in a language the FOOD EMPLOYEES understand must be posted over handwashing sinks.',
            },
            {
              section: '7.6.1.2.3',
              label: '7.6.1.2.3 - Sign',
              title: 'Sign',
              critical: false,
              pointValue: 1,
              description:
                'A sign must be conspicuously posted on the BULKHEAD adjacent to the door of the toilet or on the back of the door. The sign must state “WASH HANDS AFTER USING TOILET” in a language the FOOD EMPLOYEES understand. A sign must be posted advising users of toilet facilities to use hand towel, paper towel, or tissue to open the door unless the exit is hands free.\nA pictogram that illustrates the correct use and disposal of paper towels may be used instead of a sign.\nToilet facilities must be equipped so persons exiting the toilet room are not required to touch the door handle with bare hands.\nWhere toilet stalls include handwashing facilities, the bare-hands-free contact must begin in the toilet stall. Toilet facilities with multiple exits must have bare-hands-free contact at each exit.\nBare-hands-free contact may be accomplished by methods such as locating paper towel dispensers at sinks and waste containers near the room door, installing mechanically operated doors, or using other effective means.',
            },
            {
              section: '7.6.1.2.4',
              label: '7.6.1.2.4 - Enclosed/Doors',
              title: 'Enclosed/Doors',
              critical: false,
              pointValue: 1,
              description:
                'Toilet rooms must be completely enclosed and must have tight-fitting, self-closing doors that must be kept closed except during cleaning or maintenance.',
            },
            {
              section: '7.6.1.2.5',
              label: '7.6.1.2.5 - Waste Receptacle',
              title: 'Waste Receptacle',
              critical: false,
              pointValue: 1,
              description:
                'EASILY CLEANABLE receptacles must be provided for waste materials.',
            },
            {
              section: '7.6.1.3.2',
              label: '7.6.1.3.2 - Facilities Clean/Good Repair',
              title: 'Facilities Clean/Good Repair',
              critical: false,
              pointValue: 1,
              description:
                'Handwashing facilities must be kept clean and in good repair.',
            },
            {
              section: '7.6.1.3.3',
              label: '7.6.1.3.3 - Soap/Towels',
              title: 'Soap/Towels',
              critical: false,
              pointValue: 1,
              description:
                'Each handwashing station must have a supply of hand-cleansing soap or detergent and a supply of single-service paper towels.',
            },
            {
              section: '7.6.1.3.4',
              label: '7.6.1.3.4 - Toilets Clean/Good Repair',
              title: 'Toilets Clean/Good Repair',
              critical: false,
              pointValue: 1,
              description:
                'Toilet fixtures must be kept clean and in good repair.',
            },
            {
              section: '7.6.1.3.5',
              label: '7.6.1.3.5 - Toilet Tissue',
              title: 'Toilet Tissue',
              critical: false,
              pointValue: 1,
              description:
                'A supply of toilet tissue must be always provided at each toilet.',
            },
            {
              section: '7.6.2.1',
              label: '7.6.2.1 - Number',
              title: 'Number',
              critical: false,
              pointValue: 1,
              description:
                'Provide one obvious handwashing station per 100-people seating or fraction thereof. Stations must be equally distributed between the major entry points to the buffet area and must be separate from a toilet room.',
            },
            {
              section: '7.6.2.2',
              label: '7.6.2.2 - Entries',
              title: 'Entries',
              critical: false,
              pointValue: 1,
              description:
                'Provide handwashing stations at each minor entry to the main buffet areas proportional to the capacity, with at least one per entry. These handwashing stations can count toward the requirement of one station per 100 people.',
            },
            {
              section: '7.6.2.3',
              label: '7.6.2.3 - Self-Service Stations Outside the Main Buffet',
              title: 'Self-Service Stations Outside the Main Buffet',
              critical: false,
              pointValue: 1,
              description:
                'Provide at least one handwashing station at the entrance of each self-service station outside of the main buffet. BEVERAGE stations are excluded.',
            },
            {
              section: '7.6.2.4',
              label: '7.6.2.4 - Equipment and Supplies',
              title: 'Equipment and Supplies',
              critical: false,
              pointValue: 1,
              description:
                'The handwashing station must include a handwashing sink with hot and cold water, soap dispenser, and single-use paper towel dispenser. Electric hand dryers can be installed in addition to paper towel dispensers. Each handwashing station must have a supply of hand-cleansing soap or detergent and a supply of single-service paper towels. Waste receptacles must be provided near the handwashing sink and sized to accommodate the quantity of paper towel waste generated. The handwashing station may be decorative but must be nonabsorbent, durable, and easy to clean.',
            },
            {
              section: '7.6.2.5',
              label: '7.6.2.5 - Automatic Handwashing System',
              title: 'Automatic Handwashing System',
              critical: false,
              pointValue: 1,
              description:
                'An automatic handwashing system instead of a handwashing sink is acceptable.',
            },
            {
              section: '7.6.2.6',
              label: '7.6.2.6 - Sign',
              title: 'Sign',
              critical: false,
              pointValue: 1,
              description:
                'Each handwashing station must have a sign advising everyone to wash their hands before eating. A pictogram can be used instead of words on the sign.',
            },
            {
              section: '7.6.2.7',
              label: '7.6.2.7 - Location',
              title: 'Location',
              critical: false,
              pointValue: 1,
              description:
                'Position the handwashing stations along the flow to the buffets. Stations can be installed just outside of the entry.',
            },
          ],
        },
      ],
    },
    {
      id: 'food-safety-toxic-substances',
      name: 'Food Safety - Toxic Substances',
      items: [
        {
          itemNo: '31',
          label: '31 - Toxic items',
          pointValue: 3,
          critical: true,
          description: 'Toxic items',
          subItems: [
            {
              section: '7.5.1.1.1',
              label: '7.5.1.1.1 - Manufacturer Label',
              title: 'Manufacturer Label',
              critical: true,
              pointValue: 3,
              description:
                'Original containers of POISONOUS OR TOXIC MATERIALS and PERSONAL CARE ITEMS must bear a legible manufacturer’s label.',
            },
            {
              section: '7.5.1.1.2',
              label: '7.5.1.1.2 - Working Containers',
              title: 'Working Containers',
              critical: true,
              pointValue: 3,
              description:
                'Working containers used for storing POISONOUS OR TOXIC MATERIALS such as cleaners and SANITIZERS taken from bulk supplies must be clearly and individually identified with the common name of the material.',
            },
            {
              section: '7.5.2.1.1',
              label: '7.5.2.1.1 - Pesticide/Rodenticide Locker',
              title: 'Pesticide/Rodenticide Locker',
              critical: true,
              pointValue: 3,
              description:
                'PESTICIDES, insecticides, and rodenticides must be stored in a locked area of the vessel that is not in a FOOD AREA.',
            },
            {
              section: '7.5.2.1.2',
              label: '7.5.2.1.2 - Cleaning Materials Locker',
              title: 'Cleaning Materials Locker',
              critical: true,
              pointValue: 3,
              description:
                'POISONOUS OR TOXIC MATERIALS used in the cleaning and maintenance of FOOD AREAS must be stored in a cleaning materials locker so they cannot contaminate FOOD, EQUIPMENT, UTENSILS, LINENS, and SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.\nThis does not apply to EQUIPMENT and UTENSIL cleaners and SANITIZERS stored in WAREWASHING areas for availability and convenience if the materials are stored to prevent CONTAMINATION of FOOD, EQUIPMENT, UTENSILS, LINENS, and SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.5.2.2.1',
              label: '7.5.2.2.1 - Necessary Materials',
              title: 'Necessary Materials',
              critical: true,
              pointValue: 3,
              description:
                'Only POISONOUS OR TOXIC MATERIALS required for the operation and maintenance of a FOOD AREA of the vessel—such as for cleaning and sanitizing EQUIPMENT and UTENSILS and controlling insects and rodents—are allowed in the FOOD AREAS of the vessel.',
            },
            {
              section: '7.5.2.2.2',
              label: '7.5.2.2.2 - Use Conditions',
              title: 'Use Conditions',
              critical: true,
              pointValue: 3,
              description:
                'POISONOUS OR TOXIC MATERIALS must be used according to the following:\n• LAW and these standards.\n• Manufacturer’s use directions included in labeling (and, for a PESTICIDE, manufacturer’s label instructions stating that use is allowed in a FOOD AREA).\n• Conditions of certification (if certification is required) for use of the pest-control materials.',
            },
            {
              section: '7.5.2.2.3',
              label: '7.5.2.2.3 - Application',
              title: 'Application',
              critical: true,
              pointValue: 3,
              description:
                'POISONOUS OR TOXIC MATERIALS must be applied in a manner that prevents\n• A HAZARD to employees or other persons.\n• CONTAMINATION including toxic residues resulting from drip, drain, fog, splash, or spray on FOOD; EQUIPMENT; UTENSILS; LINENS; and SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.5.2.2.4',
              label: '7.5.2.2.4 - Restricted-Use Applications',
              title: 'Restricted-Use Applications',
              critical: true,
              pointValue: 3,
              description:
                'When a RESTRICTED-USE PESTICIDE is applied, FOOD, EQUIPMENT, UTENSILS, LINENS, and SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES must be removed; covered with impermeable covers; or other precautions taken.',
            },
            {
              section: '7.5.2.2.5',
              label: '7.5.2.2.5 - Restricted-Use Applicator',
              title: 'Restricted-Use Applicator',
              critical: true,
              pointValue: 3,
              description:
                'A RESTRICTED-USE PESTICIDE must be applied only by an applicator certified as defined in 7 USC 136(e) Certified Applicator of the Federal Insecticide, Fungicide, and Rodenticide Act or a person under the direct supervision of a certified applicator.',
            },
            {
              section: '7.5.2.2.6',
              label: '7.5.2.2.6 - Equipment Cleaning and Sanitizing',
              title: 'Equipment Cleaning and Sanitizing',
              critical: true,
              pointValue: 3,
              description:
                'FOOD EQUIPMENT and UTENSILS in the area treated must be cleaned and sanitized after the application.',
            },
            {
              section: '7.5.2.2.7',
              label: '7.5.2.2.7 - Containers',
              title: 'Containers',
              critical: true,
              pointValue: 3,
              description:
                'A container previously used to store cleanser, chemicals, or POISONOUS OR TOXIC MATERIALS must not be used to store, transport, or dispense FOOD. Additionally, FOOD containers must not be used to store, transport, or dispense cleanser, chemicals, or POISONOUS OR TOXIC MATERIALS.',
            },
            {
              section: '7.5.2.3.1',
              label: '7.5.2.3.1 - Sanitizers',
              title: 'Sanitizers',
              critical: true,
              pointValue: 3,
              description:
                'Chemical SANITIZERS, including chemical sanitizing solutions generated on-site, and other chemical antimicrobials applied to FOOD-CONTACT SURFACES must meet the requirements specified in 40 CFR 180.940 Sanitizing Solutions.',
            },
            {
              section: '7.5.2.3.2',
              label: '7.5.2.3.2 - Chemicals for Fruits and Vegetables',
              title: 'Chemicals for Fruits and Vegetables',
              critical: true,
              pointValue: 3,
              description:
                'Chemicals used to wash or peel raw whole fruits and vegetables must meet the requirements specified in 21 CFR 173.315 Chemicals Used in Washing or to Assist in the Peeling of Fruits and Vegetables (Annex 13.11).',
            },
            {
              section: '7.5.2.3.3',
              label: '7.5.2.3.3 - Boiler Water Additives',
              title: 'Boiler Water Additives',
              critical: true,
              pointValue: 3,
              description:
                'Chemicals used as boiler water ADDITIVES for culinary steam or other FOOD AREA purposes must meet the requirements specified in 21 CFR 173.310 Boiler Water ADDITIVES.',
            },
            {
              section: '7.5.2.3.4',
              label: '7.5.2.3.4 - Drying Agents',
              title: 'Drying Agents',
              critical: true,
              pointValue: 3,
              description:
                'Drying agents used in conjunction with SANITIZATION must contain only components listed as one of the following:\n• Generally recognized as safe for use in FOOD as specified in 21 CFR 182 Substances Generally Recognized as Safe or in 21 CFR 184 Direct FOOD Substances Affirmed as Generally Recognized as Safe.\n• Generally recognized as safe for the intended use as specified in 21 CFR 186 Indirect FOOD Substances Affirmed as Generally Recognized as Safe.\n• APPROVED for use as a drying agent under a prior sanction specified in 21 CFR 181 Prior-Sanctioned FOOD Ingredients.\n• Specifically regulated as an indirect FOOD ADDITIVE for use as a drying agent as specified in 21 CFR Parts 175-178.\n• APPROVED for use as a drying agent under the threshold of regulation process established by 21 CFR 170.39 Threshold of Regulation for Substances Used in FOOD-Contact Articles.',
            },
            {
              section: '7.5.2.3.5',
              label: '7.5.2.3.5 - Approved for Use with Chemical Sanitizers',
              title: 'Approved for Use with Chemical Sanitizers',
              critical: true,
              pointValue: 3,
              description:
                'When used with chemical SANITIZATION, drying agents must be specifically APPROVED for use with chemical sanitizing solutions.',
            },
            {
              section: '7.5.2.3.6',
              label: '7.5.2.3.6 - Lubricants',
              title: 'Lubricants',
              critical: true,
              pointValue: 3,
              description:
                'Lubricants must meet the requirements specified in 21 CFR 178.3570 Lubricants with Incidental food contact if they are used on FOOD- CONTACT SURFACES; on bearings and gears or within FOOD-CONTACT SURFACES; or on bearings and gears located so that lubricants may leak, drip, or be forced into FOOD or onto FOOD-CONTACT SURFACES.',
            },
            {
              section: '7.5.2.4.1',
              label: '7.5.2.4.1 - Restricted-Use Pesticides',
              title: 'Restricted-Use Pesticides',
              critical: true,
              pointValue: 3,
              description:
                'RESTRICTED-USE PESTICIDES used in FOOD AREAS must meet the requirements specified in 40 CFR 152 Subpart I Classification of PESTICIDES.',
            },
            {
              section: '7.5.2.4.2',
              label: '7.5.2.4.2 - Rodent Bait',
              title: 'Rodent Bait',
              critical: true,
              pointValue: 3,
              description:
                'Rodent bait used in FOOD AREAS must be contained in a covered, tamper-resistant bait station.',
            },
            {
              section: '7.5.2.4.3',
              label: '7.5.2.4.3 - Tracking Powder Pesticides',
              title: 'Tracking Powder Pesticides',
              critical: true,
              pointValue: 3,
              description:
                'A tracking powder PESTICIDE must not be used in a FOOD AREA.',
            },
            {
              section: '7.5.3.1.1',
              label: '7.5.3.1.1 - Necessary Medicines',
              title: 'Necessary Medicines',
              critical: true,
              pointValue: 3,
              description:
                'Only medicines necessary for the health of the FOOD EMPLOYEES are allowed in a FOOD AREA.',
            },
            {
              section: '7.5.3.1.2',
              label: '7.5.3.1.2 - Medicine Labeling/Separation',
              title: 'Medicine Labeling/Separation',
              critical: true,
              pointValue: 3,
              description:
                'Medicines in a FOOD AREA for FOOD EMPLOYEES’ use must be labeled and be in an area such as the chef’s office to prevent the CONTAMINATION of FOOD, EQUIPMENT, UTENSILS, LINENS, or SINGLE- SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.5.3.1.3',
              label: '7.5.3.1.3 - First Aid Supplies',
              title: 'First Aid Supplies',
              critical: true,
              pointValue: 3,
              description:
                'First aid supplies in a FOOD AREA for FOOD EMPLOYEES’ use must be labeled and stored in a kit or a container in a location that prevents the CONTAMINATION of FOOD, EQUIPMENT, UTENSILS, LINENS, or SINGLE- SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
          ],
        },
      ],
    },
    {
      id: 'food-safety-facilities',
      name: 'Food Safety - Facilities',
      items: [
        {
          itemNo: '32',
          label: '32 - Solid waste containers',
          pointValue: 1,
          critical: false,
          description: 'Solid waste containers',
          subItems: [
            {
              section: '7.6.3.1.1',
              label: '7.6.3.1.1 - Containers',
              title: 'Containers',
              critical: false,
              pointValue: 1,
              description:
                'Receptacles and containers for REFUSE and recyclables and for use with materials containing FOOD residue must be durable, nonabsorbent, EASILY CLEANABLE, and leakproof.',
            },
            {
              section: '7.6.3.1.2',
              label: '7.6.3.1.2 - Insect/Rodent Resistant',
              title: 'Insect/Rodent Resistant',
              critical: false,
              pointValue: 1,
              description:
                'Receptacles and containers for REFUSE and recyclables and for use with materials containing FOOD residue must be insect and rodent resistant and must have tight-fitting lids.',
            },
            {
              section: '7.6.3.1.3',
              label: '7.6.3.1.3 - Covered/Provided',
              title: 'Covered/Provided',
              critical: false,
              pointValue: 1,
              description:
                'Receptacles and containers must be kept covered when not in continuous use and after they are filled.',
            },
            {
              section: '7.6.3.1.4',
              label: '7.6.3.1.4 - Location',
              title: 'Location',
              critical: false,
              pointValue: 1,
              description:
                'A receptacle or container must be provided in each area of the vessel or premise where REFUSE is generated or commonly discarded or where recyclables are placed.',
            },
            {
              section: '7.6.3.1.5',
              label: '7.6.3.1.5 - Washing Facilities',
              title: 'Washing Facilities',
              critical: false,
              pointValue: 1,
              description:
                'Facilities suitable for washing receptacles and containers must be separate from FOOD EQUIPMENT and UTENSIL storage areas or FOOD PREPARATION AREAS.',
            },
            {
              section: '7.6.3.1.6',
              label: '7.6.3.1.6 - Design/Supplies',
              title: 'Design/Supplies',
              critical: false,
              pointValue: 1,
              description:
                'The designated container wash area must be EASILY CLEANABLE and have tempered water, access to detergent, and suitable drainage.',
            },
            {
              section: '7.6.3.1.7',
              label: '7.6.3.1.7 - Cleaned',
              title: 'Cleaned',
              critical: false,
              pointValue: 1,
              description:
                'Receptacles and containers must be cleaned when emptied.',
            },
            {
              section: '7.6.3.2.1',
              label: '7.6.3.2.1 - Easily Cleanable/Durable',
              title: 'Easily Cleanable/Durable',
              critical: false,
              pointValue: 1,
              description:
                'The dry and refrigerated garbage and REFUSE storage room must be constructed of EASILY CLEANABLE, corrosion-resistant, nonabsorbent, and durable materials.',
            },
            {
              section: '7.6.3.2.2',
              label: '7.6.3.2.2 - Size',
              title: 'Size',
              critical: false,
              pointValue: 1,
              description:
                'The garbage and REFUSE storage room must be large enough to store and process the garbage and REFUSE.',
            },
            {
              section: '7.6.3.2.3',
              label: '7.6.3.2.3 - Prevent Contamination',
              title: 'Prevent Contamination',
              critical: false,
              pointValue: 1,
              description:
                'The garbage and REFUSE storage room must be located to prevent CONTAMINATION in FOOD PREPARATION, FOOD STORAGE, and UTENSIL washing areas.',
            },
            {
              section: '7.6.3.2.4',
              label: '7.6.3.2.4 - Good Repair/Clean',
              title: 'Good Repair/Clean',
              critical: false,
              pointValue: 1,
              description:
                'The garbage and REFUSE storage room must be maintained in good repair and kept clean.',
            },
          ],
        },
        {
          itemNo: '33',
          label: '33 - Decks/bulkheads/deckheads',
          pointValue: 1,
          critical: false,
          description: 'Decks/bulkheads/deckheads',
          subItems: [
            {
              section: '7.6.5.1',
              label: '7.6.5.1 - Cleanable',
              title: 'Cleanable',
              critical: false,
              pointValue: 1,
              description:
                'Decks, BULKHEADS, and DECKHEADS in FOOD preparation, WAREWASHING, pantries, bars, and FOOD and EQUIPMENT storage areas must be constructed and maintained for easy cleaning. Decks may be of nonskid construction provided they are EASILY CLEANABLE. Carpet is not considered EASILY CLEANABLE and must not be used in these areas.',
            },
            {
              section: '7.6.5.2',
              label: '7.6.5.2 - Coving',
              title: 'Coving',
              critical: false,
              pointValue: 1,
              description:
                'BULKHEAD/deck, EQUIPMENT/deck, cabinet/deck, counter/BULKHEAD/backsplash and DECK SINK coaming/deck junctures must have COVING. This applies to galleys, pantries, buffets, bars, waiter stations, dining room work counters, provisions, scrapping stations, FOOD STORAGE ROOMS, FOOD TRANSPORTATION CORRIDORS, EQUIPMENT or UTENSIL storage rooms, and toilet rooms intended for use by FOOD EMPLOYEES.\nThis does not apply to consumer sides of bars or to BEVERAGE storage containing unopened BEVERAGES in guest or crew public spaces.',
            },
            {
              section: '7.6.5.3',
              label: '7.6.5.3 - Finishes',
              title: 'Finishes',
              critical: false,
              pointValue: 1,
              description:
                'BULKHEADS and DECKHEADS must have SMOOTH, hard finishes.',
            },
            {
              section: '7.6.5.4',
              label: '7.6.5.4 - Corrosion Resistant',
              title: 'Corrosion Resistant',
              critical: false,
              pointValue: 1,
              description:
                'Decks, BULKHEADS, and DECKHEADS in FOOD PREPARATION, WAREWASHING, pantries, and storage areas must be corrosion resistant.',
            },
            {
              section: '7.6.5.5',
              label: '7.6.5.5 - Attached Equipment',
              title: 'Attached Equipment',
              critical: false,
              pointValue: 1,
              description:
                'Light fixtures, vent covers, and similar EQUIPMENT attached to the BULKHEADS or DECKHEADS must be EASILY CLEANABLE.',
            },
            {
              section: '7.6.5.6',
              label: '7.6.5.6 - Exposed Lines',
              title: 'Exposed Lines',
              critical: false,
              pointValue: 1,
              description:
                'Exposed utility service lines and pipes, including lines for fire detection and protection systems, must be installed so they do not obstruct or prevent cleaning.',
            },
            {
              section: '7.6.5.7',
              label: '7.6.5.7 - Cleanable Surfaces',
              title: 'Cleanable Surfaces',
              critical: false,
              pointValue: 1,
              description:
                'Surfaces subject to routine splashes, spillage, or other soiling during normal use must have EASILY CLEANABLE features.',
            },
            {
              section: '7.6.5.8',
              label: '7.6.5.8 - Deck Mats',
              title: 'Deck Mats',
              critical: false,
              pointValue: 1,
              description:
                'Mats must be designed to be REMOVABLE and EASILY CLEANABLE.',
            },
            {
              section: '7.6.5.9',
              label: '7.6.5.9 - Clean',
              title: 'Clean',
              critical: false,
              pointValue: 1,
              description:
                'Decks, BULKHEADS, DECKHEADS, and attached EQUIPMENT in FOOD PREPARATION, WAREWASHING, pantries, and storage areas must be cleaned as often as necessary.',
            },
            {
              section: '7.6.5.10',
              label: '7.6.5.10 - Timing',
              title: 'Timing',
              critical: false,
              pointValue: 1,
              description:
                'Cleaning must be done during periods when the least amount of FOOD is exposed.',
            },
            {
              section: '7.6.5.11',
              label: '7.6.5.11 - Good Repair',
              title: 'Good Repair',
              critical: false,
              pointValue: 1,
              description:
                'Decks, BULKHEADS, and DECKHEADS in FOOD PREPARATION, WAREWASHING, pantries, and storage areas must be maintained in good repair.',
            },
            {
              section: '7.6.5.12',
              label: '7.6.5.12 - Ventilation',
              title: 'Ventilation',
              critical: false,
              pointValue: 1,
              description:
                'Slots for supply ventilation over the void space (plenum) are not allowed directly over FOOD PREPARATION AREAS, FOOD STORAGE AREAS, or clean EQUIPMENT storage.\nThis section applies to vessels built or renovated in accordance with the 2011 VSP Construction Guidelines or later.',
            },
          ],
        },
        {
          itemNo: '34',
          label: '34 - Plumbing fixtures; supply/drain lines/drains',
          pointValue: 1,
          critical: false,
          description: 'Plumbing fixtures; supply/drain lines/drains',
          subItems: [
            {
              section: '7.6.4.3.1',
              label: '7.6.4.3.1 - Good Repair',
              title: 'Good Repair',
              critical: false,
              pointValue: 1,
              description:
                'The PLUMBING SYSTEM in FOOD AREAS must be in good repair.',
            },
          ],
        },
        {
          itemNo: '35',
          label: '35 - Liquid waste disposal',
          pointValue: 2,
          critical: false,
          description: 'Liquid waste disposal',
          subItems: [
            {
              section: '7.6.4.2.1',
              label: '7.6.4.2.1 - Discharge',
              title: 'Discharge',
              critical: false,
              pointValue: 2,
              description:
                'BLACK and GRAY WATER must be discharged to the vessel’s wastewater disposal system and must not pool on the deck.',
            },
            {
              section: '7.6.4.2.2',
              label: '7.6.4.2.2 - Leakage',
              title: 'Leakage',
              critical: false,
              pointValue: 2,
              description:
                'Leakage of SEWAGE tanks or discharge of SEWAGE into the bilge or other areas on the vessel is prohibited.',
            },
          ],
        },
        {
          itemNo: '36',
          label: '36 - Lighting',
          pointValue: 1,
          critical: false,
          description: 'Lighting',
          subItems: [
            {
              section: '7.6.2.8',
              label: '7.6.2.8 - Lighting',
              title: 'Lighting',
              critical: false,
              pointValue: 1,
              description:
                'The light intensity must be at least 110 lux (10 foot-candles) at handwashing stations.',
            },
            {
              section: '7.6.6.1.1',
              label: '7.6.6.1.1 - 220 Lux/20 Foot-Candles',
              title: '220 Lux/20 Foot-Candles',
              critical: false,
              pointValue: 1,
              description:
                'The light intensity must be at least 220 lux (20 foot-candles) on FOOD preparation surfaces.\nThe light intensity must be at least 220 lux (20 foot-candles) as measured 750 millimeters (30 inches) above the deck in FOOD PREPARATION AREAS, handwashing facilities, WAREWASHING areas, EQUIPMENT, and UTENSIL storage, pantries, toilet rooms, and CONSUMER self-service areas.',
            },
            {
              section: '7.6.6.1.2',
              label: '7.6.6.1.2 - 110 Lux/10 Foot-Candles',
              title: '110 Lux/10 Foot-Candles',
              critical: false,
              pointValue: 1,
              description:
                'When lights are in use, the light intensity must be at least 110 lux (10 foot-candles) at 750 millimeters (30 inches) above the deck in walk- in refrigerator units, DRY STORAGE AREAS, LINEN storage, waiter stations, scrapping stations, and handwashing stations in bars.\nThe light intensity must be at least 110 lux (10 foot-candles) behind and around mounted EQUIPMENT, including counter-mounted EQUIPMENT.',
            },
            {
              section: '7.6.6.1.3',
              label: '7.6.6.1.3 - Light Intensity During Cleaning Operations',
              title: 'Light Intensity During Cleaning Operations',
              critical: false,
              pointValue: 1,
              description:
                'In bars and dining room waiter stations, the light intensity must be at least 220 lux (20 foot-candles) during cleaning operations. This includes open shelves (such as shelves without doors or shutters) where FOOD and BEVERAGES are stored.',
            },
            {
              section: '7.6.6.2',
              label: '7.6.6.2 - Light Bulbs',
              title: 'Light Bulbs',
              critical: false,
              pointValue: 1,
              description:
                'Light bulbs must be shielded, coated, or otherwise shatter resistant in areas with exposed FOOD; clean EQUIPMENT, UTENSILS, and LINENS; or unwrapped SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.\nAn infrared or other heat lamp must be protected against breakage by a shield surrounding and extending beyond the bulb so only the face of the bulb is exposed.',
            },
            {
              section: '7.7.1',
              label: '7.7.1 - Lighting',
              title: 'Lighting',
              critical: false,
              pointValue: 1,
              description:
                'The light intensity must be at least 220 lux (20 foot-candles) at the dispensing nozzle(s) and the filling surface(s) during cleaning operations. This does not apply to outdoor drinking fountains.',
            },
          ],
        },
        {
          itemNo: '37',
          label: '37 - Rooms/equipment venting',
          pointValue: 1,
          critical: false,
          description: 'Rooms/equipment venting',
          subItems: [
            {
              section: '7.3.2.4.1',
              label: '7.3.2.4.1 - Exhaust Ventilation Hood Systems',
              title: 'Exhaust Ventilation Hood Systems',
              critical: false,
              pointValue: 1,
              description:
                'Exhaust ventilation hood systems in FOOD PREPARATION AREAS and WAREWASHING areas—including components such as hoods, fans, guards, and ducting—must be designed to prevent grease or condensation from draining or dripping onto FOOD, EQUIPMENT, UTENSILS, LINENS, and SINGLE-SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.3.3.2',
              label: '7.3.3.2 - Ventilation Hood',
              title: 'Ventilation Hood',
              critical: false,
              pointValue: 1,
              description:
                'Ventilation hood systems and devices must be sufficient in number and capacity to prevent grease or condensate from collecting on BULKHEADS and DECKHEADS.',
            },
            {
              section: '7.6.7.1',
              label: '7.6.7.1 - Sufficient',
              title: 'Sufficient',
              critical: false,
              pointValue: 1,
              description:
                'All FOOD PREPARATION AREAS, WAREWASHING areas, and toilet rooms must have sufficient ventilation to keep them free of excessive heat, steam, condensation, vapors, obnoxious odors, smoke, and fumes.',
            },
            {
              section: '7.6.7.2',
              label: '7.6.7.2 - Effective',
              title: 'Effective',
              critical: false,
              pointValue: 1,
              description:
                'Ventilation hood systems and devices must operate effectively to prevent grease and condensate from collecting on the BULKHEADS and DECKHEADS and to remove contaminants generated by EQUIPMENT located under them.',
            },
            {
              section: '7.6.7.3',
              label: '7.6.7.3 - No Contamination',
              title: 'No Contamination',
              critical: false,
              pointValue: 1,
              description:
                'Heating, ventilating, and air conditioning systems must be designed and installed so that make-up air intake and exhaust vents do not cause CONTAMINATION of FOOD, FOOD-CONTACT SURFACES, EQUIPMENT, or UTENSILS.',
            },
            {
              section: '7.6.7.4',
              label: '7.6.7.4 - Filters',
              title: 'Filters',
              critical: false,
              pointValue: 1,
              description:
                'Filters and other grease-extracting EQUIPMENT must be designed to be readily REMOVABLE for cleaning and replacement if not designed to be CLEANED IN PLACE. Intake and exhaust air ducts must be cleaned, and filters changed so they are not a source of CONTAMINATION by dust, dirt, and other materials.',
            },
          ],
        },
        {
          itemNo: '38',
          label:
            '38 - Unnecessary articles, cleaning equipment; Unauthorized personnel',
          pointValue: 1,
          critical: false,
          description:
            'Unnecessary articles, cleaning equipment; Unauthorized personnel',
          subItems: [
            {
              section: '7.6.8.1.1',
              label: '7.6.8.1.1 - Necessary Articles',
              title: 'Necessary Articles',
              critical: false,
              pointValue: 1,
              description:
                'Only articles necessary for the operation of FOOD service areas must be stored in FOOD PREPARATION, FOOD STORAGE, and WAREWASHING areas.',
            },
            {
              section: '7.6.8.1.2',
              label: '7.6.8.1.2 - Cleaning Locker',
              title: 'Cleaning Locker',
              critical: false,
              pointValue: 1,
              description:
                'Maintenance tools such as mops, brooms, and similar items must be stored in a designated locker, so they do not contaminate FOOD, FOOD- CONTACT SURFACES of UTENSILS and EQUIPMENT, LINENS, or SINGLE- SERVICE ARTICLES and SINGLE-USE ARTICLES.',
            },
            {
              section: '7.6.8.1.3',
              label: '7.6.8.1.3 - Labeled',
              title: 'Labeled',
              critical: false,
              pointValue: 1,
              description:
                'The locker must be labeled “CLEANING MATERIALS ONLY.”',
            },
            {
              section: '7.6.8.1.4',
              label: '7.6.8.1.4 - Orderly Manner',
              title: 'Orderly Manner',
              critical: false,
              pointValue: 1,
              description:
                'Maintenance tools such as mops, brooms, and similar items must be stored in an orderly manner that facilitates cleaning of the area used for storing the maintenance tools.',
            },
            {
              section: '7.6.8.1.5',
              label: '7.6.8.1.5 - Mop Drying',
              title: 'Mop Drying',
              critical: false,
              pointValue: 1,
              description:
                'After use, mops must be placed in a position that allows them to air dry without soiling walls, EQUIPMENT, or supplies.',
            },
            {
              section: '7.6.8.1.6',
              label: '7.6.8.1.6 - Bucket Storage',
              title: 'Bucket Storage',
              critical: false,
              pointValue: 1,
              description:
                'Buckets or other containers for washing, rinsing, and sanitizing can be stored with maintenance tools if they are stored inverted and nested.',
            },
          ],
        },
      ],
    },
    {
      id: 'environmental-health',
      name: 'Environmental Health',
      items: [
        {
          itemNo: '39',
          label: '39 - IPM program effective; Approved pesticide application',
          pointValue: 3,
          critical: true,
          description: 'IPM program effective; Approved pesticide application',
          subItems: [
            {
              section: '8.1.3.1.2',
              label: '8.1.3.1.2 - Restricted Use',
              title: 'Restricted Use',
              critical: true,
              pointValue: 3,
              description:
                'A RESTRICTED-USE PESTICIDE must be applied only by a certified applicator or a person with training and testing equivalent to that of a certified applicator.',
            },
            {
              section: '8.2.1.1',
              label: '8.2.1.1 - Effective Control',
              title: 'Effective Control',
              critical: true,
              pointValue: 3,
              description:
                'The presence of insects, rodents, and other pests must be effectively controlled to minimize their presence in FOOD AREAS.',
            },
            {
              section: '8.2.2.1',
              label: '8.2.2.1 - Chemical Controls',
              title: 'Chemical Controls',
              critical: true,
              pointValue: 3,
              description:
                'Chemical control measures must conform to products and application procedures specifically allowed in the FOOD safety section of these standards and the vessel’s IPM plan.',
            },
          ],
        },
        {
          itemNo: '40',
          label: '40 - IPM procedures; Outer openings protection',
          pointValue: 1,
          critical: false,
          description: 'IPM procedures; Outer openings protection',
          subItems: [
            {
              section: '8.1.1.1',
              label: '8.1.1.1 - IPM Plan',
              title: 'IPM Plan',
              critical: false,
              pointValue: 1,
              description:
                'Each vessel must have an IPM plan to implement effective monitoring and control strategies for pests on the vessel.',
            },
            {
              section: '8.1.1.2',
              label: '8.1.1.2 - Monitoring',
              title: 'Monitoring',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan must set a schedule for periodic active monitoring inspections, including some at night or during periods of no or minimal activity.',
            },
            {
              section: '8.1.1.3',
              label: '8.1.1.3 - Logs',
              title: 'Logs',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan must include provisions for logs for active monitoring of pest sightings in operational areas of the vessel. The IPM plan also must include provisions for training of crew members in charge of log completion. The time of the active monitoring inspections must be recorded in the log.',
            },
            {
              section: '8.1.1.4',
              label: '8.1.1.4 - Passive Surveillance',
              title: 'Passive Surveillance',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan must include passive surveillance procedures such as glue traps or other passive monitoring devices and must include the location of each. A passive device monitoring log must be maintained.',
            },
            {
              section: '8.1.1.5',
              label: '8.1.1.5 - Action and Follow Up',
              title: 'Action and Follow Up',
              critical: false,
              pointValue: 1,
              description:
                'When pests are observed during an inspection, the log must include action taken as well as follow-up inspection results.',
            },
            {
              section: '8.1.2.1',
              label: '8.1.2.1 - Evaluation',
              title: 'Evaluation',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan must be evaluated for effectiveness periodically or whenever there is a significant change in the vessel’s operation or structure (for example, renovation).\nEvaluation may be required more frequently in areas where pest infestations cannot be controlled.',
            },
            {
              section: '8.1.2.2',
              label: '8.1.2.2 - Reviews',
              title: 'Reviews',
              critical: false,
              pointValue: 1,
              description:
                'IPM plan evaluations and changes must be documented in the IPM plan.',
            },
            {
              section: '8.1.2.3',
              label: '8.1.2.3 - Inspections',
              title: 'Inspections',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan, monitoring records, and other documentation must be available for review during inspections.',
            },
            {
              section: '8.1.3.1.1',
              label: '8.1.3.1.1 - Pesticide Record',
              title: 'Pesticide Record',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan must include a record of GENERAL-USE and RESTRICTED- USE PESTICIDES used to control pests and vectors. The record must include all GENERAL-USE and RESTRICTED-USE PESTICIDES currently on the vessel and those used in the previous 12 months.',
            },
            {
              section: '8.1.3.1.3',
              label: '8.1.3.1.3 - Applicator Training',
              title: 'Applicator Training',
              critical: false,
              pointValue: 1,
              description:
                'Training of the pest-control personnel must be documented.',
            },
            {
              section: '8.1.3.1.4',
              label: '8.1.3.1.4 - Safety',
              title: 'Safety',
              critical: false,
              pointValue: 1,
              description:
                'The IPM plan must establish health and safety procedures to protect the passengers and crew.',
            },
            {
              section: '8.2.1.2',
              label: '8.2.1.2 - Exclusion',
              title: 'Exclusion',
              critical: false,
              pointValue: 1,
              description:
                'Entry points where pests may enter the FOOD AREAS must be protected.\nRat guards must be made of a durable and nonchewable material. Rat guards must be used according to the manufacturer’s specifications for the effective prevention of pest migration.\nRat guards must be on all lines that go ashore on arrival and until 1 hour before the ship leaves port. Each line must have at least one rat guard placed either as far as practicable from the pier or as far as practicable from the ship but still close enough for any openings to be stuffed with nonchewable material. Rat guards must not be staggered on adjacent lines, and a group of lines must not be merged onto one rat guard.',
            },
            {
              section: '8.2.1.3',
              label: '8.2.1.3 - Incoming Food and Other Supplies',
              title: 'Incoming Food and Other Supplies',
              critical: false,
              pointValue: 1,
              description:
                'Incoming shipments of FOOD and all other supplies must be routinely inspected for evidence of insects, rodents, and other pests. A record of these inspections must be available for review during inspections.',
            },
            {
              section: '8.2.1.4',
              label: '8.2.1.4 - IPM Inspections',
              title: 'IPM Inspections',
              critical: false,
              pointValue: 1,
              description:
                'All FOOD AREAS must be inspected at a frequency that can quickly detect the evidence of pests, harborage conditions, cleanliness, and protection of outer openings.\nReasonable care must be given to conduct inspections in nonfood areas for the presence of insects, rodents, and other pests.\nThe garbage handling areas of the vessel must be inspected at least weekly for the presence of insects, rodents, and other pests. The results of these inspections must be maintained in a log. These results may be included in the log of the FOOD AREA inspections.',
            },
            {
              section: '8.2.2.2.1',
              label: '8.2.2.2.1 - Insect-Control Devices',
              title: 'Insect-Control Devices',
              critical: false,
              pointValue: 1,
              description:
                'Insect-control devices that electrocute or stun flying insects are not permitted in FOOD AREAS.',
            },
            {
              section: '8.2.2.2.4',
              label: '8.2.2.2.4 - Cleaning',
              title: 'Cleaning',
              critical: false,
              pointValue: 1,
              description:
                'Dead or trapped insects, rodents, and other pests must be removed from control devices and the vessel at a frequency that prevents their accumulation or decomposition or the attraction of other pests.',
            },
          ],
        },
        {
          itemNo: '41',
          label: '41 - Housekeeping',
          pointValue: 2,
          critical: false,
          description: 'Housekeeping',
          subItems: [
            {
              section: '9.1.1.1',
              label: '9.1.1.1 - Disinfectant',
              title: 'Disinfectant',
              critical: false,
              pointValue: 2,
              description:
                "DISINFECTANT products or systems (for example, on-site hypochlorous acid generators) must be effective against human norovirus or an acceptable surrogate (for example, calicivirus) and used according to the manufacturer's instructions (such as concentration, contact time, and approved surfaces). Surfaces must be cleaned thoroughly before DISINFECTION.",
            },
            {
              section: '9.1.1.1.1',
              label: '9.1.1.1.1 - Continuous Disinfection of Public Areas',
              title: 'Continuous Disinfection of Public Areas',
              critical: false,
              pointValue: 2,
              description:
                'When the cumulative percentage of REPORTABLE AGE CASES among passengers or crew members is greater than or equal to 2%, the OUTBREAK management response must include cleaning and disinfecting all public areas, including handrails and restrooms, on a continuous basis while passengers or crew are circulating in those areas.\nThis process involves DISINFECTION of hand-contact surfaces of all public areas throughout the ship, including handrails and restrooms. These areas are identified in the OUTBREAK prevention and response plan (OPRP). Workers disinfect these surfaces without interruption along a logical route by deck, venue, and area when people are present or circulating in these areas.',
            },
            {
              section: '9.1.1.1.2',
              label: '9.1.1.1.2 - Cabin Cleaning',
              title: 'Cabin Cleaning',
              critical: false,
              pointValue: 2,
              description:
                'Cabins that house passengers or crew with AGE must be cleaned and disinfected daily while the occupants are ill.',
            },
            {
              section: '9.1.1.1.3',
              label: '9.1.1.1.3 - Precautionary Measures',
              title: 'Precautionary Measures',
              critical: false,
              pointValue: 2,
              description:
                'Precautionary measures by housekeeping personnel must be taken in consultation with the vessel’s medical staff to prevent the spread of AGE.\nPrecautionary measures by housekeeping personnel may include using disposable personal protection EQUIPMENT, including gloves that are changed after each cabin; cleaning cabins with ill passengers or crew after all other cabins; or having specific crew members only clean cabins of ill passengers or crew.',
            },
            {
              section: '9.1.2',
              label: '9.1.2 - Written OPRP',
              title: 'Written OPRP',
              critical: false,
              pointValue: 2,
              description:
                'Each vessel must have a written OUTBREAK prevention and response plan (OPRP) that details standard procedures and policies to specifically address AGE. The written OPRP must include the following at a minimum:\n• Duties and responsibilities of each department and their staff for all passenger and crew public areas.\n• Steps in OUTBREAK management and control and the trigger required for action at each step.\nAt a minimum, triggers must address a graduated approach to OUTBREAK management in response to increasing case counts. Additionally, triggers may be based on events, such as reports of public vomiting or diarrhea, increased room service requests, meal or excursion cancellations, missed events, or others.\nCruise ship AGE surveillance data has shown that a 0.45% daily ATTACK RATE is indicative of a pending OUTBREAK.\n• DISINFECTANT products or on-site DISINFECTANT generators (such as generators making hypochlorous acid [electrolyzed water, electrochemically activated water, and electroactivated water]) used, including the surfaces or items the DISINFECTANTS will be applied to, concentrations, and required contact times.\nOn-site DISINFECTANT generators must generate output solutions that constitute an effective DISINFECTANT when used according to the manufacturer’s instructions. Include and follow parameters for use (concentration, contact time, PH, approved surfaces, and other applicable factors) in the OPRP.\nFor on-site DISINFECTANT generators, the following must be available for inspector review: o Studies demonstrating effectiveness against human norovirus or an acceptable surrogate (for example, calicivirus). o Device operating and maintenance procedures.\n• Procedures for informing passengers and crew members of the OUTBREAK. This section should address the procedures for notification of passengers embarking the vessel after an OUTBREAK voyage. In the case of an extended voyage separated into segments, such as a world cruise, this requirement applies to passengers embarking for the segment after an OUTBREAK segment.\n• Procedures for returning the vessel to normal operating conditions after an OUTBREAK.\n• Procedures to protect the passengers and crew from exposure to DISINFECTANTS, if not already included in the vessel’s safety management system. At a minimum, this must include the following: − Safety data sheets (SDSs). − Personal protective equipment for crew. − Health and safety procedures to minimize respiratory and dermal exposures to both passengers and crew.\nWash contaminated linen with an adequate amount of detergent at a minimum temperature of 71°C (160°F) for a minimum of 25 minutes. Many factors contribute to the effectiveness of the laundering process. These include washer and dryer machine cycles, water temperature, and detergent.',
            },
            {
              section: '9.1.3',
              label: '9.1.3 - Public Toilet Facilities',
              title: 'Public Toilet Facilities',
              critical: false,
              pointValue: 2,
              description:
                'Passenger and crew public toilets facilities (not including those in FOOD AREAS) must be provided with a handwashing station that includes the following:\n• Hot and cold running water.\n• Soap.\n• A method to dry hands (for example, sanitary hand-drying device, paper towels).\n• A sign advising users to wash hands. (Pictograms are acceptable.)',
            },
            {
              section: '9.1.3.1',
              label: '9.1.3.1 - Hands-Free Exit',
              title: 'Hands-Free Exit',
              critical: false,
              pointValue: 2,
              description:
                'Passenger and crew public toilet facilities must be equipped so persons exiting the toilet room are not required to touch the door handle with bare hands.\nWhere toilet stalls include handwashing facilities, the bare-hands-free contact must begin in the toilet stall. Toilet facilities with multiple exits, such as spa dressing rooms, must have bare-hands-free contact at each exit leading to the public area (such as corridor, outside deck).\nBare-hands-free contact may be accomplished by methods such as locating paper towel dispensers at sinks and waste containers near the room door, installing mechanically operated doors, removing doors, or using other effective means.',
            },
            {
              section: '9.1.3.2',
              label: '9.1.3.2 - Sign',
              title: 'Sign',
              critical: false,
              pointValue: 2,
              description:
                'A sign must be posted advising users of toilet facilities to use hand towel, paper towel, or tissue to open the door unless the exit is hands free.\nA pictogram that illustrates the correct use and disposal of paper towels may be used instead of a sign.',
            },
            {
              section: '10.2.2.8',
              label: '10.2.2.8 - Exiting',
              title: 'Exiting',
              critical: false,
              pointValue: 2,
              description:
                'Toilet rooms must be equipped so that persons exiting the toilet room are not required to touch the door handle or button with bare hands.\nBare-hands-free contact may be accomplished by methods such as locating paper towel dispensers at sinks and waste containers near the room door, installing mechanically operated doors, removing doors, or using other effective means.',
            },
          ],
        },
        {
          itemNo: '42',
          label: '42 - Child activity centers',
          pointValue: 1,
          critical: false,
          description: 'Child activity centers',
          subItems: [
            {
              section: '10.1.1.1',
              label: '10.1.1.1 - Diaper Changing',
              title: 'Diaper Changing',
              critical: false,
              pointValue: 1,
              description:
                'If children who wear diapers are accepted in the CHILD ACTIVITY CENTER, diaper-changing stations and disposal facilities must be provided.',
            },
            {
              section: '10.1.1.2',
              label: '10.1.1.2 - Diaper-Changing Stations',
              title: 'Diaper-Changing Stations',
              critical: false,
              pointValue: 1,
              description:
                'Each station must include the following:\n• A changing table that is nonabsorbent, nontoxic, smooth, durable, easy- to-clean, and designed for diaper changing.\n• A supply of disposable diapers, gloves, wipes, table cleanser, and DISINFECTANT.\n• An airtight soiled-diaper receptacle.\n• An adjacent handwashing station.',
            },
            {
              section: '10.1.1.3',
              label: '10.1.1.3 - Signs',
              title: 'Signs',
              critical: false,
              pointValue: 1,
              description:
                'Signs must be posted in the diaper changing area advising handwashing after each diaper change.',
            },
            {
              section: '10.2.1',
              label: '10.2.1 - Employee Handwashing',
              title: 'Employee Handwashing',
              critical: false,
              pointValue: 1,
              description:
                'Vessels constructed to the VSP 2005 Construction Guidelines or later must have at least one handwashing station separate from the children’s toilet room(s). The handwashing station must be kept clean and must always be accessible.',
            },
            {
              section: '10.2.2.1',
              label: '10.2.2.1 - Child-Sized Toilet',
              title: 'Child-Sized Toilet',
              critical: false,
              pointValue: 1,
              description:
                'If toilet rooms are in a CHILD ACTIVITY CENTER, CHILD-SIZED TOILET(s), or toilet with child-sized seat and step stool and handwashing facilities must be provided.\nCHILD-SIZED TOILETS (to include the toilet seat) must have a maximum height of 305 millimeters (12 inches) and a toilet seat opening no greater than 203 millimeters (8 inches).',
            },
            {
              section: '10.2.2.2',
              label: '10.2.2.2 - Toilet Supplies',
              title: 'Toilet Supplies',
              critical: false,
              pointValue: 1,
              description:
                'Each child’s toilet facility must be provided with a supply of toilet tissue, disposable gloves, and sanitary wipes.',
            },
            {
              section: '10.2.2.3',
              label: '10.2.2.3 - Handwashing Stations',
              title: 'Handwashing Stations',
              critical: false,
              pointValue: 1,
              description:
                'The maximum water temperature for a handwashing station must not exceed 43°C (110°F).\nHandwashing sinks must have a maximum height of 560 millimeters (22 inches) above the deck or a step stool must be provided.\nSoap, paper towels or air dryers, and a waste towel receptacle must be located at handwashing stations.',
            },
            {
              section: '10.2.2.4',
              label: '10.2.2.4 - Waste Receptacle',
              title: 'Waste Receptacle',
              critical: false,
              pointValue: 1,
              description:
                'An airtight, washable waste receptacle must be conveniently located to dispose of excrement, soiled sanitary wipes, and soiled gloves. Waste materials must be removed from the CHILD ACTIVITY CENTER each day.',
            },
            {
              section: '10.2.2.5',
              label: '10.2.2.5 - Signs',
              title: 'Signs',
              critical: false,
              pointValue: 1,
              description:
                'Signs must be posted in children’s toilet room with the exact wording “Wash your hands and assist the children with handwashing after helping them use the toilet.”',
            },
            {
              section: '10.2.2.6',
              label: '10.2.2.6 - Assistance',
              title: 'Assistance',
              critical: false,
              pointValue: 1,
              description:
                'Children under 6 years old must be assisted in washing their hands in the CHILD ACTIVITY CENTER after using the toilet room, before eating, and after otherwise contaminating their hands.',
            },
            {
              section: '10.2.2.7',
              label: '10.2.2.7 - Separate',
              title: 'Separate',
              critical: false,
              pointValue: 1,
              description:
                'Separate toilet facilities must be provided for CHILD ACTIVITY CENTER staff. CHILD ACTIVITY CENTER staff must not use the children’s toilet facilities. Public toilet facilities are acceptable.',
            },
            {
              section: '10.2.2.9',
              label: '10.2.2.9 - Child-Sized Handwashing Station',
              title: 'Child-Sized Handwashing Station',
              critical: false,
              pointValue: 1,
              description:
                'Handwashing stations designated for children and located outside toilet rooms must meet the requirements in section 10.2.2.3.',
            },
            {
              section: '10.3.2.1.1',
              label: '10.3.2.1.1 - Cleanable',
              title: 'Cleanable',
              critical: false,
              pointValue: 1,
              description:
                'Surfaces of tables, chairs, and other furnishings that children touch with their hands must be cleanable.',
            },
            {
              section: '10.3.2.1.2',
              label: '10.3.2.1.2 - Condition',
              title: 'Condition',
              critical: false,
              pointValue: 1,
              description:
                'Toys used in the CHILD ACTIVITY CENTER must be cleanable and maintained in a clean condition.',
            },
            {
              section: '10.3.2.2.1',
              label: '10.3.2.2.1 - Hard Surfaces',
              title: 'Hard Surfaces',
              critical: false,
              pointValue: 1,
              description:
                'Surfaces that children touch with their hands must be cleaned and disinfected daily with products labeled by the manufacturer for that purpose.',
            },
            {
              section: '10.3.2.2.2',
              label: '10.3.2.2.2 - Toy Cleaning and Ball Pits',
              title: 'Toy Cleaning and Ball Pits',
              critical: false,
              pointValue: 1,
              description:
                'Toys used in the CHILD ACTIVITY CENTER must be cleaned and disinfected daily.\nBalls used in ball pits/pens must be cleaned when contaminated or at least once per week.\nIf a chemical DISINFECTANT is used, toys must be air dried before use.',
            },
            {
              section: '10.3.2.2.3',
              label: '10.3.2.2.3 - Tables and Highchairs',
              title: 'Tables and Highchairs',
              critical: false,
              pointValue: 1,
              description:
                'Tables and highchair trays must be cleaned and disinfected before and after they are used for eating.',
            },
            {
              section: '10.3.2.2.4',
              label: '10.3.2.2.4 - Decks',
              title: 'Decks',
              critical: false,
              pointValue: 1,
              description:
                'Carpeting must be vacuumed daily and must be periodically cleaned when it becomes visibly soiled. Decks must be washed and disinfected when soiled or at least daily.',
            },
            {
              section: '10.3.2.2.5',
              label: '10.3.2.2.5 - Facility Cleaning and Disinfecting',
              title: 'Facility Cleaning and Disinfecting',
              critical: false,
              pointValue: 1,
              description:
                'Diaper changing stations, handwashing facilities, and toilet rooms must be cleaned and disinfected daily and when soiled during use.',
            },
            {
              section: '10.3.2.2.6',
              label: '10.3.2.2.6 - Linens Laundered',
              title: 'Linens Laundered',
              critical: false,
              pointValue: 1,
              description:
                'LINENS such as blankets, sheets, and pillowcases must be laundered between each use.',
            },
            {
              section: '10.3.2.2.7',
              label: '10.3.2.2.7 - Costumes',
              title: 'Costumes',
              critical: false,
              pointValue: 1,
              description:
                'Costumes worn by children—including clothing items, masks, and hats—must be laundered, cleaned and disinfected, discarded, retained by the guest, or dry cleaned between each use.',
            },
            {
              section: '10.4.1.1.1',
              label: '10.4.1.1.1 - Written Guidance',
              title: 'Written Guidance',
              critical: false,
              pointValue: 1,
              description:
                'Written guidance on symptoms of common childhood infectious illnesses must be posted at the entrance of the CHILD ACTIVITY CENTER.',
            },
            {
              section: '10.4.1.1.2',
              label: '10.4.1.1.2 - Exclusion Policy',
              title: 'Exclusion Policy',
              critical: false,
              pointValue: 1,
              description:
                'The CHILD ACTIVITY CENTER must have a written exclusion policy on procedures to be followed when a child develops symptoms of an infectious illness while at the center.\nThe policy must include a requirement for written clearance from the medical staff before a child with symptoms of infectious illness can be allowed in the CHILD ACTIVITY CENTER.\nThis policy must be posted in a prominent location at the entrance of the CHILD ACTIVITY CENTER.',
            },
            {
              section: '10.4.1.1.3',
              label: '10.4.1.1.3 - Infectious Illness',
              title: 'Infectious Illness',
              critical: false,
              pointValue: 1,
              description:
                'Children with symptoms of an infectious illness must not be allowed in the CHILD ACTIVITY CENTER without written permission from vessel medical staff.',
            },
          ],
        },
        {
          itemNo: '43',
          label: '43 - Ventilation',
          pointValue: 1,
          critical: false,
          description: 'Ventilation',
          subItems: [
            {
              section: '11.1.1.1',
              label: '11.1.1.1 - Condensate Pans',
              title: 'Condensate Pans',
              critical: false,
              pointValue: 1,
              description:
                'Air handling unit condensate drain pans and collection systems must be able to be accessed for inspection, maintenance, and cleaning. Installation of sight windows or other effective methods for full inspection of condensate collection pans must be used when original equipment access makes evaluation during operational inspections impractical.',
            },
            {
              section: '11.1.1.2',
              label: '11.1.1.2 - Self Draining',
              title: 'Self Draining',
              critical: false,
              pointValue: 1,
              description:
                'Condensation collection pans must be self-draining.',
            },
            {
              section: '11.1.1.3',
              label: '11.1.1.3 - Potable Water',
              title: 'Potable Water',
              critical: false,
              pointValue: 1,
              description:
                'Only POTABLE WATER can be used for cleaning the HVAC system.',
            },
            {
              section: '11.1.2.1',
              label: '11.1.2.1 - Air Handling Units',
              title: 'Air Handling Units',
              critical: false,
              pointValue: 1,
              description: 'Air handling units must be kept clean.',
            },
            {
              section: '11.1.2.2',
              label: '11.1.2.2 - Condensers',
              title: 'Condensers',
              critical: false,
              pointValue: 1,
              description:
                'Evaporative condensers must be inspected at least annually and cleaned as necessary to remove scale and sediment. Cooling coils and condensate pans must be cleaned as necessary to remove dirt and organic material.',
            },
            {
              section: '11.1.2.3',
              label: '11.1.2.3 - Inspection and Maintenance Plan',
              title: 'Inspection and Maintenance Plan',
              critical: false,
              pointValue: 1,
              description:
                'Vessels must have a plan to inspect and maintain HVAC systems in accordance with the manufacturer’s recommendations and industry standards. The written inspection, cleaning, and maintenance plan for the HVAC system must be maintained on the vessel and available for review during inspections.\nDocumentation of the inspection, cleaning, and maintenance plan must be available for review during inspections.\nAn electronic maintenance tracking system is acceptable for both the plan and the documentation if the work description and action completed are available.',
            },
            {
              section: '11.2.1.1.1',
              label: '11.2.1.1.1 - Sprays',
              title: 'Sprays',
              critical: false,
              pointValue: 1,
              description:
                'Only POTABLE WATER can be used for water sprays, decorative fountains, humidifiers, and misting systems. The water must be further treated to avoid microbial buildup in the operation of water sprays, fountains, humidifiers, and misting systems.',
            },
            {
              section: '11.2.1.2.1',
              label: '11.2.1.2.1 - Clean',
              title: 'Clean',
              critical: false,
              pointValue: 1,
              description:
                'Decorative fountains and misting systems must be maintained free of Mycobacterium, Legionella, algae, and mold growth.\nFor systems installed after the adoption of the VSP 2011 Operations Manual,\n• Provide an automated treatment system (halogenation, UV, or other effective DISINFECTANT) to prevent the growth of Mycobacterium and Legionella in any decorative fountain, misting system, or similar facility.\n• Ensure that nozzles are REMOVABLE for cleaning and DISINFECTION.\n• Ensure that pipes and reservoirs can be drained when the fountain/system is not in use.\nPORTABLE units must be kept clean.',
            },
            {
              section: '11.2.1.2.2',
              label: '11.2.1.2.2 - Shock Treatment',
              title: 'Shock Treatment',
              critical: false,
              pointValue: 1,
              description:
                'For misting systems and similar facilities, ensure that these systems can also be manually disinfected (halogenation, heat, etc.).\nIf heat is used as a DISINFECTANT, ensure that the water temperature, as measured at the misting nozzle, can be maintained at 65°C (149°F) for a minimum of 10 minutes.',
            },
            {
              section: '11.2.2.1.1',
              label: '11.2.2.1.1 - Hot-Water System',
              title: 'Hot-Water System',
              critical: false,
              pointValue: 1,
              description:
                'The potable hot-water system—including shower heads—must be maintained to preclude growth of Mycobacterium or Legionella.',
            },
            {
              section: '11.2.2.1.2',
              label: '11.2.2.1.2 - Showers',
              title: 'Showers',
              critical: false,
              pointValue: 1,
              description:
                'Shower heads must be cleaned and disinfected every 6 months. DISINFECTION must be accomplished with an appropriate HALOGEN- based DISINFECTANT at 10 MG/L (ppm) for 60 minutes, or an equivalent CT VALUE.\nShower head cleaning and DISINFECTION must be recorded in a log and maintained on the vessel.',
            },
          ],
        },
      ],
    },
    {
      id: 'knowledge',
      name: 'Knowledge',
      items: [
        {
          itemNo: '44',
          label: '44 - Person in charge, Knowledge',
          pointValue: 2,
          critical: false,
          description: 'Person in charge, Knowledge',
          subItems: [
            {
              section: '4.6.1',
              label:
                '4.6.1 - Demonstration of Knowledge - AGE Surveillance Knowledge',
              title: 'Demonstration of Knowledge - AGE Surveillance Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for medical operations related to AGE on the vessel must demonstrate to VSP—on request during inspections—knowledge of medical operations related to AGE.\nThe person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for medical operations related to AGE on the vessel must ensure that employees are properly trained to comply with this section of the standards as it relates to their assigned duties.',
            },
            {
              section: '5.8.1',
              label:
                '5.8.1 - Demonstration of Knowledge - Potable Water Knowledge',
              title: 'Demonstration of Knowledge - Potable Water Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for POTABLE WATER operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of POTABLE WATER operations.\nThe person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for POTABLE WATER operations on the vessel must ensure that employees are properly trained to comply with this section of these standards as it relates to their assigned duties.',
            },
            {
              section: '6.11.1',
              label: '6.11.1 - Demonstration of Knowledge - RWF Knowledge',
              title: 'Demonstration of Knowledge - RWF Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for RWFs operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of RWF operations.\nThe person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for RWF operations on the vessel must ensure that employees are properly trained to comply with this section of these standards as it relates to their assigned duties.',
            },
            {
              section: '8.3.1',
              label: '8.3.1 - Demonstration of Knowledge - IPM Knowledge',
              title: 'Demonstration of Knowledge - IPM Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for IPM operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of IPM operations. The person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for IPM operations on the vessel must ensure that employees are properly trained to comply with this section of these standards as it relates to their assigned duties.',
            },
            {
              section: '9.2.1',
              label:
                '9.2.1 - Demonstration of Knowledge - Housekeeping Knowledge',
              title: 'Demonstration of Knowledge - Housekeeping Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for housekeeping operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of housekeeping operations.\nThe person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for housekeeping operations on the vessel must ensure that employees are properly trained to comply with this section of these standards as it relates to their assigned duties.',
            },
            {
              section: '10.5.1',
              label:
                '10.5.1 - Demonstration of Knowledge - Child Activity Center Knowledge',
              title:
                'Demonstration of Knowledge - Child Activity Center Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for CHILD ACTIVITY CENTER operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of CHILD ACTIVITY CENTER operations.\nThe person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for CHILD ACTIVITY CENTER operations on the vessel must ensure that employees are properly trained to comply with this section of these standards as it relates to their assigned duties.',
            },
            {
              section: '11.3.1',
              label:
                '11.3.1 - Demonstration of Knowledge - HVAC Systems, Fountains, Misting Systems, Humidifiers, and Showers Knowledge',
              title:
                'Demonstration of Knowledge - HVAC Systems, Fountains, Misting Systems, Humidifiers, and Showers Knowledge',
              critical: false,
              pointValue: 2,
              description:
                'The supervisor or person responsible for HVAC operations on the vessel must demonstrate to VSP—on request during inspections—knowledge of HVAC systems, fountains, misting systems, humidifiers, and showers operations.\nThe person must demonstrate this knowledge by compliance with this section of these standards or by responding correctly to the inspector’s questions as they relate to the specific operation. In addition, the supervisor or person responsible for HVAC systems, fountains, misting systems, humidifiers, and showers operations on the vessel must ensure that employees are properly trained to comply with this section of these standards as it relates to their assigned duties.',
            },
            {
              section: '12.11.3',
              label:
                '12.11.3 - Deficiencies Identified during Construction Activities',
              title: 'Deficiencies Identified during Construction Activities',
              critical: false,
              pointValue: 2,
              description:
                'Construction related deficiencies identified during construction activities (such as plan reviews, equipment reviews, consultations, and construction inspections) must be addressed after the initial construction or renovation inspection and before the first operational inspection.\nIf the deficiencies cannot be readily corrected, a timeline for correction must be discussed with VSP.',
            },
            {
              section: '12.13.3.3',
              label: '12.13.3.3 - Areas Not Identified',
              title: 'Areas Not Identified',
              critical: false,
              pointValue: 2,
              description:
                'Procedures, systems, EQUIPMENT, technology, processes, or activities not identified in the scope of these standards must not be tested or introduced operationally on any vessel until the concept is submitted in writing to VSP for review. If the review determines the concept is within the scope of the VSP Environmental Public Health Standards, written procedures, control measures, or a complete VARIANCE submission may be required.',
            },
          ],
        },
      ],
    },
  ],
};

export const VSP_CATEGORIES = VSP_DATA.categories;
